import { FormEvent, lazy, ReactNode, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  PlusCircle,
  ArrowRight,
  Bell,
  Building,
  Calendar,
  Check,
  CheckCircle,
  ClipboardCheck,
  Clock,
  CloudUpload,
  Community,
  Database,
  Download,
  EditPencil,
  Eye,
  FileNotFound,
  Filter,
  HomeSimple,
  JournalPage,
  LogOut,
  Menu,
  NavArrowLeft,
  NavArrowRight,
  PageSearch,
  Search,
  Settings,
  ShieldCheck,
  Spark,
  Star,
  StatsUpSquare,
  TableRows,
  Trash,
  UserCircle,
  WarningTriangle,
  Xmark
} from "iconoir-react";
import { api, downloadFile, post } from "./api";
import { BrandMark } from "./BrandMark";
import type { AppState, Application, AuditEvent, AuditEventPage, AuditSecurity, AuditSummary, Channel, Customer, CustomerChangeSet, DeletedPlan, DownstreamChannel, EligibleDownstreamRoute, MessageItem, MetricVisual, Plan, PlanAnalysis, PlanStatistics, ProgressRecord, RouteItem, TeamMember, User, WorkNature } from "./types";
import { CURRENT_VERSION } from "./version";

const CustomerResearchWorkspace = lazy(() => import("./CustomerResearchWorkspace").then((module) => ({ default: module.CustomerResearchWorkspace })));
const ChangelogPage = lazy(() => import("./ChangelogPage").then((module) => ({ default: module.ChangelogPage })));

type PageKey = "dashboard" | "ai" | "customers" | "pool" | "approvals" | "plans" | "plan-stats" | "exports" | "business" | "channels" | "downstream-channels" | "messages" | "import" | "audit" | "system" | "profile" | "changelog" | "customer-research";
type Navigate = (page: PageKey, replace?: boolean, queryString?: string) => void;
type Notice = { tone: "success" | "error"; message: string };
type RelationUnlinkTarget = {
  relationId: string;
  customerName: string;
  channelName: string;
  routeCount: number;
  routeTypes: string[];
  planCount: number;
  resultCount: number;
  downstreamCount: number;
};
type NavItem = { key: PageKey; label: string; icon: typeof HomeSimple };
type NavGroup = { label: string; items: NavItem[] };
type LookupCustomer = Pick<Customer, "id" | "name" | "shortName" | "region" | "industry" | "favorite">;
type PoolResult = {
  items: LookupCustomer[];
  total: number;
  page: number;
  pageSize: number;
  pageCount: number;
  facets: { regions: { value: string; count: number }[] };
};
type CustomerLibraryResult = {
  items: Customer[];
  total: number;
  page: number;
  pageSize: number;
  pageCount: number;
  rangeStart: number;
  rangeEnd: number;
  facets: { regions: { value: string; count: number }[] };
};

const iconProps = { width: 20, height: 20, strokeWidth: 1.6, "aria-hidden": true } as const;
const metricVisuals: Record<MetricVisual, { icon: typeof Building; tone: string }> = {
  customers: { icon: Building, tone: "green" },
  relations: { icon: Community, tone: "teal" },
  pool: { icon: PageSearch, tone: "blue" },
  multiChannel: { icon: TableRows, tone: "purple" },
  approvals: { icon: ClipboardCheck, tone: "orange" },
  overdue: { icon: WarningTriangle, tone: "red" },
  channels: { icon: Community, tone: "green" },
  routes: { icon: ArrowRight, tone: "teal" },
  attention: { icon: Clock, tone: "orange" },
  activity: { icon: CheckCircle, tone: "blue" },
  accounts: { icon: UserCircle, tone: "purple" }
};

function metricVisualFor(label: string, visual?: MetricVisual) {
  if (visual) return visual;
  if (label.includes("客户")) return "customers";
  if (label.includes("审批") || label.includes("申报")) return "approvals";
  if (label.includes("逾期")) return "overdue";
  if (label.includes("渠道")) return "channels";
  if (label.includes("通路")) return "routes";
  return "activity";
}
const isProductionDeployment = import.meta.env.VITE_DEPLOY_MODE === "production";
const isHostedDeployment = isProductionDeployment || import.meta.env.VITE_DEPLOY_MODE === "netlify-demo";
const pageKeys = new Set<PageKey>(["dashboard", "ai", "customers", "pool", "approvals", "plans", "plan-stats", "exports", "business", "channels", "downstream-channels", "messages", "import", "audit", "system", "profile", "changelog", "customer-research"]);

function pageFromLocation(): PageKey {
  const key = window.location.hash.replace(/^#\/?/, "").split(/[/?]/u)[0] as PageKey;
  if (key === "plan-stats") {
    window.history.replaceState(null, "", "#/plans?view=statistics");
    return "plans";
  }
  return pageKeys.has(key) ? key : "dashboard";
}

function researchCustomerIdFromLocation(hash = window.location.hash) {
  const match = hash.match(/^#\/customer-research\/([^/?]+)/u);
  return match ? decodeURIComponent(match[1]) : "";
}

const managerNavGroups: NavGroup[] = [
  {
    label: "核心经营",
    items: [
      { key: "dashboard", label: "联合运营总览", icon: HomeSimple },
      { key: "approvals", label: "申报审批", icon: ClipboardCheck },
      { key: "plans", label: "计划督办", icon: Calendar },
      { key: "customers", label: "全部客户库", icon: Building }
    ]
  },
  {
    label: "资源协同",
    items: [
      { key: "channels", label: "渠道资源库", icon: Community },
      { key: "business", label: "商业组销售", icon: UserCircle },
      { key: "ai", label: "AI 作战助手", icon: Spark },
      { key: "messages", label: "通知与公告", icon: Bell }
    ]
  },
  {
    label: "数据与治理",
    items: [
      { key: "exports", label: "数据导出", icon: Download },
      { key: "import", label: "Excel 导入", icon: CloudUpload },
      { key: "audit", label: "审计日志", icon: ShieldCheck },
      { key: "system", label: "系统运行", icon: StatsUpSquare },
      { key: "changelog", label: "升级日志", icon: JournalPage }
    ]
  }
];

const channelNavGroups: NavGroup[] = [
  {
    label: "日常工作",
    items: [
      { key: "dashboard", label: "我的工作台", icon: HomeSimple },
      { key: "approvals", label: "通路申报", icon: PlusCircle },
      { key: "plans", label: "计划与进度", icon: Calendar },
      { key: "customers", label: "我的客户", icon: Building },
      { key: "downstream-channels", label: "下游渠道资源池", icon: Community },
      { key: "pool", label: "待认领客户池", icon: PageSearch }
    ]
  },
  {
    label: "协作与账号",
    items: [
      { key: "ai", label: "AI 作战助手", icon: Spark },
      { key: "messages", label: "消息中心", icon: Bell },
      { key: "profile", label: "渠道资料", icon: UserCircle }
    ]
  }
];

const businessNavGroups: NavGroup[] = [
  {
    label: "经营视图",
    items: [
      { key: "dashboard", label: "渠道经营总览", icon: HomeSimple },
      { key: "plans", label: "计划全景", icon: Calendar },
      { key: "customers", label: "对接渠道客户", icon: Building },
      { key: "channels", label: "对接渠道", icon: Community }
    ]
  },
  {
    label: "协作工具",
    items: [
      { key: "ai", label: "AI 作战助手", icon: Spark },
      { key: "messages", label: "消息中心", icon: Bell }
    ]
  }
];

function navGroupsFor(user: User) {
  if (user.role === "manager") return managerNavGroups;
  if (user.role === "business") return businessNavGroups;
  return channelNavGroups.map((group) => ({
    ...group,
    items: group.items.filter((item) => item.key !== "downstream-channels" || user.downstreamChannelEnabled).map((item) => {
      if (item.key === "approvals" && user.accountRole === "channel_owner") return { ...item, label: "申报与审批" };
      if (item.key === "profile" && user.accountRole === "channel_owner") return { ...item, label: "渠道与账号" };
      return item;
    })
  }));
}

function navFor(user: User) {
  return navGroupsFor(user).flatMap((group) => group.items);
}

function actionableApplicationCount(state: AppState, user: User) {
  if (user.role === "manager") return state.applications.filter((item) => item.status === "待总管理员审批").length;
  if (user.role === "business") return 0;
  if (user.accountRole === "channel_owner") return state.applications.filter((item) => item.status === "待渠道审核").length;
  return state.applications.filter((item) => ["渠道退回补充", "总管理员退回补充"].includes(item.status)).length;
}

function formatDate(value?: string) {
  if (!value) return "—";
  return new Intl.DateTimeFormat("zh-CN", { timeZone: "Asia/Shanghai", month: "numeric", day: "numeric" }).format(new Date(value.includes("T") ? value : `${value}T00:00:00+08:00`));
}

function formatDateTime(value?: string) {
  if (!value) return "—";
  return new Intl.DateTimeFormat("zh-CN", {
    timeZone: "Asia/Shanghai",
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false
  }).format(new Date(value));
}

function todayOffset(days: number) {
  const now = new Date(Date.now() + (days * 86400000));
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Shanghai", year: "numeric", month: "2-digit", day: "2-digit" }).format(now);
}

function FavoriteButton({ customer, onToggle }: { customer: Pick<Customer, "id" | "name" | "favorite">; onToggle: (customer: Pick<Customer, "id" | "favorite">) => void | Promise<void> }) {
  return (
    <button
      type="button"
      className={`favorite-button ${customer.favorite ? "is-favorite" : ""}`}
      aria-label={customer.favorite ? `取消重点客户：${customer.name}` : `标记重点客户：${customer.name}`}
      aria-pressed={Boolean(customer.favorite)}
      onClick={(event) => {
        event.stopPropagation();
        void onToggle(customer);
      }}
    >
      <Star width={19} height={19} strokeWidth={1.8} aria-hidden />
    </button>
  );
}

export default function App() {
  const [user, setUser] = useState<User | null>(null);
  const [state, setState] = useState<AppState | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState<PageKey>(pageFromLocation);
  const [routeHash, setRouteHash] = useState(() => window.location.hash);
  const [mobileNav, setMobileNav] = useState(false);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [passwordForm, setPasswordForm] = useState(false);

  useEffect(() => {
    api<{ user: User }>("/api/session")
      .then(({ user: sessionUser }) => {
        setUser(sessionUser);
        return api<AppState>("/api/state");
      })
      .then((nextState) => { setState(nextState); setUser(nextState.session.user); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const navigate = useCallback((nextPage: PageKey, replace = false, queryString = "") => {
    setPage(nextPage);
    const normalizedQuery = queryString.replace(/^\?/u, "");
    const nextHash = `#/${nextPage}${normalizedQuery ? `?${normalizedQuery}` : ""}`;
    setRouteHash(nextHash);
    if (window.location.hash === nextHash) return;
    if (replace) window.history.replaceState(null, "", nextHash);
    else window.history.pushState(null, "", nextHash);
  }, []);

  useEffect(() => {
    const syncPage = () => {
      setPage(pageFromLocation());
      setRouteHash(window.location.hash);
    };
    window.addEventListener("hashchange", syncPage);
    window.addEventListener("popstate", syncPage);
    return () => {
      window.removeEventListener("hashchange", syncPage);
      window.removeEventListener("popstate", syncPage);
    };
  }, []);

  useEffect(() => {
    if (!user) return;
    if (page === "customer-research" && researchCustomerIdFromLocation()) return;
    if (!navFor(user).some((item) => item.key === page)) navigate("dashboard", true);
  }, [navigate, page, user]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [page, user?.id]);

  const refresh = useCallback(async () => setState(await api<AppState>("/api/state")), []);

  const notify = useCallback((tone: Notice["tone"], message: string) => {
    setNotice({ tone, message });
    window.setTimeout(() => setNotice(null), 3600);
  }, []);

  const login = async (account: string, password: string, otp?: string) => {
    try {
      const result = await post<{ user?: User; mfaRequired?: boolean; message?: string }>("/api/auth/login", { account, password, otp });
      if (result.mfaRequired) return { mfaRequired: true, message: result.message };
      if (!result.user) throw new Error("登录响应无效");
      const nextState = await api<AppState>("/api/state");
      setUser(nextState.session.user);
      setState(nextState);
      return { mfaRequired: false };
    } catch (error) {
      notify("error", error instanceof Error ? error.message : "登录失败");
      return { mfaRequired: Boolean(otp) };
    }
  };

  const logout = async () => {
    await post("/api/auth/logout", {});
    setUser(null);
    setState(null);
    navigate("dashboard", true);
  };

  if (loading) return <LoadingScreen />;
  if (!user || !state) return <Login onLogin={login} notice={notice} />;
  if (page === "customer-research" && researchCustomerIdFromLocation(routeHash)) {
    const researchCustomerId = researchCustomerIdFromLocation(routeHash);
    return <Suspense fallback={<LoadingScreen />}><CustomerResearchWorkspace key={researchCustomerId} customerId={researchCustomerId} user={user} onLogout={logout} /></Suspense>;
  }

  const navGroups = navGroupsFor(user);
  const actionableCount = actionableApplicationCount(state, user);
  const unreadMessage = (state.messages ?? [])
    .filter((message) => !message.read && (user.role !== "manager" || message.targetType === "all"))
    .sort((left, right) => new Date(left.createdAt).getTime() - new Date(right.createdAt).getTime())[0];

  return (
    <div className="app-shell">
      <aside className={`sidebar ${mobileNav ? "is-open" : ""}`}>
        <div className="brand-lockup">
          <BrandMark />
          <div><strong>渠道作战沙盘</strong><small>客户 × 渠道协同运营</small></div>
        </div>
        <nav aria-label="主导航">
          <p className="nav-workspace-caption">{user.role === "manager" ? "管理组工作区" : user.role === "business" ? "商业组工作区" : "渠道工作区"}</p>
          <div className="nav-groups">
            {navGroups.map((group) => (
              <div className="nav-group" aria-label={group.label} key={group.label}>
                <p className="nav-caption">{group.label}</p>
                {group.items.map((item) => {
                  const Icon = item.icon;
                  return (
                    <button type="button" aria-current={page === item.key ? "page" : undefined} className={page === item.key ? "active" : ""} key={item.key} onClick={() => { navigate(item.key); setMobileNav(false); }}>
                      <Icon {...iconProps} /> <span>{item.label}</span>
                      {item.key === "approvals" && actionableCount > 0 && <em>{actionableCount}</em>}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </nav>
        <div className="sidebar-foot">
          <div className={`demo-mode ${isProductionDeployment ? "production-mode" : ""}`}><Spark {...iconProps} /><span><strong>{isProductionDeployment ? "正式运行环境" : isHostedDeployment ? "在线演示环境" : "本地演示模式"}</strong><small>{isProductionDeployment ? "PostgreSQL · 自动备份" : isHostedDeployment ? "与本地数据隔离" : "未连接正式业务库"}</small></span></div>
          <div className="user-chip">
            <span className="avatar">{user.name.slice(0, 1)}</span>
            <span><strong>{user.name}</strong><small>{user.title}</small></span>
            <button className="icon-button" aria-label="修改我的密码" onClick={() => setPasswordForm(true)}><Settings {...iconProps} /></button>
            <button className="icon-button" aria-label="退出登录" onClick={logout}><LogOut {...iconProps} /></button>
          </div>
        </div>
      </aside>
      {mobileNav && <button className="nav-scrim" aria-label="关闭导航" onClick={() => setMobileNav(false)} />}
      <main id="main-content">
        <header className="topbar">
          <button className="icon-button menu-toggle" aria-label="打开导航" onClick={() => setMobileNav(true)}><Menu {...iconProps} /></button>
          <div className="breadcrumb"><small>{pageGroupLabel(page, user)}</small><span>{pageLabel(page, user)}</span></div>
          <div className="top-actions">
            <button className="ai-top-entry" onClick={() => navigate("ai")}><Spark width={17} height={17} strokeWidth={1.7} aria-hidden />AI 助手</button>
            <span className="mode-tag">V{CURRENT_VERSION} · {isProductionDeployment ? "正式运行" : isHostedDeployment ? "在线演示" : "演示数据"}</span>
            <button className="icon-button notification-button" aria-label={`消息中心${state.unreadMessageCount ? `，${state.unreadMessageCount} 条未读` : ""}`} onClick={() => navigate("messages")}><Bell {...iconProps} />{Boolean(state.unreadMessageCount) && <em>{state.unreadMessageCount}</em>}</button>
          </div>
        </header>
        <div className="page-wrap">
          {page === "dashboard" && <Dashboard state={state} user={user} navigate={navigate} />}
          {page === "ai" && <AiAssistantPage state={state} user={user} refresh={refresh} notify={notify} navigate={navigate} />}
          {page === "customers" && <CustomersPage state={state} user={user} refresh={refresh} notify={notify} />}
          {page === "pool" && <PoolPage state={state} user={user} notify={notify} refresh={refresh} />}
          {page === "approvals" && (user.role === "manager"
            ? <ApprovalsPage state={state} user={user} refresh={refresh} notify={notify} />
            : user.accountRole === "channel_owner"
              ? <ApprovalsPage state={state} user={user} refresh={refresh} notify={notify} />
              : <ApplicationsPage state={state} user={user} refresh={refresh} notify={notify} />)}
          {page === "plans" && <PlansPage state={state} refresh={refresh} notify={notify} user={user} />}
          {page === "exports" && <ExportPage state={state} notify={notify} />}
          {page === "business" && <BusinessUsersPage state={state} refresh={refresh} notify={notify} />}
          {page === "messages" && <MessagesPage state={state} user={user} refresh={refresh} notify={notify} />}
          {page === "channels" && <ChannelsPage state={state} user={user} refresh={refresh} notify={notify} />}
          {page === "downstream-channels" && <DownstreamChannelsPage state={state} user={user} refresh={refresh} notify={notify} />}
          {page === "import" && <ImportPage notify={notify} refreshState={refresh} />}
          {page === "audit" && <AuditPage state={state} notify={notify} />}
          {page === "system" && <SystemPage user={user} notify={notify} />}
          {page === "profile" && <ProfilePage state={state} user={user} refresh={refresh} notify={notify} />}
          {page === "changelog" && <Suspense fallback={<div className="inline-loading">正在加载升级日志…</div>}><ChangelogPage /></Suspense>}
        </div>
      </main>
      {notice && <div className={`toast ${notice.tone}`} role="status">{notice.tone === "success" ? <CheckCircle {...iconProps} /> : <WarningTriangle {...iconProps} />} {notice.message}</div>}
      {passwordForm && <PasswordForm user={user} close={() => setPasswordForm(false)} notify={notify} onUserUpdated={setUser} onDone={(message) => { setPasswordForm(false); notify("success", message); }} />}
      {unreadMessage && <AutoMessagePopup message={unreadMessage} refresh={refresh} openCenter={() => navigate("messages")} notify={notify} />}
    </div>
  );
}

function LoadingScreen() {
  return <div className="loading-screen"><BrandMark large /><p>正在整理联合运营工作区…</p></div>;
}

function Login({ onLogin, notice }: { onLogin: (account: string, password: string, otp?: string) => Promise<{ mfaRequired: boolean; message?: string }>; notice: Notice | null }) {
  const [account, setAccount] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [mfaRequired, setMfaRequired] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const useDemoAccount = (nextAccount: string, nextPassword: string) => {
    setAccount(nextAccount);
    setPassword(nextPassword);
  };
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    const result = await onLogin(account, password, mfaRequired ? otp : undefined);
    setMfaRequired(result.mfaRequired);
    setSubmitting(false);
  };
  return (
    <main className="login-page">
      <section className="login-story">
        <div className="login-flow-ribbons" aria-hidden="true">
          <svg viewBox="0 0 850 810" preserveAspectRatio="none">
            <path className="flow-route-one" d="M-80 760 C220 720 190 360 560 330 S710 146 940 90" />
            <path className="flow-route-two" d="M-80 640 C210 648 274 500 500 500 S706 320 930 304" />
            <path className="flow-route-edge" d="M-40 684 C230 684 280 420 545 415 S720 246 910 208" />
          </svg>
        </div>
        <div className="brand-lockup light">
          <BrandMark label="渠道作战沙盘双链汇流标识" />
          <div><strong>渠道作战沙盘</strong><small>客户与渠道联合运营工作台</small></div>
        </div>
        <div className="login-copy">
          <p className="eyebrow">CHANNEL JOINT OPERATIONS</p>
          <h1>让每一条渠道关系，<br /><span>都有清晰的下一步。</span></h1>
          <p>从客户归属、通路申报到计划执行，把分散的渠道协同沉淀为可追踪、可推进、可复盘的共同工作面。</p>
          <div className="login-capability-line" aria-label="平台核心能力"><span>关系可追溯</span><i /><span>计划可推进</span><i /><span>协同可复盘</span></div>
        </div>
        <div className="login-live-chain" aria-hidden="true">
          <b /><span>渠道关系</span><i /><span>客户通路</span><i /><span>工作计划</span>
        </div>
      </section>
      <section className="login-panel">
        <div className="login-box">
          <p className="eyebrow">UNIFIED SIGN IN</p>
          <h2>进入渠道联合作战台</h2>
          <p className="muted">管理员、商业组销售、渠道负责人和渠道销售使用同一入口，系统会根据账号自动识别权限。</p>
          <form className="login-form" onSubmit={submit}>
            <label className="field"><span>账号</span><input autoComplete="username" value={account} onChange={(event) => { setAccount(event.target.value); setMfaRequired(false); setOtp(""); }} placeholder="请输入登录账号" required disabled={mfaRequired} /></label>
            <label className="field"><span>密码</span><input type="password" autoComplete="current-password" value={password} onChange={(event) => { setPassword(event.target.value); setMfaRequired(false); setOtp(""); }} placeholder="请输入密码" required disabled={mfaRequired} /></label>
            {mfaRequired && <label className="field mfa-login-field"><span>动态验证码</span><input inputMode="numeric" autoComplete="one-time-code" value={otp} onChange={(event) => setOtp(event.target.value.replace(/\D/gu, "").slice(0, 6))} placeholder="6 位动态验证码" required autoFocus /></label>}
            <button className="button primary" disabled={!account.trim() || !password || submitting || (mfaRequired && otp.length !== 6)}>{submitting ? "正在验证…" : mfaRequired ? "验证并登录" : "登录"}<ArrowRight {...iconProps} /></button>
            {mfaRequired && <button type="button" className="text-button login-mfa-back" onClick={() => { setMfaRequired(false); setOtp(""); }}>返回修改账号或密码</button>}
          </form>
          {isProductionDeployment ? (
            <div className="login-note production-note"><ShieldCheck {...iconProps} /><span>当前为正式运行环境。</span></div>
          ) : isHostedDeployment ? (
            <div className="login-note"><ShieldCheck {...iconProps} /><span>这是与本地文件完全隔离的在线演示环境。登录账号由项目管理员保管，线上操作不会修改本地演示数据。</span></div>
          ) : (
            <>
              <div className="demo-account-list">
                <span>本地演示账号</span>
                <button type="button" onClick={() => useDemoAccount("admin", "admin011")}><ShieldCheck {...iconProps} /><span><strong>总管理员</strong><small>admin</small></span><em>填入</em></button>
                <button type="button" onClick={() => useDemoAccount("business.sales", "demo011")}><UserCircle {...iconProps} /><span><strong>商业组销售</strong><small>business.sales</small></span><em>填入</em></button>
                <button type="button" onClick={() => useDemoAccount("qinghe.owner", "demo011")}><Community {...iconProps} /><span><strong>渠道负责人</strong><small>qinghe.owner</small></span><em>填入</em></button>
                <button type="button" onClick={() => useDemoAccount("qinghe.sales", "demo011")}><UserCircle {...iconProps} /><span><strong>渠道销售</strong><small>qinghe.sales</small></span><em>填入</em></button>
              </div>
              <div className="login-note"><Database {...iconProps} /><span>页面使用当前本地数据；Excel 只有在确认导入后才会更新客户库，尚未连接正式业务数据库。</span></div>
            </>
          )}
          {notice && <p className="form-error">{notice.message}</p>}
        </div>
      </section>
    </main>
  );
}

type DashboardPlanMatch = {
  route: RouteItem;
  plan: Plan;
  state: NonNullable<Plan["state"]>;
};

function dashboardPlanMatches(routes: RouteItem[], query: string) {
  const tokens = query.trim().toLocaleLowerCase("zh-CN").split(/\s+/u).filter(Boolean);
  if (!tokens.length) return [];
  const matches = new Map<string, DashboardPlanMatch>();
  routes.forEach((route) => {
    const plans = [route.plan, ...(route.queuedPlans ?? []), ...(route.planTimeline ?? [])].filter((plan): plan is Plan => Boolean(plan));
    plans.forEach((plan) => {
      const state = plan.state ?? (route.plan?.id === plan.id ? "current" : "queued");
      if (state === "cancelled" || state === "deleted") return;
      const text = [
        route.customer?.name,
        route.customer?.region,
        route.customer?.industry,
        route.channel?.name,
        route.channel?.shortName,
        route.channelPath,
        route.downstreamFollower?.name,
        route.type,
        route.stage,
        plan.nextAction,
        plan.stage,
        plan.owner,
        plan.status,
        ...(plan.workTags ?? []).map((tag) => tag.name)
      ].filter(Boolean).join(" ").toLocaleLowerCase("zh-CN");
      if (tokens.every((token) => text.includes(token))) matches.set(`${route.id}:${plan.id}`, { route, plan, state });
    });
  });
  const stateOrder: Record<NonNullable<Plan["state"]>, number> = {
    current: 0,
    queued: 1,
    completed: 2,
    cancelled: 3,
    deleted: 3
  };
  const proximity = (value: string) => {
    const time = new Date(`${value}T00:00:00+08:00`).getTime();
    return Number.isFinite(time) ? Math.abs(time - Date.now()) : Number.MAX_SAFE_INTEGER;
  };
  return [...matches.values()].sort((left, right) => stateOrder[left.state] - stateOrder[right.state]
    || proximity(left.plan.followUpDate) - proximity(right.plan.followUpDate)
    || left.plan.nextAction.localeCompare(right.plan.nextAction, "zh-CN"));
}

function DashboardSearch({ state, user, navigate }: { state: AppState; user: User; navigate: Navigate }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupAttempted, setLookupAttempted] = useState(false);
  const [lookupError, setLookupError] = useState("");
  const root = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const normalizedQuery = query.trim();
  const planMatches = useMemo(
    () => dashboardPlanMatches(state.plans.length ? state.plans : state.routes, normalizedQuery).slice(0, 5),
    [normalizedQuery, state.plans, state.routes]
  );
  const placeholder = user.role === "business" ? "搜索对接渠道客户或计划…" : user.role === "channel" ? "搜索我的客户或计划…" : "搜索客户或工作计划…";

  useEffect(() => {
    const handleShortcut = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        input.current?.focus();
        setOpen(true);
      }
    };
    window.addEventListener("keydown", handleShortcut);
    return () => window.removeEventListener("keydown", handleShortcut);
  }, []);

  useEffect(() => {
    const closeOnOutsidePress = (event: PointerEvent) => {
      if (root.current && !root.current.contains(event.target as Node)) {
        setOpen(false);
        input.current?.blur();
      }
    };
    window.addEventListener("pointerdown", closeOnOutsidePress);
    return () => window.removeEventListener("pointerdown", closeOnOutsidePress);
  }, []);

  useEffect(() => {
    if (!normalizedQuery) {
      setCustomers([]);
      setLookupLoading(false);
      setLookupAttempted(false);
      setLookupError("");
      return;
    }
    const controller = new AbortController();
    setCustomers([]);
    setLookupLoading(true);
    setLookupAttempted(false);
    setLookupError("");
    const timer = window.setTimeout(() => {
      const params = new URLSearchParams({ q: normalizedQuery, region: "all", routeFilter: "all", sort: "recent", page: "1", pageSize: "5" });
      api<CustomerLibraryResult>(`/api/customers/library?${params}`, { signal: controller.signal })
        .then((result) => setCustomers(result.items.slice(0, 5)))
        .catch((error) => {
          if (!controller.signal.aborted) setLookupError(error instanceof Error ? error.message : "客户搜索暂时不可用");
        })
        .finally(() => {
          if (!controller.signal.aborted) {
            setLookupLoading(false);
            setLookupAttempted(true);
          }
        });
    }, 220);
    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [normalizedQuery]);

  const openPage = (page: "customers" | "plans", params: URLSearchParams) => {
    setOpen(false);
    navigate(page, false, params.toString());
  };
  const moveToFirstResult = () => window.requestAnimationFrame(() => root.current?.querySelector<HTMLButtonElement>(".global-search-result")?.focus());
  const totalResults = customers.length + planMatches.length;

  return (
    <div className={`global-search ${open ? "is-open" : ""}`} ref={root} role="search">
      <label className="global-search-field">
        <Search width={23} height={23} strokeWidth={1.65} aria-hidden />
        <span className="sr-only">搜索客户或工作计划</span>
        <input
          ref={input}
          type="search"
          value={query}
          placeholder={placeholder}
          aria-expanded={open}
          aria-controls="dashboard-search-panel"
          onFocus={() => setOpen(true)}
          onClick={() => setOpen(true)}
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
          }}
          onKeyDown={(event) => {
            if (event.key === "Escape") setOpen(false);
            if (event.key === "ArrowDown" && totalResults > 0) {
              event.preventDefault();
              moveToFirstResult();
            }
          }}
        />
        {query ? <button type="button" className="global-search-clear" aria-label="清除搜索" onClick={() => { setQuery(""); input.current?.focus(); }}><Xmark width={18} height={18} strokeWidth={1.8} aria-hidden /></button> : <kbd>⌘ K</kbd>}
      </label>
      {open && <section id="dashboard-search-panel" className="global-search-panel" aria-label="客户与工作计划搜索结果" onKeyDown={(event) => {
        if (event.key === "Escape") {
          event.preventDefault();
          input.current?.focus();
          setOpen(false);
        }
      }}>
        {!normalizedQuery ? <div className="global-search-intro"><span><PageSearch width={22} height={22} strokeWidth={1.6} aria-hidden /></span><div><strong>先搜索，再决定去哪里</strong><small>输入客户全称、简称、渠道名称、计划动作、阶段或负责人。</small></div></div> : <>
          <header className="global-search-summary" aria-live="polite"><span>{lookupLoading ? "正在检索客户…" : lookupError ? "客户检索未完成" : `找到 ${customers.length} 个客户、${planMatches.length} 项计划`}</span><small>选择具体结果后再打开对应页面</small></header>
          {customers.length > 0 && <div className="global-search-group"><h3>客户</h3>{customers.map((customer) => <button type="button" className="global-search-result" key={customer.id} onClick={() => openPage("customers", new URLSearchParams({ q: customer.name, customer: customer.id }))}><span className="global-search-result-icon"><Building width={19} height={19} strokeWidth={1.65} aria-hidden /></span><span><strong>{customer.name}</strong><small>{[customer.region, customer.industry || "行业待补充"].filter(Boolean).join(" · ")}</small></span><em>{customer.planStatus || "客户"}</em><NavArrowRight width={18} height={18} strokeWidth={1.65} aria-hidden /></button>)}</div>}
          {planMatches.length > 0 && <div className="global-search-group"><h3>工作计划</h3>{planMatches.map(({ route, plan, state: planState }) => <button type="button" className="global-search-result" key={`${route.id}:${plan.id}`} onClick={() => openPage("plans", new URLSearchParams({ q: plan.nextAction }))}><span className="global-search-result-icon plan"><Calendar width={19} height={19} strokeWidth={1.65} aria-hidden /></span><span><strong>{plan.nextAction}</strong><small>{[route.customer?.name, route.channelPath || route.channel?.shortName || route.channel?.name, route.type, formatDate(plan.followUpDate)].filter(Boolean).join(" · ")}</small></span><em>{planState === "current" ? "当前计划" : planState === "queued" ? "后续计划" : "已完成"}</em><NavArrowRight width={18} height={18} strokeWidth={1.65} aria-hidden /></button>)}</div>}
          {lookupAttempted && !lookupLoading && !lookupError && totalResults === 0 && <div className="global-search-empty"><PageSearch width={24} height={24} strokeWidth={1.6} aria-hidden /><strong>没有匹配的客户或计划</strong><small>可以缩短关键词，或检查客户名称、计划动作是否准确。</small></div>}
          {lookupError && <div className="global-search-empty error"><WarningTriangle width={22} height={22} strokeWidth={1.6} aria-hidden /><strong>客户检索暂时不可用</strong><small>仍可查看上方已匹配的工作计划，或稍后重试。</small></div>}
          <footer className="global-search-footer"><button type="button" onClick={() => openPage("customers", new URLSearchParams({ q: normalizedQuery }))}><Building width={17} height={17} strokeWidth={1.6} aria-hidden />在客户库中查看全部</button><button type="button" onClick={() => openPage("plans", new URLSearchParams({ q: normalizedQuery }))}><Calendar width={17} height={17} strokeWidth={1.6} aria-hidden />在计划页中查看全部</button></footer>
        </>}
      </section>}
    </div>
  );
}

function Dashboard({ state, user, navigate }: { state: AppState; user: User; navigate: Navigate }) {
  const role = user.role;
  const hero = state.summary.hero;
  const pending = state.applications.filter((item) => item.status === "待总管理员审批");
  const focusPlans = state.plans
    .filter((item) => role === "manager"
      ? ["已逾期", "推进受阻", "今日到期", "三日内到期"].includes(item.plan?.status ?? "")
      : role === "business"
        ? ["已逾期", "推进受阻", "今日到期", "三日内到期"].includes(item.plan?.status ?? "")
        : item.canOperate)
    .slice(0, 6);
  const approvalFirst = role === "manager" || role === "channel";
  const primaryTarget: PageKey = approvalFirst ? "approvals" : "plans";
  const primaryLabel = role === "manager" ? "处理待审批" : role === "business" ? "查看渠道计划" : user.accountRole === "channel_owner" ? "处理申报审批" : "查看通路申报";
  const secondaryTarget: PageKey = role === "business" ? "channels" : "plans";
  const secondaryLabel = role === "manager" ? "查看计划督办" : role === "business" ? "查看对接渠道" : "更新计划进展";
  return (
    <>
      <section className="welcome">
        <div className="welcome-copy">
          <p className="eyebrow">{role === "manager" ? "联合运营总览" : role === "business" ? "对接渠道经营总览" : "今天的工作"}</p>
          {hero ? (
            <h1 className="welcome-title">
              <span className="welcome-salutation">{hero.salutation}</span>
              {hero.clauses?.map((clause, index) => (
                <span className={`welcome-clause tone-${clause.tone ?? "neutral"}`} key={`${clause.tail}-${index}`}>
                  {clause.lead ? `${clause.lead} ` : ""}<strong>{clause.value}</strong> {clause.tail}
                </span>
              ))}
              {hero.calmText && <span className="welcome-calm">{hero.calmText}</span>}
            </h1>
          ) : <h1>{state.summary.greeting}</h1>}
          <p>{approvalFirst ? "先处理申报，再推进计划；每个数字均来自当前数据状态。" : "先识别重点计划，再进入客户与渠道动作；每个数字均来自当前数据状态。"}</p>
        </div>
        <div className="welcome-actions">
          <button className="button primary" onClick={() => navigate(primaryTarget)}>{approvalFirst ? <ClipboardCheck {...iconProps} /> : <Calendar {...iconProps} />}{primaryLabel}</button>
          <button className="button secondary" onClick={() => navigate(secondaryTarget)}>{role === "business" ? <Community {...iconProps} /> : <Calendar {...iconProps} />}{secondaryLabel}</button>
        </div>
      </section>
      <DashboardSearch state={state} user={user} navigate={navigate} />
      <section className={`metric-strip ${role === "channel" ? "channel-metrics" : ""}`} aria-label="核心指标">
        {state.summary.metrics.map((metric) => {
          const visual = metricVisualFor(metric.label, metric.visual);
          const presentation = metricVisuals[visual];
          const MetricIcon = presentation.icon;
          return (
            <div className={`metric-card metric-tone-${presentation.tone}`} key={metric.label}>
              <span className="metric-icon"><MetricIcon width={21} height={21} strokeWidth={1.7} aria-hidden /></span>
              <strong>{metric.value}</strong>
              <small>{metric.label}</small>
            </div>
          );
        })}
      </section>
      {role === "manager" ? (
        <div className="dashboard-grid">
          <section className="panel focus-panel">
            <PanelHead eyebrow="ACTION QUEUE" title="需要处理的申报" action={() => navigate("approvals")} />
            {pending.length ? pending.slice(0, 4).map((application) => <ApplicationRow key={application.id} application={application} />) : <EmptyState compact title="当前没有待审批事项" text="新的通路申报会自动出现在这里。" />}
          </section>
          <section className="panel">
            <PanelHead eyebrow="OPERATION PULSE" title="联合运营脉搏" action={() => navigate("plans")} />
            <div className="pulse-list">
              {focusPlans.length ? focusPlans.map((route) => <PlanRow key={route.id} route={route} compact />) : <EmptyState compact title="工作节奏平稳" text="暂无到期、逾期或受阻计划。" />}
            </div>
          </section>
        </div>
      ) : (
        <section className="panel current-plan-panel">
          <PanelHead eyebrow="MY CURRENT PLANS" title={role === "business" ? "对接渠道重点计划" : "我的当前工作计划"} action={() => navigate("plans")} />
          <div className="current-plan-grid">
            {focusPlans.length ? focusPlans.map((route) => <PlanRow key={route.id} route={route} compact />) : <EmptyState compact title={role === "business" ? "对接渠道当前无风险计划" : "当前没有分配给你的工作计划"} text={role === "business" ? "这里会汇总临期、逾期和推进受阻的只读计划。" : "可从我的客户进入已获批通路，直接建立下一步计划。"} />}
          </div>
        </section>
      )}
      <section className="coverage-board">
        <div><p className="eyebrow">ROUTE COVERAGE</p><h2>{role === "manager" ? "技术口与采购口覆盖" : "我的通路覆盖"}</h2><p>同一客户的两条通路独立推进，避免关系和计划混在一段文字里。</p></div>
        <div className="coverage-cells">
          <div className="technical"><span>技术口</span><strong>{state.routes.filter((route) => route.type === "技术口").length}</strong><small>条有效通路</small></div>
          <div className="purchase"><span>采购口</span><strong>{state.routes.filter((route) => route.type === "采购口").length}</strong><small>条有效通路</small></div>
        </div>
      </section>
    </>
  );
}

type AiAssistantResult = {
  interactionId?: string;
  conversationId?: string;
  turn?: number;
  prompt?: string;
  createdAt?: string;
  archived?: boolean;
  status: "ok" | "unavailable" | "failed";
  model: string;
  intent?: "overview" | "plan_prioritization" | "customer_insight" | "application_review" | "channel_matching" | "drafting" | "freeform";
  intentLabel?: string;
  headline?: string;
  answer: string;
  keyPoints?: string[];
  recommendations?: { title: string; reason: string; priority: "high" | "medium" | "low" }[];
  followUpQuestions?: string[];
  evidence: string[];
  serviceConfigured: boolean;
  scope: {
    customers: { available: number; used: number };
    routes: { available: number; used: number };
    applications: { available: number; used: number };
    disclosure: string;
  };
  visualization?: {
    mode?: "overview" | "plan_prioritization" | "customer_insight" | "application_review" | "channel_matching" | "drafting" | "freeform";
    intentLabel?: string;
    metrics: {
      key: "pending" | "dueSoon" | "overdue" | "blocked" | "unplanned";
      label: string;
      value: number;
      tone: "success" | "warning" | "danger" | "neutral";
    }[];
    planStatuses: {
      label: string;
      value: number;
      tone: "success" | "warning" | "danger" | "neutral";
    }[];
    attentionItems: {
      id: string;
      kind: "application" | "plan";
      eyebrow: string;
      title: string;
      detail: string;
      status: string;
      date?: string | null;
      tone: "success" | "warning" | "danger" | "neutral";
    }[];
    customerItems?: { id: string; title: string; detail: string; status: string }[];
    matching?: { pendingReferences: number; pendingAssociationReviews: number } | null;
    planTotal: number;
    generatedFrom: "permission_context";
  } | null;
  draft?: {
    id: string;
    type: "plan";
    summary: string;
    status: string;
    payload: {
      routeId: string;
      mode: "current" | "queued";
      stage: string;
      nextAction: string;
      followUpDate: string;
      collaboration?: string;
    };
  } | null;
};

type AiMetricKey = "pending" | "dueSoon" | "overdue" | "blocked" | "unplanned";
type AiHistoryResult = { items: AiAssistantResult[]; total: number };

function AiMetricIcon({ metricKey }: { metricKey: AiMetricKey }) {
  if (metricKey === "pending") return <ClipboardCheck {...iconProps} />;
  if (metricKey === "dueSoon") return <Clock {...iconProps} />;
  if (metricKey === "overdue" || metricKey === "blocked") return <WarningTriangle {...iconProps} />;
  return <Calendar {...iconProps} />;
}

function AiAssistantPage({ state, user, refresh, notify, navigate }: { state: AppState; user: User; refresh: () => Promise<void>; notify: (tone: Notice["tone"], message: string) => void; navigate: (page: PageKey) => void }) {
  const [prompt, setPrompt] = useState("");
  const [responseMode, setResponseMode] = useState<"auto" | "concise" | "analysis" | "action">("auto");
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [conversationTurn, setConversationTurn] = useState(0);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<AiAssistantResult | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [workspaceView, setWorkspaceView] = useState<"ask" | "archive">("ask");
  const [history, setHistory] = useState<AiAssistantResult[]>([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyBusy, setHistoryBusy] = useState(false);
  const [historyQuery, setHistoryQuery] = useState("");
  const [historyStatus, setHistoryStatus] = useState("all");
  const roleLabel = user.role === "manager" ? "总管理员" : user.role === "business" ? "商业组销售" : user.accountRole === "channel_owner" ? "渠道负责人" : "渠道销售";
  const maxStatusValue = Math.max(1, ...(result?.visualization?.planStatuses.map((item) => item.value) ?? [1]));
  const riskPlanCount = result?.visualization?.metrics
    .filter((item) => ["dueSoon", "overdue", "blocked"].includes(item.key))
    .reduce((total, item) => total + item.value, 0) ?? 0;
  const riskPlanPercent = result?.visualization?.planTotal
    ? Math.round((riskPlanCount / result.visualization.planTotal) * 100)
    : 0;
  const visualMode = result?.visualization?.mode ?? "overview";
  const showMetrics = ["overview", "plan_prioritization", "application_review"].includes(visualMode);
  const showPlanHealth = ["overview", "plan_prioritization", "drafting"].includes(visualMode);
  const showAttention = ["overview", "plan_prioritization", "application_review", "drafting"].includes(visualMode);
  const suggestions = user.role === "manager"
    ? ["汇总当前待关联渠道与需要人工研判的事项", "梳理本周逾期和受阻计划，给出处理顺序", "根据可见数据生成一份渠道治理工作草稿"]
    : user.role === "business"
      ? ["汇总我对接渠道的计划完成情况", "比较对接渠道本月不同工作标签的有效工作", "生成一份对接渠道经营复盘草稿"]
    : user.accountRole === "channel_owner"
      ? ["汇总本渠道团队当前计划并指出缺口", "为本渠道逾期事项拟一份跟进计划", "梳理本渠道进行中的申报和待处理事项"]
      : ["列出我当前最需要处理的客户计划", "为我负责的客户生成一条后续计划草稿", "汇总我发起的申报和需要补充的内容"];
  const loadHistory = useCallback(async () => {
    setHistoryBusy(true);
    try {
      const params = new URLSearchParams({ limit: "50", status: historyStatus });
      if (historyQuery.trim()) params.set("q", historyQuery.trim());
      const response = await api<AiHistoryResult>(`/api/ai/interactions?${params}`);
      setHistory(response.items);
      setHistoryTotal(response.total);
    } catch (error) {
      notify("error", error instanceof Error ? error.message : "研判档案加载失败");
    } finally {
      setHistoryBusy(false);
    }
  }, [historyQuery, historyStatus, notify]);
  useEffect(() => {
    if (workspaceView === "archive") void loadHistory();
  }, [workspaceView, loadHistory]);
  const ask = async (event?: FormEvent) => {
    event?.preventDefault();
    if (!prompt.trim()) return;
    setBusy(true);
    try {
      const next = await post<AiAssistantResult>("/api/ai/assistant", {
        prompt: prompt.trim(),
        responseMode,
        conversationId
      });
      setResult({ ...next, archived: false });
      setConversationId(next.conversationId ?? null);
      setConversationTurn(next.turn ?? 1);
      setPrompt("");
    } catch (error) {
      notify("error", error instanceof Error ? error.message : "AI 作战助手暂时不可用");
    } finally {
      setBusy(false);
    }
  };
  const confirmDraft = async () => {
    if (!result?.draft) return;
    setConfirming(true);
    try {
      const response = await post<{ message: string }>(`/api/ai/drafts/${result.draft.id}/confirm`, {});
      await refresh();
      setResult((current) => current ? { ...current, draft: current.draft ? { ...current.draft, status: "confirmed" } : null } : current);
      if (workspaceView === "archive") await loadHistory();
      notify("success", response.message);
    } catch (error) {
      notify("error", error instanceof Error ? error.message : "AI 草稿确认失败");
    } finally {
      setConfirming(false);
    }
  };
  const openHistoryItem = (item: AiAssistantResult) => {
    setResult({ ...item, archived: true });
    setConversationId(item.conversationId ?? null);
    setConversationTurn(item.turn ?? 1);
    window.requestAnimationFrame(() => {
      document.querySelector(".ai-result-panel")?.scrollIntoView({ block: "start" });
    });
  };
  const startNewConversation = () => {
    setConversationId(null);
    setConversationTurn(0);
    setResult(null);
    setPrompt("");
    setWorkspaceView("ask");
  };
  return (
    <>
      <PageHeader eyebrow="PERMISSION-AWARE COPILOT" title="AI 作战助手" description={`面向${roleLabel}的客户、渠道与计划经营研判。`}>
        <button className="button secondary" onClick={() => navigate("plans")}><Calendar {...iconProps} />查看计划</button>
      </PageHeader>
      <section className="ai-assistant-shell">
        <div className="ai-assistant-intro">
          <div className="ai-orbit" aria-hidden="true"><Spark width={34} height={34} strokeWidth={1.4} /></div>
          <div><p className="eyebrow">DEEPSEEK OPERATIONS COPILOT</p><h2>提出问题，获得可执行的经营研判。</h2></div>
          <div className="ai-context-stats"><span><strong>{state.dataScope?.customers.available ?? state.customers.length}</strong>家权限内客户</span><span><strong>{state.dataScope?.routes.available ?? state.routes.length}</strong>条权限内通路</span><span><strong>{state.dataScope?.applications.available ?? state.applications.length}</strong>条权限内申报</span></div>
        </div>
        <div className="ai-workspace-tabs" role="tablist" aria-label="AI 作战助手视图">
          <button role="tab" aria-selected={workspaceView === "ask"} className={workspaceView === "ask" ? "active" : ""} onClick={() => setWorkspaceView("ask")}><Spark width={17} height={17} strokeWidth={1.6} aria-hidden />发起研判</button>
          <button role="tab" aria-selected={workspaceView === "archive"} className={workspaceView === "archive" ? "active" : ""} onClick={() => setWorkspaceView("archive")}><JournalPage width={17} height={17} strokeWidth={1.6} aria-hidden />研判档案{historyTotal > 0 && <span>{historyTotal}</span>}</button>
        </div>
        {workspaceView === "ask" ? <form className="ai-prompt-panel" onSubmit={ask}>
          <div className="ai-prompt-head">
            <div><span>{conversationId ? `继续当前对话 · 第 ${conversationTurn + 1} 轮` : "开始一个新的业务问题"}</span><small>{conversationId ? "助手会结合最近对话理解“第一家”“继续展开”等追问。" : "可直接询问客户、计划、申报、渠道匹配，也可以要求起草具体内容。"}</small></div>
            {conversationId && <button type="button" className="button ghost" onClick={startNewConversation}><PlusCircle {...iconProps} />新对话</button>}
          </div>
          <label><span>告诉助手你具体需要什么</span><textarea rows={5} maxLength={1000} value={prompt} onChange={(event) => setPrompt(event.target.value)} placeholder="例如：先分析澄江智能装备当前两条通路的差异，再给出本周处理顺序；或：把结论整理成一份给渠道负责人的行动清单。" /></label>
          <div className="ai-response-modes" role="group" aria-label="回答方式">
            {([
              ["auto", "智能组织", "根据问题自动选择结构"],
              ["concise", "只给结论", "控制篇幅，快速阅读"],
              ["analysis", "深入分析", "展开依据、风险和边界"],
              ["action", "行动清单", "突出优先级与下一步"]
            ] as const).map(([value, label, help]) => <button type="button" key={value} className={responseMode === value ? "active" : ""} aria-pressed={responseMode === value} onClick={() => setResponseMode(value)}><strong>{label}</strong><small>{help}</small></button>)}
          </div>
          <div className="ai-suggestion-list">{suggestions.map((item) => <button type="button" key={item} onClick={() => setPrompt(item)}>{item}</button>)}</div>
          <div className="ai-prompt-actions"><button className="button primary" disabled={busy || !prompt.trim()}>{busy ? "正在理解问题并组织回答…" : <><Spark {...iconProps} />{conversationId ? "继续追问" : "开始研判"}</>}</button></div>
        </form> : <section className="ai-history-panel">
          <header>
            <div><p className="eyebrow">PERSONAL REVIEW ARCHIVE</p><h2>我的研判档案</h2><p>仅展示当前账号生成的结果；每条记录保留生成时的数据范围和可视化快照。</p></div>
            <strong>{historyTotal}</strong>
          </header>
          <div className="ai-history-toolbar">
            <label className="search-field"><Search {...iconProps} /><input value={historyQuery} onChange={(event) => setHistoryQuery(event.target.value)} placeholder="搜索任务、结论或依据" /></label>
            <label><Filter width={17} height={17} strokeWidth={1.6} aria-hidden /><span className="sr-only">筛选研判状态</span><select value={historyStatus} onChange={(event) => setHistoryStatus(event.target.value)}><option value="all">全部状态</option><option value="ok">已完成</option><option value="unavailable">本地降级</option><option value="failed">调用失败</option></select></label>
          </div>
          {historyBusy ? <div className="loading-card">正在读取研判档案…</div> : history.length ? <div className="ai-history-list">
            {history.map((item) => <button type="button" key={item.interactionId} className={result?.interactionId === item.interactionId ? "selected" : ""} onClick={() => openHistoryItem(item)}>
              <span className={`ai-history-status ${item.status}`}><Spark width={16} height={16} strokeWidth={1.6} aria-hidden /></span>
              <span className="ai-history-copy"><small>{formatDateTime(item.createdAt)} · {item.model}</small><strong>{item.prompt || "未命名研判任务"}</strong><em>{item.answer}</em></span>
              <span className="ai-history-scope">客户 {item.scope.customers.used}/{item.scope.customers.available}<br />通路 {item.scope.routes.used}/{item.scope.routes.available}</span>
              <NavArrowRight width={18} height={18} strokeWidth={1.6} aria-hidden />
            </button>)}
          </div> : <EmptyState title="暂无符合条件的研判档案" text="完成一次 AI 研判后，结果和权限快照会自动保存在这里。" />}
        </section>}
        {result && <section className="ai-result-panel">
          <header><div><p className="eyebrow">{result.archived ? "AI REVIEW ARCHIVE" : "AI REVIEW RESULT"} · {result.intentLabel ?? result.visualization?.intentLabel ?? "智能研判"}</p><h2>{result.headline ?? (result.archived ? "研判档案" : "研判结果")}</h2>{result.prompt && <p className="ai-result-prompt">“{result.prompt}”</p>}</div><div className="ai-result-meta"><span className={`ai-service-status ${result.status}`}>{result.serviceConfigured ? result.model : "本地降级模式"} · {result.status === "ok" ? "已完成" : result.status === "unavailable" ? "未配置服务" : "调用失败"}</span>{result.createdAt && <small>{formatDateTime(result.createdAt)}{result.turn ? ` · 第 ${result.turn} 轮` : ""}</small>}</div></header>
          <div className="ai-scope-disclosure"><ShieldCheck width={18} height={18} strokeWidth={1.6} aria-hidden /><div><strong>本次实际分析范围</strong><span>客户 {result.scope.customers.used}/{result.scope.customers.available} · 通路 {result.scope.routes.used}/{result.scope.routes.available} · 申报 {result.scope.applications.used}/{result.scope.applications.available}</span><small>{result.scope.disclosure}</small></div></div>
          <article className="ai-direct-answer">
            <span className="ai-answer-mark"><Spark width={20} height={20} strokeWidth={1.5} aria-hidden /></span>
            <div>{result.answer.split(/\n+/u).filter(Boolean).map((paragraph, index) => <p key={`${index}-${paragraph.slice(0, 12)}`}>{paragraph}</p>)}</div>
          </article>
          {!!result.keyPoints?.length && <section className="ai-key-points">
            <header><span>本轮关键判断</span><small>{result.keyPoints.length} 项</small></header>
            <div>{result.keyPoints.map((item, index) => <article key={item}><span>{String(index + 1).padStart(2, "0")}</span><p>{item}</p></article>)}</div>
          </section>}
          {!!result.recommendations?.length && <section className="ai-recommendations">
            <header><span>建议怎么做</span><small>由你确认后再进入业务操作</small></header>
            <div>{result.recommendations.map((item, index) => <article className={item.priority} key={`${item.title}-${index}`}><span>{item.priority === "high" ? "优先" : item.priority === "low" ? "后续" : "建议"}</span><div><strong>{item.title}</strong><p>{item.reason}</p></div></article>)}</div>
          </section>}
          {result.visualization ? <>
            {showMetrics && <section className="ai-visual-metrics" aria-label="AI 研判关键指标">
              {result.visualization.metrics.map((metric) => <article key={metric.key} className={`ai-visual-metric ${metric.tone}`}>
                <span><AiMetricIcon metricKey={metric.key} /></span>
                <strong>{metric.value}</strong>
                <small>{metric.label}</small>
              </article>)}
            </section>}
            {visualMode === "customer_insight" && <section className="ai-customer-findings">
              <header><div><p className="eyebrow">MATCHED CUSTOMERS</p><h3>与问题相关的客户</h3></div><span>{result.visualization.customerItems?.length ?? 0} 项</span></header>
              {result.visualization.customerItems?.length ? result.visualization.customerItems.map((item) => <button type="button" key={item.id} onClick={() => navigate("customers")}><span><strong>{item.title}</strong><small>{item.detail}</small></span><em>{item.status}</em><NavArrowRight width={18} height={18} strokeWidth={1.6} aria-hidden /></button>) : <div className="ai-visual-empty"><PageSearch {...iconProps} /><strong>没有明确命中客户</strong><span>请补充客户全称、简称或中间关键词。</span></div>}
            </section>}
            {visualMode === "channel_matching" && result.visualization.matching && <section className="ai-matching-summary">
              <article><small>待研判渠道文本</small><strong>{result.visualization.matching.pendingReferences}</strong><p>尚未绑定平台渠道的历史来源文本</p></article>
              <article><small>待确认历史关联</small><strong>{result.visualization.matching.pendingAssociationReviews}</strong><p>需要管理员逐项确认后才会建立关系</p></article>
              <button type="button" className="button secondary" onClick={() => navigate("channels")}>进入渠道资源库<NavArrowRight {...iconProps} /></button>
            </section>}
            {(showPlanHealth || showAttention) && <section className={`ai-visual-board ${!showPlanHealth || !showAttention ? "single" : ""}`}>
              {showPlanHealth && <article className="ai-status-chart">
                <header><div><p className="eyebrow">PLAN HEALTH</p><h3>计划健康度</h3></div><span>{result.visualization.planTotal} 条通路</span></header>
                {result.visualization.planStatuses.length ? <div className="ai-status-bars" role="img" aria-label="权限内通路计划状态分布">
                  {result.visualization.planStatuses.map((item) => <div className={`ai-status-bar ${item.tone}`} key={item.label}>
                    <div><span>{item.label}</span><strong>{item.value}</strong></div>
                    <i><b style={{ width: `${Math.max(7, (item.value / maxStatusValue) * 100)}%` }} /></i>
                  </div>)}
                </div> : <div className="ai-visual-empty"><CheckCircle {...iconProps} /><strong>暂无计划数据</strong><span>当前权限范围内没有可汇总的通路计划。</span></div>}
                {result.visualization.planTotal > 0 && <div className="ai-risk-summary">
                  <span style={{ background: `conic-gradient(var(--danger) ${riskPlanPercent}%, color-mix(in srgb, var(--line) 64%, transparent) 0)` }}><i>{riskPlanPercent}%</i></span>
                  <div><small>需要关注的计划占比</small><strong>{riskPlanCount} / {result.visualization.planTotal}</strong><p>包含临近、逾期和推进受阻计划；点击右侧队列可进入业务页面继续处理。</p></div>
                </div>}
              </article>}
              {showAttention && <article className="ai-attention-board">
                <header><div><p className="eyebrow">PRIORITY QUEUE</p><h3>优先处理队列</h3></div><span>按风险排序 · 最多展示 8 项</span></header>
                {result.visualization.attentionItems.length ? <div className="ai-attention-list">
                  {result.visualization.attentionItems.map((item, index) => <button type="button" className={`ai-attention-item ${item.tone}`} key={`${item.kind}-${item.id}`} onClick={() => navigate(item.kind === "application" ? "approvals" : "plans")}>
                    <span className="ai-attention-index">{String(index + 1).padStart(2, "0")}</span>
                    <span className="ai-attention-copy"><small>{item.eyebrow}</small><strong>{item.title}</strong><em>{item.detail}</em></span>
                    <span className="ai-attention-meta"><strong>{item.status}</strong><small>{item.date ? (item.date.includes("T") ? formatDateTime(item.date) : formatDate(item.date)) : "待安排"}</small></span>
                    <NavArrowRight width={18} height={18} strokeWidth={1.6} aria-hidden />
                  </button>)}
                </div> : <div className="ai-visual-empty"><CheckCircle {...iconProps} /><strong>当前没有高优先级事项</strong><span>权限范围内未发现待审批、临期、逾期或受阻事项。</span></div>}
              </article>}
            </section>}
            {result.evidence.length > 0 && <details className="ai-evidence-details">
              <summary><span><ShieldCheck width={17} height={17} strokeWidth={1.6} aria-hidden />证据追溯</span><small>{result.evidence.length} 条依据</small></summary>
              <div className="ai-evidence">{result.evidence.map((item) => <span key={item}><CheckCircle width={15} height={15} strokeWidth={1.7} aria-hidden />{item}</span>)}</div>
            </details>}
          </> : <>
            <p className="ai-answer">{result.answer}</p>
            {result.evidence.length > 0 && <div className="ai-evidence"><strong>依据摘要</strong>{result.evidence.map((item) => <span key={item}><CheckCircle width={15} height={15} strokeWidth={1.7} aria-hidden />{item}</span>)}</div>}
          </>}
          {result.draft && <article className="ai-draft-preview">
            <div><span className="ai-source-tag">AI 草稿 · 待人工确认</span><h3>{result.draft.summary}</h3><p>{result.draft.payload.nextAction}</p></div>
            <dl><div><dt>计划类型</dt><dd>{result.draft.payload.mode === "queued" ? "后续计划" : "当前计划"}</dd></div><div><dt>阶段</dt><dd>{result.draft.payload.stage}</dd></div><div><dt>日期</dt><dd>{formatDate(result.draft.payload.followUpDate)}</dd></div></dl>
            <div className="ai-draft-actions"><span>确认前不会写入计划队列。</span><button className="button primary" disabled={confirming || result.draft.status !== "pending_confirmation"} onClick={confirmDraft}>{result.draft.status === "confirmed" ? "已确认执行" : confirming ? "正在确认…" : "确认并写入计划"}</button></div>
          </article>}
          {!!result.followUpQuestions?.length && !result.archived && <section className="ai-follow-ups">
            <div><strong>你还可以继续问</strong><small>点击后会带着当前对话继续研判</small></div>
            <div>{result.followUpQuestions.map((item) => <button type="button" key={item} onClick={() => { setPrompt(item); setWorkspaceView("ask"); window.requestAnimationFrame(() => document.querySelector<HTMLTextAreaElement>(".ai-prompt-panel textarea")?.focus()); }}>{item}<NavArrowRight width={16} height={16} strokeWidth={1.6} aria-hidden /></button>)}</div>
          </section>}
        </section>}
      </section>
    </>
  );
}

function CustomersPage({ state, user, refresh, notify }: { state: AppState; user: User; refresh: () => Promise<void>; notify: (tone: Notice["tone"], message: string) => void }) {
  const role = user.role;
  const [query, setQuery] = useState(() => new URLSearchParams(window.location.hash.split("?")[1] ?? "").get("q") ?? "");
  const [debouncedQuery, setDebouncedQuery] = useState(() => new URLSearchParams(window.location.hash.split("?")[1] ?? "").get("q") ?? "");
  const [routeFilter, setRouteFilter] = useState("全部");
  const [region, setRegion] = useState("all");
  const [sort, setSort] = useState("name");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(30);
  const [jumpPage, setJumpPage] = useState("1");
  const [loading, setLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [libraryRevision, setLibraryRevision] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [result, setResult] = useState<CustomerLibraryResult>({
    items: [],
    total: 0,
    page: 1,
    pageSize: 30,
    pageCount: 1,
    rangeStart: 0,
    rangeEnd: 0,
    facets: { regions: [] }
  });
  const [selectedId, setSelectedId] = useState<string | null>(() => new URLSearchParams(window.location.hash.split("?")[1] ?? "").get("customer"));
  const [newCustomer, setNewCustomer] = useState(false);
  const selected = result.items.find((customer) => customer.id === selectedId)
    ?? state.customers.find((customer) => customer.id === selectedId)
    ?? null;
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedQuery(query.trim());
      setPage(1);
    }, 220);
    return () => window.clearTimeout(timer);
  }, [query]);
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    const routeFilterValue = routeFilter === "技术口"
      ? "technical"
      : routeFilter === "采购口"
        ? "procurement"
        : routeFilter === "多渠道"
          ? "multi"
          : routeFilter === "无渠道跟进"
            ? "uncovered"
          : "all";
    const params = new URLSearchParams({
      q: debouncedQuery,
      region,
      routeFilter: routeFilterValue,
      sort,
      page: String(page),
      pageSize: String(pageSize)
    });
    api<CustomerLibraryResult>(`/api/customers/library?${params}`, { signal: controller.signal })
      .then((value) => {
        setResult(value);
        setJumpPage(String(value.page));
        if (value.page !== page) setPage(value.page);
      })
      .catch((error) => {
        if (!controller.signal.aborted) notify("error", error instanceof Error ? error.message : "客户库加载失败");
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setLoading(false);
          setHasLoaded(true);
        }
      });
    return () => controller.abort();
  }, [debouncedQuery, libraryRevision, notify, page, pageSize, region, routeFilter, sort]);
  const refreshLibrary = useCallback(async () => {
    await refresh();
    setLibraryRevision((value) => value + 1);
  }, [refresh]);
  const clearFilters = () => {
    setQuery("");
    setDebouncedQuery("");
    setRouteFilter("全部");
    setRegion("all");
    setSort("name");
    setPage(1);
  };
  const goToPage = () => {
    const target = Math.min(result.pageCount, Math.max(1, Number(jumpPage) || 1));
    setPage(target);
    setJumpPage(String(target));
  };
  const exportCurrentScope = async () => {
    const routeFilterValue = routeFilter === "技术口"
      ? "technical"
      : routeFilter === "采购口"
        ? "procurement"
        : routeFilter === "多渠道"
          ? "multi"
          : routeFilter === "无渠道跟进"
            ? "uncovered"
          : "all";
    const params = new URLSearchParams({
      q: debouncedQuery,
      region,
      routeFilter: routeFilterValue,
      sort
    });
    setExporting(true);
    try {
      await downloadFile(`/api/customers/library/export?${params}`, `客户库-${todayOffset(0)}.csv`);
      notify("success", `已导出当前筛选范围内的 ${result.total.toLocaleString("zh-CN")} 家客户。`);
    } catch (error) {
      notify("error", error instanceof Error ? error.message : "客户库导出失败");
    } finally {
      setExporting(false);
    }
  };
  const toggleFavorite = async (customer: Pick<Customer, "id" | "favorite">) => {
    try {
      await api(`/api/customers/${customer.id}/favorite`, { method: customer.favorite ? "DELETE" : "PUT" });
      setResult((current) => ({
        ...current,
        items: current.items
          .map((item) => item.id === customer.id ? { ...item, favorite: !customer.favorite } : item)
          .sort((left, right) => Number(Boolean(right.favorite)) - Number(Boolean(left.favorite)))
      }));
      notify("success", customer.favorite ? "已取消个人重点标记。" : "已加入我的重点客户。");
    } catch (error) {
      notify("error", error instanceof Error ? error.message : "重点客户标记失败");
    }
  };
  return (
    <>
      <PageHeader eyebrow={role === "manager" ? "CUSTOMER LIBRARY" : "MY CUSTOMERS"} title={role === "manager" ? "全部客户库" : "我的客户"} description={role === "manager" ? "从客户视角检查渠道关系、双通路覆盖和计划状态。" : "只展示本渠道已获批参与的客户与计划。"}>
        {role === "manager" && <button className="button primary" onClick={() => setNewCustomer(true)}><PlusCircle {...iconProps} />新增客户</button>}
      </PageHeader>
      <div className="toolbar customer-library-toolbar">
        <label className="search-field"><Search {...iconProps} /><span className="sr-only">搜索客户</span><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="客户全称、简称或任意中间关键词" />{query && <button type="button" aria-label="清除搜索" onClick={() => setQuery("")}><Xmark width={16} height={16} strokeWidth={1.7} aria-hidden /></button>}</label>
        <div className="segmented" role="group" aria-label="通路筛选">
          {(role === "manager" ? ["全部", "技术口", "采购口", "多渠道", "无渠道跟进"] : ["全部", "技术口", "采购口"]).map((item) => <button key={item} className={routeFilter === item ? "active" : ""} onClick={() => { setRouteFilter(item); setPage(1); }}>{item}</button>)}
        </div>
        <label className="compact-select"><span className="sr-only">区域筛选</span><select value={region} onChange={(event) => { setRegion(event.target.value); setPage(1); }}><option value="all">全部区域</option>{result.facets.regions.map((item) => <option key={item.value} value={item.value}>{item.value}（{item.count}）</option>)}</select></label>
        <label className="compact-select"><span className="sr-only">排序方式</span><select value={sort} onChange={(event) => { setSort(event.target.value); setPage(1); }}><option value="name">按名称排序</option><option value="recent">按最近更新</option></select></label>
        <button className="button ghost" disabled={exporting || loading || result.total === 0} onClick={exportCurrentScope}><Download {...iconProps} />{exporting ? "正在导出…" : "导出当前范围"}</button>
      </div>
      <section className="table-panel">
        <div className="table-meta customer-library-meta">
          <span>{!hasLoaded && loading ? "正在加载客户目录…" : <>共 <strong>{result.total.toLocaleString("zh-CN")}</strong> 个客户 · 当前显示 {result.rangeStart}–{result.rangeEnd}</>}</span>
          <span>{loading ? "正在检索…" : `第 ${result.page} / ${result.pageCount} 页`} · 数据范围：{role === "manager" ? "全部" : "本渠道获批"}</span>
          {(query || routeFilter !== "全部" || region !== "all" || sort !== "name") && <button type="button" className="button ghost" onClick={clearFilters}>清除筛选</button>}
        </div>
        <div className={`data-table customer-table ${role === "channel" ? "channel-customer-table" : ""} ${loading && hasLoaded ? "is-loading" : ""}`} role="table" aria-label="客户列表" aria-busy={loading}>
          <div className="table-row table-head" role="row"><span>客户</span><span>区域 / 行业</span><span>通路</span><span>{role === "manager" ? "渠道" : "负责销售"}</span><span>计划状态</span><span>更新</span><span /></div>
          {!hasLoaded && loading && Array.from({ length: 8 }, (_, index) => <div className="table-row customer-skeleton-row" role="row" aria-hidden key={`customer-skeleton-${index}`}><span /><span /><span /><span /><span /><span /><span /></div>)}
          {result.items.map((customer) => (
            <div className="table-row customer-result-row" role="row" key={customer.id} onClick={() => setSelectedId(customer.id)} onKeyDown={(event) => (event.key === "Enter" || event.key === " ") && setSelectedId(customer.id)} tabIndex={0}>
              <span className="customer-name-cell"><FavoriteButton customer={customer} onToggle={toggleFavorite} /><span><strong>{customer.name}</strong><small>{customer.shortName || "暂无简称"}</small></span></span>
              <span><strong>{customer.region}</strong><small>{customer.industry || "行业待补充"}</small></span>
              <span className="route-tags">{customer.technical && <RouteTag type="技术口" />}{customer.procurement && <RouteTag type="采购口" />}{!customer.technical && !customer.procurement && <small>暂无</small>}</span>
              {role === "manager"
                ? <span><strong>{customer.channelCount}</strong><small>个参与渠道</small></span>
                : <span className="responsible-sales-cell"><strong>{[...new Set((customer.responsibleSales ?? []).map((item) => item.downstreamFollowerName ? `${item.name} / ${item.downstreamFollowerName}` : item.name))].join("、") || "待分配"}</strong><small>{(customer.responsibleSales ?? []).length ? `${customer.responsibleSales?.length} 条通路 · 一级 / 下游负责人` : "由渠道负责人分配"}</small></span>}
              <span><Status value={customer.planStatus} /></span>
              <span>{formatDate(customer.updatedAt)}</span>
              <span><NavArrowRight {...iconProps} /></span>
            </div>
          ))}
        </div>
        {!loading && !result.items.length && <EmptyState title="没有匹配的客户" text="调整关键词或筛选条件后再试。" />}
        <nav className="customer-library-pagination" aria-label="全部客户库分页">
          <div>
            <button className="button ghost" disabled={result.page <= 1 || loading} onClick={() => setPage(1)}>首页</button>
            <button className="button ghost" disabled={result.page <= 1 || loading} onClick={() => setPage((value) => Math.max(1, value - 1))}><NavArrowLeft {...iconProps} />上一页</button>
            <button className="button ghost" disabled={result.page >= result.pageCount || loading} onClick={() => setPage((value) => Math.min(result.pageCount, value + 1))}>下一页<NavArrowRight {...iconProps} /></button>
            <button className="button ghost" disabled={result.page >= result.pageCount || loading} onClick={() => setPage(result.pageCount)}>末页</button>
          </div>
          <div className="page-jump">
            <label>每页<select value={pageSize} onChange={(event) => { setPageSize(Number(event.target.value)); setPage(1); }}><option value={20}>20</option><option value={30}>30</option><option value={50}>50</option><option value={100}>100</option></select>条</label>
            <label>跳至第<input type="number" min={1} max={result.pageCount} value={jumpPage} onChange={(event) => setJumpPage(event.target.value)} onKeyDown={(event) => event.key === "Enter" && goToPage()} />页</label>
            <button className="button secondary" type="button" onClick={goToPage}>跳转</button>
          </div>
        </nav>
      </section>
      {selected && <CustomerDrawer customer={selected} routes={state.routes.filter((route) => route.customer?.id === selected.id)} channels={state.channels ?? []} teamMembers={state.teamMembers ?? []} workNatures={state.workNatures ?? []} user={user} close={() => setSelectedId(null)} onRouteAdded={refreshLibrary} notify={notify} />}
      {newCustomer && <CustomerForm close={() => setNewCustomer(false)} onDone={async (message) => { await refreshLibrary(); setNewCustomer(false); notify("success", message); }} notify={notify} />}
    </>
  );
}

function CustomerDrawer({ customer, routes, channels, teamMembers, workNatures, user, close, onRouteAdded, notify, returnLabel }: { customer: Customer; routes: RouteItem[]; channels: Channel[]; teamMembers: TeamMember[]; workNatures: WorkNature[]; user: User; close: () => void; onRouteAdded: () => Promise<void>; notify: (tone: Notice["tone"], message: string) => void; returnLabel?: string }) {
  const role = user.role;
  const dialog = useRef<HTMLDivElement>(null);
  const [routeForm, setRouteForm] = useState<"技术口" | "采购口" | "全部" | null>(null);
  const [changeForm, setChangeForm] = useState(false);
  const [editingPlan, setEditingPlan] = useState<RouteItem | null>(null);
  const [queuedPlanForm, setQueuedPlanForm] = useState<RouteItem | null>(null);
  const [deletingPlan, setDeletingPlan] = useState<{ route: RouteItem; plan: Plan } | null>(null);
  const [recordingPlan, setRecordingPlan] = useState<{ route: RouteItem; plan: Plan } | null>(null);
  const [editingRecord, setEditingRecord] = useState<{ route: RouteItem; record: ProgressRecord } | null>(null);
  const [deletingRecord, setDeletingRecord] = useState<{ route: RouteItem; record: ProgressRecord } | null>(null);
  const [assigningDownstreamRoute, setAssigningDownstreamRoute] = useState<RouteItem | null>(null);
  const [unlinkingRelation, setUnlinkingRelation] = useState<RelationUnlinkTarget | null>(null);
  const [researchFallbackUrl, setResearchFallbackUrl] = useState("");
  useEffect(() => {
    dialog.current?.focus();
    const handler = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      if (routeForm) setRouteForm(null);
      else if (changeForm) setChangeForm(false);
      else if (unlinkingRelation) setUnlinkingRelation(null);
      else if (deletingPlan) setDeletingPlan(null);
      else if (deletingRecord) setDeletingRecord(null);
      else if (editingRecord) setEditingRecord(null);
      else if (recordingPlan) setRecordingPlan(null);
      else if (queuedPlanForm) setQueuedPlanForm(null);
      else if (editingPlan) setEditingPlan(null);
      else if (assigningDownstreamRoute) setAssigningDownstreamRoute(null);
      else close();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [assigningDownstreamRoute, changeForm, close, deletingPlan, deletingRecord, editingPlan, editingRecord, queuedPlanForm, recordingPlan, routeForm, unlinkingRelation]);
  const changePriority = async (routeId: string, priority: string) => {
    try {
      const result = await post<{ message: string }>(`/api/routes/${routeId}/priority`, { priority });
      await onRouteAdded();
      notify("success", result.message);
    } catch (error) {
      notify("error", error instanceof Error ? error.message : "优先级调整失败");
      throw error;
    }
  };
  const toggleFavorite = async () => {
    try {
      await api(`/api/customers/${customer.id}/favorite`, { method: customer.favorite ? "DELETE" : "PUT" });
      await onRouteAdded();
      notify("success", customer.favorite ? "已取消个人重点标记。" : "已加入我的重点客户。");
    } catch (error) {
      notify("error", error instanceof Error ? error.message : "重点客户标记失败");
    }
  };
  const assignOwner = async (routeId: string, ownerId: string) => {
    try {
      const response = await api<{ message: string }>(`/api/routes/${routeId}/owner`, {
        method: "PATCH",
        body: JSON.stringify({ ownerId })
      });
      await onRouteAdded();
      notify("success", response.message);
    } catch (error) {
      notify("error", error instanceof Error ? error.message : "负责销售分配失败");
      throw error;
    }
  };
  const openResearch = () => {
    const url = `${window.location.origin}${window.location.pathname}#/customer-research/${encodeURIComponent(customer.id)}`;
    const popup = window.open(url, "_blank");
    if (popup) {
      popup.opener = null;
      setResearchFallbackUrl("");
    } else {
      setResearchFallbackUrl(url);
      notify("error", "浏览器阻止了新窗口，请使用“在新标签页打开”。");
    }
  };
  const channelRouteGroups = useMemo(() => {
    const groups = new Map<string, { relationId: string; channel: RouteItem["channel"]; routes: RouteItem[] }>();
    routes.forEach((route) => {
      const key = route.relationId ?? `${route.channel?.id ?? "unassigned"}:${route.customer?.id ?? customer.id}`;
      const current = groups.get(key);
      if (current) current.routes.push(route);
      else groups.set(key, { relationId: route.relationId ?? "", channel: route.channel, routes: [route] });
    });
    return [...groups.values()].sort((left, right) => (left.channel?.name ?? "").localeCompare(right.channel?.name ?? "", "zh-CN"));
  }, [customer.id, routes]);
  const unlinkTargetFor = (group: (typeof channelRouteGroups)[number]): RelationUnlinkTarget => ({
    relationId: group.relationId,
    customerName: customer.name,
    channelName: group.channel?.name ?? "未分配渠道",
    routeCount: group.routes.length,
    routeTypes: [...new Set(group.routes.map((route) => route.type))],
    planCount: group.routes.reduce((total, route) => total + (route.planTimeline?.length ?? 0), 0),
    resultCount: group.routes.reduce((total, route) => total + (route.progressHistory?.length ?? 0), 0),
    downstreamCount: group.routes.filter((route) => route.downstreamChannel).length
  });
  return (
    <>
      <div className="drawer-layer" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && close()}>
        <div className="drawer" role="dialog" aria-modal="true" aria-labelledby="customer-title" tabIndex={-1} ref={dialog}>
          <div className="drawer-head customer-drawer-head">
            <div><p className="eyebrow">CUSTOMER ROUTES</p><div className="drawer-customer-title"><h2 id="customer-title">{customer.name}</h2><FavoriteButton customer={customer} onToggle={toggleFavorite} /></div><p>{customer.region} · {customer.industry || "行业待补充"} · {role === "manager" ? `${customer.channelCount} 个参与渠道` : `${routes.length} 条本渠道通路`}</p></div>
            <div className="drawer-head-actions">
              {returnLabel && <button className="button secondary" onClick={close}><NavArrowLeft {...iconProps} />{returnLabel}</button>}
              <button className="button secondary" onClick={openResearch}><Spark {...iconProps} />AI 调研</button>
              {role === "channel" && <button className="button secondary" onClick={() => setChangeForm(true)}><EditPencil {...iconProps} />申请更新客户信息</button>}
              {role === "manager" && <button className="button secondary" onClick={() => setChangeForm(true)}><EditPencil {...iconProps} />编辑客户资料</button>}
              {role === "manager" && <button className="button primary" onClick={() => setRouteForm("全部")}><PlusCircle {...iconProps} />添加渠道通路</button>}
              <button className="icon-button" aria-label="关闭详情" onClick={close}><Xmark {...iconProps} /></button>
            </div>
            {researchFallbackUrl && <a className="research-fallback-link" href={researchFallbackUrl} target="_blank" rel="noreferrer">在新标签页打开 AI 调研<ArrowRight {...iconProps} /></a>}
          </div>
          <section className="customer-facts-card" aria-label="客户基础信息">
            <div><span>客户简称</span><strong>{customer.shortName || "未填写"}</strong></div>
            <div><span>所属区域</span><strong>{customer.region}</strong></div>
            <div><span>所属行业</span><strong>{customer.industry || "未填写"}</strong></div>
            <div><span>最近更新</span><strong>{formatDate(customer.updatedAt)}</strong></div>
          </section>
          <section className="customer-contact-card">
            <div><span>客户联系人</span><strong>{customer.contactName || "未填写"}</strong></div>
            <div><span>联系方式</span><strong>{customer.contactInfo || "未填写"}</strong></div>
          </section>
          <section className="customer-route-directory">
            <header>
              <div><p className="eyebrow">CHANNEL ROUTES</p><h3>客户渠道通路</h3><span>{channelRouteGroups.length} 个渠道 · {routes.length} 条通路</span></div>
              {role === "manager" && <div className="route-section-actions"><button className="button secondary" onClick={() => setRouteForm("技术口")}><PlusCircle width={16} height={16} strokeWidth={1.7} aria-hidden />添加技术口</button><button className="button secondary" onClick={() => setRouteForm("采购口")}><PlusCircle width={16} height={16} strokeWidth={1.7} aria-hidden />添加采购口</button></div>}
            </header>
            {channelRouteGroups.length ? <div className="customer-channel-groups">{channelRouteGroups.map((group) => <section className="customer-channel-group" key={group.relationId || group.channel?.id || "unassigned"}><header><div><strong>{group.channel?.name ?? "未分配渠道"}</strong><small>{group.channel?.shortName ?? "—"} · {group.routes.length} 条通路</small></div>{group.relationId && group.routes.some((route) => route.canUnlinkRelation) && <button type="button" className="relation-unlink-button" aria-label={`取消${group.channel?.name ?? "该渠道"}与${customer.name}的关联`} title="取消客户渠道关联" onClick={() => setUnlinkingRelation(unlinkTargetFor(group))}><Xmark width={17} height={17} strokeWidth={2} aria-hidden /><span>取消关联</span></button>}</header><div>{group.routes.map((route) => <CustomerRouteCard key={route.id} route={route} role={role} user={user} teamMembers={teamMembers} assignOwner={assignOwner} changePriority={changePriority} manageDownstream={route.canAssignDownstreamChannel ? () => setAssigningDownstreamRoute(route) : undefined} editPlan={route.canOperate ? () => setEditingPlan(route) : undefined} addQueuedPlan={route.canOperate ? () => setQueuedPlanForm(route) : undefined} deletePlan={(plan) => setDeletingPlan({ route, plan })} recordResult={(plan) => setRecordingPlan({ route, plan })} editRecord={(record) => setEditingRecord({ route, record })} deleteRecord={(record) => setDeletingRecord({ route, record })} />)}</div></section>)}</div> : <EmptyState compact title="暂无渠道通路" text={role === "manager" ? "添加技术口或采购口后，计划与工作结果将按渠道连续展示。" : "当前未获批该客户通路。"} />}
          </section>
        </div>
      </div>
      {routeForm && <ChannelRouteForm customer={customer} channels={channels} routes={routes} initialType={routeForm} close={() => setRouteForm(null)} notify={notify} onDone={async (message) => { await onRouteAdded(); setRouteForm(null); notify("success", message); }} />}
      {changeForm && <CustomerUpdateRequestForm customer={customer} user={user} direct={role === "manager"} close={() => setChangeForm(false)} notify={notify} onDone={async (message) => { await onRouteAdded(); setChangeForm(false); notify("success", message); }} />}
      {editingPlan && <PlanEditForm route={editingPlan} user={user} teamMembers={teamMembers} workNatures={workNatures} close={() => setEditingPlan(null)} notify={notify} onDone={async (message) => { await onRouteAdded(); setEditingPlan(null); notify("success", message); }} />}
      {queuedPlanForm && <PlanEditForm route={queuedPlanForm} mode="queued" user={user} teamMembers={teamMembers} workNatures={workNatures} close={() => setQueuedPlanForm(null)} notify={notify} onDone={async (message) => { await onRouteAdded(); setQueuedPlanForm(null); notify("success", message); }} />}
      {deletingPlan && <DeletePlanConfirm route={deletingPlan.route} plan={deletingPlan.plan} close={() => setDeletingPlan(null)} notify={notify} onDone={async (message) => { await onRouteAdded(); setDeletingPlan(null); notify("success", message); }} />}
      {recordingPlan && <ProgressForm route={recordingPlan.route} plan={recordingPlan.plan} close={() => setRecordingPlan(null)} notify={notify} onDone={async (message) => { await onRouteAdded(); setRecordingPlan(null); notify("success", message); }} />}
      {editingRecord && <EditProgressRecordForm route={editingRecord.route} record={editingRecord.record} close={() => setEditingRecord(null)} notify={notify} onDone={async (message) => { await onRouteAdded(); setEditingRecord(null); notify("success", message); }} />}
      {deletingRecord && <DeleteProgressRecordConfirm route={deletingRecord.route} record={deletingRecord.record} close={() => setDeletingRecord(null)} notify={notify} onDone={async (message) => { await onRouteAdded(); setDeletingRecord(null); notify("success", message); }} />}
      {assigningDownstreamRoute && <RouteDownstreamAssignmentModal route={assigningDownstreamRoute} close={() => setAssigningDownstreamRoute(null)} notify={notify} onDone={async (message) => { await onRouteAdded(); setAssigningDownstreamRoute(null); notify("success", message); }} />}
      {unlinkingRelation && <UnlinkRelationConfirm target={unlinkingRelation} close={() => setUnlinkingRelation(null)} notify={notify} onDone={async (message) => { await onRouteAdded(); setUnlinkingRelation(null); notify("success", message); }} />}
    </>
  );
}

function RouteDownstreamAssignmentModal({ route, close, onDone, notify }: { route: RouteItem; close: () => void; onDone: (message: string) => void; notify: (tone: Notice["tone"], message: string) => void }) {
  const [items, setItems] = useState<DownstreamChannel[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [downstreamId, setDownstreamId] = useState(route.downstreamChannel?.id ?? "");
  const [followerId, setFollowerId] = useState(route.downstreamFollower?.id ?? "");
  useEffect(() => {
    api<{ items: DownstreamChannel[] }>("/api/downstream-channels")
      .then((response) => setItems(response.items))
      .catch((error) => notify("error", error instanceof Error ? error.message : "下游渠道加载失败"))
      .finally(() => setLoading(false));
  }, [notify]);
  const selected = items.find((item) => item.id === downstreamId) ?? null;
  const contacts = selected ? [selected.responsibleContact, ...selected.salesContacts].filter((contact) => contact.active !== false && contact.name && contact.phone) : [];
  const save = async () => {
    if (!downstreamId || !followerId) return notify("error", "请选择下游渠道和有效跟进人");
    setSubmitting(true);
    try {
      const result = await api<{ message: string }>(`/api/routes/${route.id}/downstream-assignment`, {
        method: "PUT",
        body: JSON.stringify({ downstreamChannelId: downstreamId, followerContactId: followerId, version: route.downstreamAssignmentVersion ?? 0 })
      });
      onDone(result.message);
    } catch (error) {
      notify("error", error instanceof Error ? error.message : "下游渠道关联失败");
      setSubmitting(false);
    }
  };
  const remove = async () => {
    if (!window.confirm("确认解除该通路的下游渠道关联？计划、结果和审计记录不会删除，通路将恢复显示为一级渠道直属推进。")) return;
    setSubmitting(true);
    try {
      const result = await api<{ message: string }>(`/api/routes/${route.id}/downstream-assignment`, {
        method: "DELETE",
        body: JSON.stringify({ version: route.downstreamAssignmentVersion ?? 0 })
      });
      onDone(result.message);
    } catch (error) {
      notify("error", error instanceof Error ? error.message : "解除关联失败");
      setSubmitting(false);
    }
  };
  return <Modal title="维护下游执行链" description={`${route.customer?.name} · ${route.type} · ${route.channel?.name ?? "所属渠道"}`} close={close}>
    <section className="downstream-assignment-summary"><span>当前执行方式</span><strong>{route.downstreamChannel ? `下游渠道跟进 · ${route.downstreamChannel.companyName}` : "直属跟进"}</strong><small>关联只改变该条通路的执行链展示，不创建新渠道关系，也不合并计划和工作结果。</small></section>
    {loading ? <div className="inline-loading">正在加载下游渠道资源池…</div> : <div className="form-grid downstream-assignment-form">
      <label className="field full"><span>下游渠道 *</span><select value={downstreamId} onChange={(event) => { setDownstreamId(event.target.value); setFollowerId(""); }}><option value="">请选择已启用且有维护权限的档案</option>{items.filter((item) => item.canEdit || item.id === route.downstreamChannel?.id).map((item) => <option key={item.id} value={item.id} disabled={!item.canEdit}>{item.companyName} · {item.assignedManager?.name ?? "待分配"}</option>)}</select></label>
      <label className="field full"><span>下游渠道跟进负责人 *</span><select value={followerId ?? ""} disabled={!selected} onChange={(event) => setFollowerId(event.target.value)}><option value="">请选择负责人或销售联系人</option>{contacts.map((contact) => <option key={contact.id} value={contact.id}>{contact.name} · {contact.phone}</option>)}</select><small>联系人姓名与电话仅在所属一级渠道的资源池和详情中展示。</small></label>
    </div>}
    {!loading && items.filter((item) => item.canEdit).length === 0 && <EmptyState compact title="暂无可关联档案" text="渠道销售只能使用分配给自己的下游档案；请由渠道负责人先完成建档和分配。" />}
    <div className="form-actions downstream-modal-actions">{route.downstreamChannel && route.canUnlinkDownstreamChannel && <button type="button" className="button danger-outline" disabled={submitting} onClick={remove}>解除当前关联</button>}<span /><button type="button" className="button ghost" onClick={close}>取消</button><button type="button" className="button primary" disabled={loading || submitting || !downstreamId || !followerId || !selected?.canEdit} onClick={save}>{submitting ? "正在保存…" : "保存通路关联"}</button></div>
  </Modal>;
}

function priorityClass(priority?: string | null) {
  return `priority-${String(priority ?? "P4").toLowerCase()}`;
}

function PriorityBadge({ value }: { value?: string | null }) {
  const priority = value ?? "P4";
  return <span className={`priority ${priorityClass(priority)}`}>{priority} 内部</span>;
}

type PlanWithResults = Plan & { results?: ProgressRecord[] };

function planStateOf(plan: Plan, route: RouteItem): "current" | "queued" | "completed" | "cancelled" {
  if (plan.state === "deleted") return "cancelled";
  if (plan.state) return plan.state;
  return plan.id === route.plan?.id ? "current" : "completed";
}

function PlanResultTimeline({ records, editRecord, deleteRecord, emptyText = "暂无工作结果" }: { records: ProgressRecord[]; editRecord?: (record: ProgressRecord) => void; deleteRecord?: (record: ProgressRecord) => void; emptyText?: string }) {
  if (!records.length) return <div className="plan-result-empty"><JournalPage width={18} height={18} strokeWidth={1.5} aria-hidden /><span>{emptyText}</span></div>;
  return (
    <div className="plan-result-timeline" role="list" aria-label="计划工作结果时间线">
      {records.map((record) => <section className="plan-result-record" role="listitem" key={record.id}>
        <i aria-hidden />
        <div>
          <header><div><strong>{formatDateTime(record.occurredAt)}</strong><span>{record.author} · {record.stage}{record.collaboration ? ` · 协同：${record.collaboration}` : ""}</span>{record.revisionCount ? <small>已编辑 {record.revisionCount} 次{record.updatedBy ? ` · ${record.updatedBy}` : ""}</small> : null}</div>{record.canEdit && (editRecord || deleteRecord) ? <div className="plan-result-actions">{editRecord && <button type="button" className="button ghost" onClick={() => editRecord(record)}><EditPencil width={15} height={15} strokeWidth={1.6} aria-hidden />编辑</button>}{deleteRecord && <button type="button" className="button ghost danger" onClick={() => deleteRecord(record)}><Trash width={15} height={15} strokeWidth={1.6} aria-hidden />删除</button>}</div> : null}</header>
          <div className="plan-result-actions-list">{record.actions.map((action) => <article key={action.id}><div><strong>{action.action}</strong><Status value={action.progressStatus} /></div><p>{action.result}</p></article>)}</div>
        </div>
      </section>)}
    </div>
  );
}

function PlanResultPreview({ records, editRecord, deleteRecord }: { records: ProgressRecord[]; editRecord?: (record: ProgressRecord) => void; deleteRecord?: (record: ProgressRecord) => void }) {
  const ordered = records.slice().sort((left, right) => String(right.occurredAt).localeCompare(String(left.occurredAt)));
  const [latest, ...earlier] = ordered;
  if (!latest) return <div className="customer-follow-up-result empty"><JournalPage width={17} height={17} strokeWidth={1.5} aria-hidden /><span>暂未记录工作结果</span></div>;
  return <section className="customer-follow-up-result" aria-label="工作结果">
    <header><div><span>最新工作结果</span><strong>{latest.completePlan ? "本次已完成计划" : "计划继续跟进"}</strong></div><small>{formatDateTime(latest.occurredAt)}</small></header>
    <PlanResultTimeline records={[latest]} editRecord={editRecord} deleteRecord={deleteRecord} />
    {earlier.length > 0 && <details className="customer-follow-up-earlier-results"><summary>其余 {earlier.length} 条结果<NavArrowRight width={16} height={16} strokeWidth={1.7} aria-hidden /></summary><PlanResultTimeline records={earlier} editRecord={editRecord} deleteRecord={deleteRecord} /></details>}
  </section>;
}

function currentPlanWithResults(route: RouteItem): PlanWithResults | null {
  const timeline = (route.planTimeline ?? []) as PlanWithResults[];
  const current = timeline.find((plan) => planStateOf(plan, route) === "current");
  if (current) return current;
  if (!route.plan) return null;
  return {
    ...route.plan,
    results: (route.progressHistory ?? []).filter((record) => record.planId === route.plan?.id)
  };
}

function planFollowUpTime(plan: Plan) {
  const parsed = Date.parse(`${plan.followUpDate}T00:00:00`);
  return Number.isFinite(parsed) ? parsed : Number.MAX_SAFE_INTEGER;
}

function CustomerFollowUpBoard({ routes, onRecordResult, onEditPlan, onDeletePlan, onEditRecord, onDeleteRecord, collapseByRouteType = false }: { routes: RouteItem[]; onRecordResult: (route: RouteItem, plan: Plan) => void; onEditPlan: (route: RouteItem) => void; onDeletePlan: (route: RouteItem, plan: Plan) => void; onEditRecord: (route: RouteItem, record: ProgressRecord) => void; onDeleteRecord: (route: RouteItem, record: ProgressRecord) => void; collapseByRouteType?: boolean }) {
  const [expanded, setExpanded] = useState(false);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const items = routes.flatMap((route) => {
    const plan = currentPlanWithResults(route);
    return plan ? [{ route, plan }] : [];
  }).sort((left, right) => {
    const leftTime = planFollowUpTime(left.plan);
    const rightTime = planFollowUpTime(right.plan);
    const dateDelta = Math.abs(leftTime - today.getTime()) - Math.abs(rightTime - today.getTime());
    if (dateDelta) return dateDelta;
    if (leftTime !== rightTime) return leftTime - rightTime;
    return `${left.route.channel?.name ?? ""}${left.route.type}`.localeCompare(`${right.route.channel?.name ?? ""}${right.route.type}`, "zh-CN");
  });
  const primaryItems = collapseByRouteType ? items.filter((item, index) => items.findIndex((candidate) => candidate.route.type === item.route.type) === index) : items;
  const visibleItems = collapseByRouteType && !expanded ? primaryItems : items;
  const hiddenCount = Math.max(0, items.length - primaryItems.length);

  return <section className="customer-follow-up-board" aria-label="客户正在跟进的工作计划">
    <header><div><p className="eyebrow">ACTIVE FOLLOW-UP</p><h3>正在跟进</h3></div><span>距今天最近的日期优先 · {items.length} 项</span></header>
    {items.length ? <><div className="customer-follow-up-list" role="list">
      {visibleItems.map(({ route, plan }) => {
        const tags = plan.workTags?.length ? plan.workTags : [{ id: "unclassified", name: "未分类", groupName: "未分组" }];
        const canOperatePlan = Boolean(plan.canOperate ?? route.canOperate);
        return <article className={`customer-follow-up-item ${route.type === "技术口" ? "technical" : "purchase"}`} role="listitem" key={`${route.id}-${plan.id}`}>
          <div className="customer-follow-up-date"><Calendar width={18} height={18} strokeWidth={1.6} aria-hidden /><time dateTime={plan.followUpDate}>{formatDate(plan.followUpDate)}</time><small>跟进日期</small></div>
          <div className="customer-follow-up-main">
            <header><div><RouteTag type={route.type} /><span>{route.channel?.name ?? "未分配渠道"}</span></div><Status value={plan.status} /></header>
            <div className="customer-follow-up-context">
              <div><small>计划阶段</small><strong>{plan.stage}</strong></div>
              <div><small>计划标签</small><span className="customer-follow-up-tags">{tags.map((tag) => <span key={tag.id}>{tag.groupName && tag.groupName !== "未分组" ? <small>{tag.groupName}</small> : null}{tag.name}</span>)}</span></div>
            </div>
            <div className="customer-follow-up-action"><small>当前计划</small><h4>{plan.nextAction}</h4></div>
            <PlanResultPreview records={plan.results ?? []} editRecord={(record) => onEditRecord(route, record)} deleteRecord={(record) => onDeleteRecord(route, record)} />
            <footer><span><UserCircle width={15} height={15} strokeWidth={1.6} aria-hidden />{plan.owner || "待分配"} · 已记录 {plan.results?.length ?? 0} 次结果</span>{canOperatePlan ? <div><button type="button" className="button primary" onClick={() => onRecordResult(route, plan)}><JournalPage width={16} height={16} strokeWidth={1.7} aria-hidden />记录工作结果</button><button type="button" className="button secondary" onClick={() => onEditPlan(route)}><EditPencil width={16} height={16} strokeWidth={1.7} aria-hidden />编辑计划</button><button type="button" className="button danger-outline" onClick={() => onDeletePlan(route, plan)}><Trash width={16} height={16} strokeWidth={1.7} aria-hidden />删除</button></div> : <small>当前为只读查看</small>}</footer>
          </div>
        </article>;
      })}
    </div>{collapseByRouteType && hiddenCount > 0 && <div className="customer-follow-up-disclosure"><button type="button" aria-expanded={expanded} onClick={() => setExpanded((value) => !value)}><span>{expanded ? "收起，每类仅显示最近 1 条" : `展开其余 ${hiddenCount} 条当前计划`}</span><NavArrowRight width={17} height={17} strokeWidth={1.7} aria-hidden /></button></div>}</> : <div className="customer-follow-up-empty"><JournalPage width={20} height={20} strokeWidth={1.5} aria-hidden /><span><strong>当前没有正在跟进的计划</strong><small>可在下方对应渠道通路中建立当前计划。</small></span></div>}
  </section>;
}

function RoutePlanChain({ route, onEditCurrentPlan, onAddQueuedPlan, onDeletePlan, onRecordResult, onEditRecord, onDeleteRecord, compact = false, hideCurrentWhenPresent = false }: { route: RouteItem; onEditCurrentPlan?: () => void; onAddQueuedPlan?: () => void; onDeletePlan?: (plan: Plan) => void; onRecordResult?: (plan: Plan) => void; onEditRecord?: (record: ProgressRecord) => void; onDeleteRecord?: (record: ProgressRecord) => void; compact?: boolean; hideCurrentWhenPresent?: boolean }) {
  const fallbackPlans: PlanWithResults[] = [
    ...(route.plan ? [{ ...route.plan, results: (route.progressHistory ?? []).filter((record) => record.planId === route.plan?.id) }] : []),
    ...((route.queuedPlans ?? []).map((plan) => ({ ...plan, results: (route.progressHistory ?? []).filter((record) => record.planId === plan.id) })))
  ];
  const plans = ((route.planTimeline?.length ? route.planTimeline : fallbackPlans) as PlanWithResults[])
    .filter((plan) => planStateOf(plan, route) !== "cancelled");
  const currentPlans = plans.filter((plan) => planStateOf(plan, route) === "current");
  const queuedPlans = plans.filter((plan) => planStateOf(plan, route) === "queued");
  const completedPlans = plans.filter((plan) => planStateOf(plan, route) === "completed");
  const unlinkedResults = route.unlinkedProgressHistory ?? (route.progressHistory ?? []).filter((record) => !record.planId);
  const renderPlan = (plan: PlanWithResults, index = 0) => {
    const state = planStateOf(plan, route);
    const canOperatePlan = Boolean(plan.canOperate ?? route.canOperate);
    const results = plan.results ?? [];
    const tags = plan.workTags?.length ? plan.workTags : [{ id: "unclassified", name: "未分类", groupName: "未分组" }];
    return <article className={`plan-chain-item is-${state}`} key={plan.id}>
      <header><div className="plan-chain-heading"><span className="plan-sequence">{state === "queued" ? String(index + 1).padStart(2, "0") : state === "current" ? "NOW" : "DONE"}</span><div><strong>{plan.nextAction}</strong><div className="plan-chain-tags">{tags.map((tag) => <span key={tag.id}>{tag.groupName && tag.groupName !== "未分组" ? <small>{tag.groupName}</small> : null}{tag.name}</span>)}</div></div></div><div className="plan-chain-status"><Status value={state === "queued" ? "等待推进" : state === "completed" ? "已完成" : plan.status} /><span>{results.reduce((sum, record) => sum + record.actions.length, 0)} 项结果</span></div></header>
      <div className="plan-chain-meta"><span><Calendar width={15} height={15} strokeWidth={1.6} aria-hidden />{formatDate(plan.followUpDate)}</span><span><UserCircle width={15} height={15} strokeWidth={1.6} aria-hidden />{plan.owner || "待分配"}</span><span className="plan-stage-chip"><ClipboardCheck width={15} height={15} strokeWidth={1.6} aria-hidden />计划阶段 · {plan.stage}</span>{plan.collaboration && <span><Community width={15} height={15} strokeWidth={1.6} aria-hidden />{plan.collaboration}</span>}</div>
      <PlanResultTimeline records={results} editRecord={onEditRecord} deleteRecord={onDeleteRecord} />
      <footer>
        {state === "current" && canOperatePlan && <>{onRecordResult && <button type="button" className="button primary" onClick={() => onRecordResult(plan)}><JournalPage width={16} height={16} strokeWidth={1.7} aria-hidden />填写工作结果</button>}{onEditCurrentPlan && <button type="button" className="button secondary" onClick={onEditCurrentPlan}><EditPencil width={16} height={16} strokeWidth={1.7} aria-hidden />编辑计划</button>}</>}
        {state === "queued" && <small>按跟进日期自动排序，成为当前计划后可编辑并填写结果</small>}
        {state === "completed" && <small>已完成计划保留结果与追溯记录</small>}
        {!canOperatePlan && state !== "completed" && <small>当前无操作权限</small>}
        {canOperatePlan && onDeletePlan && <button type="button" className="button danger-outline" onClick={() => onDeletePlan(plan)}><Trash width={16} height={16} strokeWidth={1.7} aria-hidden />删除计划</button>}
      </footer>
    </article>;
  };
  return <div className={`route-plan-chain ${compact ? "is-compact" : ""}`}>
    {(!hideCurrentWhenPresent || !currentPlans.length) && <section className="plan-chain-section current"><header><div><span>当前计划</span><strong>当前执行</strong></div><small>{currentPlans.length ? "记录阶段性结果，完成后再推进队列" : "尚未建立"}</small></header>{currentPlans.length ? currentPlans.map((plan) => renderPlan(plan)) : <div className="plan-chain-empty"><span>暂无当前计划</span>{route.canOperate && onEditCurrentPlan ? <button type="button" className="button secondary" onClick={onEditCurrentPlan}><PlusCircle width={16} height={16} strokeWidth={1.7} aria-hidden />建立当前计划</button> : <small>当前无操作权限</small>}</div>}</section>}
    <section className="plan-chain-section queued"><header><div><span>后续计划</span><strong>自动排序队列</strong></div><div className="plan-chain-section-actions"><small>{queuedPlans.length} 项</small>{currentPlans.length > 0 && route.canOperate && onAddQueuedPlan && <button type="button" className="button ghost" onClick={onAddQueuedPlan}><PlusCircle width={16} height={16} strokeWidth={1.7} aria-hidden />新建工作计划</button>}</div></header>{queuedPlans.length ? <div className="plan-chain-list">{queuedPlans.map((plan, index) => renderPlan(plan, index))}</div> : <div className="plan-chain-empty"><span>暂无后续计划</span></div>}</section>
    {completedPlans.length ? <details className="plan-chain-section completed completed-plan-archive"><summary onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); const details = event.currentTarget.parentElement as HTMLDetailsElement; details.open = !details.open; } }}><div><span>已完成计划</span><strong>历史追溯</strong></div><span className="completed-plan-archive-toggle"><small>{completedPlans.length} 项</small><span className="completed-plan-archive-collapsed">展开历史</span><span className="completed-plan-archive-expanded">收起历史</span><NavArrowRight width={17} height={17} strokeWidth={1.7} aria-hidden /></span></summary><div className="completed-plan-archive-body"><div className="plan-chain-list">{completedPlans.map((plan) => renderPlan(plan))}</div></div></details> : <section className="plan-chain-section completed"><header><div><span>已完成计划</span><strong>历史追溯</strong></div><small>0 项</small></header><div className="plan-chain-empty"><span>暂无已完成计划</span></div></section>}
    {unlinkedResults.length > 0 && <section className="unlinked-result-section"><header><WarningTriangle width={18} height={18} strokeWidth={1.7} aria-hidden /><div><strong>历史未关联结果</strong><small>旧记录缺少明确计划关联，未自动猜测归属。</small></div></header><PlanResultTimeline records={unlinkedResults} editRecord={onEditRecord} deleteRecord={onDeleteRecord} emptyText="暂无未关联结果" /></section>}
  </div>;
}

function CustomerRouteCard({ route, role, user, teamMembers, assignOwner, changePriority, manageDownstream, editPlan, addQueuedPlan, deletePlan, recordResult, editRecord, deleteRecord }: { route: RouteItem; role: User["role"]; user: User; teamMembers: TeamMember[]; assignOwner: (routeId: string, ownerId: string) => Promise<void>; changePriority: (routeId: string, priority: string) => Promise<void>; manageDownstream?: () => void; editPlan?: () => void; addQueuedPlan?: () => void; deletePlan: (plan: Plan) => void; recordResult: (plan: Plan) => void; editRecord: (record: ProgressRecord) => void; deleteRecord: (record: ProgressRecord) => void }) {
  const [changing, setChanging] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const updatePriority = async (priority: string) => {
    setChanging(true);
    try { await changePriority(route.id, priority); }
    finally { setChanging(false); }
  };
  const updateOwner = async (ownerId: string) => {
    setAssigning(true);
    try { await assignOwner(route.id, ownerId); }
    finally { setAssigning(false); }
  };
  return (
    <article className="route-card">
      <div className="route-card-head">
        <div className="route-card-identity"><RouteTag type={route.type} /><span className={`route-execution-mode ${route.downstreamChannel ? "downstream" : "direct"}`}>{route.downstreamChannel ? "下游渠道跟进" : "直属"}</span><strong>{route.channel?.name ?? "未知渠道"}</strong><span>· {route.stage}</span></div>
        <div className="route-card-head-actions">
        {role === "manager" && (
          <label className={`priority-control ${priorityClass(route.priority)}`}>
            <span className="sr-only">{route.channel?.name}内部优先级</span>
            <select aria-label={`${route.channel?.name}内部优先级`} value={route.priority ?? "P4"} disabled={changing} onChange={(event) => updatePriority(event.target.value)}>
              <option value="P1">P1 · 最高</option>
              <option value="P2">P2 · 较高</option>
              <option value="P3">P3 · 常规</option>
              <option value="P4">P4 · 观察</option>
            </select>
          </label>
        )}
        {!route.downstreamChannel && manageDownstream && <button type="button" className="button ghost" onClick={manageDownstream}>关联下游渠道</button>}
        </div>
      </div>
      <section className="route-owner-panel" aria-label="跟进负责人">
        <div><span>一级渠道跟进负责人</span><strong>{route.owner?.name ?? "待分配"}</strong>{route.owner && <small>{route.owner.title}{route.owner.contactInfo ? ` · ${route.owner.contactInfo}` : ""}</small>}</div>
        {role === "channel" && user.accountRole === "channel_owner" && <label><span className="sr-only">分配负责销售</span><select value={route.owner?.id ?? ""} disabled={assigning} onChange={(event) => updateOwner(event.target.value)}><option value="" disabled>选择负责销售</option>{teamMembers.filter((member) => member.active).map((member) => <option value={member.id} key={member.id}>{member.name} · {member.accountRole === "channel_owner" ? "负责人" : "渠道销售"}</option>)}</select></label>}
      </section>
      {route.downstreamChannel && <section className="route-downstream-strip is-linked" aria-label="下游渠道跟进信息">
        <div><span>下游渠道</span><strong>{route.downstreamChannel.companyName}</strong><small>下游跟进负责人：{route.downstreamFollower?.name ?? "待补充"}{route.downstreamFollower?.phone ? ` · ${route.downstreamFollower.phone}` : ""}</small></div>
        {manageDownstream && <button type="button" className="button ghost" onClick={manageDownstream}>调整下游关联</button>}
      </section>}
      <RoutePlanChain route={route} compact onEditCurrentPlan={editPlan} onAddQueuedPlan={addQueuedPlan} onDeletePlan={deletePlan} onRecordResult={recordResult} onEditRecord={editRecord} onDeleteRecord={deleteRecord} />
    </article>
  );
}

function ChannelRouteForm({ customer, channels, routes, initialType, close, onDone, notify }: { customer: Customer; channels: Channel[]; routes: RouteItem[]; initialType: "技术口" | "采购口" | "全部"; close: () => void; onDone: (message: string) => void; notify: (tone: Notice["tone"], message: string) => void }) {
  const [submitting, setSubmitting] = useState(false);
  const [channelId, setChannelId] = useState("");
  const [selectedTypes, setSelectedTypes] = useState<("技术口" | "采购口")[]>(initialType === "全部" ? ["技术口", "采购口"] : [initialType]);
  const [stages, setStages] = useState<Record<"技术口" | "采购口", string>>({ 技术口: "待建立联系", 采购口: "采购信息摸排" });
  const [priorities, setPriorities] = useState<Record<"技术口" | "采购口", string>>({ 技术口: "", 采购口: "" });
  const selectedChannel = channels.find((channel) => channel.id === channelId);
  const existingTypes = routes.filter((route) => route.channel?.id === channelId && route.active).map((route) => route.type);
  const stageOptions = {
    技术口: ["待建立联系", "需求摸排", "技术交流", "方案沟通", "测试验证", "商务协同", "暂停推进"],
    采购口: ["待建立联系", "采购信息摸排", "预算确认", "采购流程推进", "商务谈判", "合同推进", "暂停推进"]
  } as const;
  const toggleType = (type: "技术口" | "采购口") => setSelectedTypes((current) => current.includes(type) ? current.filter((item) => item !== type) : [...current, type]);
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!channelId) return notify("error", "请选择参与渠道");
    if (!selectedTypes.length) return notify("error", "请至少选择一个通路");
    setSubmitting(true);
    try {
      const result = await post<{ message: string }>(`/api/customers/${customer.id}/routes`, { channelId, routeTypes: selectedTypes, stages, priorities });
      onDone(result.message);
    } catch (error) {
      notify("error", error instanceof Error ? error.message : "添加通路失败");
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <Modal title="添加客户渠道通路" description={`为 ${customer.name} 选择参与渠道，并分别建立技术口或采购口。`} close={close}>
      <form className="form-grid route-config-form" onSubmit={submit}>
        <label className="field full"><span>参与渠道 *</span><select value={channelId} onChange={(event) => setChannelId(event.target.value)} required><option value="">请选择已启用渠道</option>{channels.filter((channel) => channel.active).map((channel) => <option key={channel.id} value={channel.id}>{channel.name}（{channel.shortName}）</option>)}</select></label>
        {selectedChannel && (
          <div className="existing-route-hint full">
            <strong>{selectedChannel.shortName} 当前通路</strong>
            <span>{existingTypes.length ? existingTypes.join("＋") : "尚未与该客户建立通路"}</span>
            {existingTypes.length > 0 && <small>重复选择的通路会自动跳过；新通路仍可正常建立。</small>}
          </div>
        )}
        <fieldset className="route-type-picker full">
          <legend>通路类型 *</legend>
          {(["技术口", "采购口"] as const).map((type) => <label className={selectedTypes.includes(type) ? "selected" : ""} key={type}><input type="checkbox" checked={selectedTypes.includes(type)} onChange={() => toggleType(type)} /><RouteTag type={type} /><span>{type === "技术口" ? "技术关系与方案推进" : "采购流程与商务推进"}</span></label>)}
        </fieldset>
        <div className="route-config-list full">
          {selectedTypes.map((type) => (
            <section className={type === "技术口" ? "technical" : "purchase"} key={type}>
              <header><RouteTag type={type} /><strong>{type}初始设置</strong></header>
              <div className="route-option-grid">
                <label className="field"><span>当前阶段</span><select value={stages[type]} onChange={(event) => setStages((current) => ({ ...current, [type]: event.target.value }))}>{stageOptions[type].map((stage) => <option key={stage}>{stage}</option>)}</select></label>
                <label className="field"><span>内部优先级</span><select value={priorities[type] || "P4"} onChange={(event) => setPriorities((current) => ({ ...current, [type]: event.target.value }))}><option value="P1">P1 · 最高优先</option><option value="P2">P2 · 较高优先</option><option value="P3">P3 · 常规推进</option><option value="P4">P4 · 观察储备</option></select></label>
              </div>
            </section>
          ))}
        </div>
        <div className="form-note full"><Clock width={18} height={18} strokeWidth={1.6} aria-hidden /><span><strong>建立后状态为“跟进中”</strong><small>首次计划可在“计划督办”中补充；内部优先级仅管理组可见。</small></span></div>
        <div className="form-actions full"><button type="button" className="button ghost" onClick={close}>取消</button><button className="button primary" disabled={submitting || !channelId || !selectedTypes.length}>{submitting ? "正在建立…" : "确认建立通路"}</button></div>
      </form>
    </Modal>
  );
}

function CustomerForm({ close, onDone, notify }: { close: () => void; onDone: (message: string) => void; notify: (tone: Notice["tone"], message: string) => void }) {
  const [submitting, setSubmitting] = useState(false);
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    const form = new FormData(event.currentTarget);
    try {
      const result = await post<{ duplicate: unknown[] }>("/api/customers", Object.fromEntries(form));
      onDone(result.duplicate.length ? "客户已新增，并保留了疑似重复提示。" : "客户已新增。");
    } catch (error) {
      notify("error", error instanceof Error ? error.message : "保存失败");
    } finally { setSubmitting(false); }
  };
  return <Modal title="新增客户" description="客户全称和所属区域为必填项；区域支持按实际业务口径自由填写。" close={close}><form className="form-grid" onSubmit={submit}><Field label="客户全称" name="name" required placeholder="请输入完整企业名称" /><Field label="客户简称" name="shortName" placeholder="选填" /><Field label="所属区域" name="region" required placeholder="如：四川-成都、成都高新区或华西区域" /><Field label="所属行业" name="industry" placeholder="选填" /><Field label="客户联系人" name="contactName" placeholder="选填，如：王老师" /><Field label="联系方式" name="contactInfo" placeholder="选填，手机、座机或邮箱" /><label className="field full"><span>备注</span><textarea name="note" rows={3} placeholder="选填，不填写管理组内部敏感信息" /></label><div className="form-actions full"><button type="button" className="button ghost" onClick={close}>取消</button><button className="button primary" disabled={submitting}>{submitting ? "正在保存…" : "确认新增"}</button></div></form></Modal>;
}

function CustomerUpdateRequestForm({ customer, user, direct = false, application, close, onDone, notify }: { customer: Customer; user: User; direct?: boolean; application?: Application; close: () => void; onDone: (message: string) => void; notify: (tone: Notice["tone"], message: string) => void }) {
  const [submitting, setSubmitting] = useState(false);
  const proposed = application?.customerChange?.proposed;
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const values = Object.fromEntries(formData);
    setSubmitting(true);
    try {
      const payload = {
        reason: values.reason,
        customer: {
          name: values.name,
          shortName: values.shortName,
          region: values.region,
          industry: values.industry,
          contactName: values.contactName,
          contactInfo: values.contactInfo,
          ...(direct ? { note: values.note } : {})
        }
      };
      const response = await post<{ message: string }>(
        direct
          ? `/api/customers/${customer.id}/update`
          : application
            ? `/api/applications/${application.id}/resubmit`
            : `/api/customers/${customer.id}/change-request`,
        payload
      );
      onDone(response.message);
    } catch (error) {
      notify("error", error instanceof Error ? error.message : direct ? "客户资料保存失败" : "客户资料变更申请提交失败");
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <Modal
      title={direct ? "编辑客户资料" : application ? "补充客户资料变更" : "申请更新客户信息"}
      description={direct
        ? `${customer.name} · 总管理员保存后立即更新客户主档。`
        : user.accountRole === "channel_owner"
          ? `${customer.name} · 渠道负责人提交后由总管理员审批。`
          : `${customer.name} · 渠道销售提交后先由渠道负责人审批。`}
      close={close}
    >
      <form className="form-grid" onSubmit={submit}>
        {application ? <label className="field"><span>客户全称（锁定）</span><input name="name" readOnly value={customer.name} /><small>补充重提不得修改客户名称或客户ID。</small></label> : <Field label="客户全称" name="name" required defaultValue={proposed?.name ?? customer.name} />}
        <Field label="客户简称" name="shortName" defaultValue={proposed?.shortName ?? customer.shortName} placeholder="选填" />
        <Field label="所属区域" name="region" required defaultValue={proposed?.region ?? customer.region} placeholder="按实际业务口径填写" />
        <Field label="所属行业" name="industry" defaultValue={proposed?.industry ?? customer.industry} placeholder="选填" />
        <Field label="客户联系人" name="contactName" defaultValue={proposed?.contactName ?? customer.contactName} placeholder="选填" />
        <Field label="联系方式" name="contactInfo" defaultValue={proposed?.contactInfo ?? customer.contactInfo} placeholder="选填" />
        {direct && <label className="field full"><span>管理备注</span><textarea name="note" rows={3} defaultValue={customer.note} placeholder="仅管理组可见，选填" /></label>}
        {!direct && <label className="field full"><span>变更原因或依据 *</span><textarea name="reason" required rows={3} defaultValue={application?.situation} placeholder="说明信息来源、需要修正的内容或本次核实结果" /></label>}
        <div className="form-flow-note full"><ShieldCheck {...iconProps} /><span>{direct ? "保存后立即生效。" : user.accountRole === "channel_owner" ? "总管理员审批通过后更新客户主档；原值、拟更新值和审批记录会完整保留。" : "渠道负责人审批通过后更新客户主档；原值、拟更新值和审批记录会完整保留。"}</span></div>
        <div className="form-actions full"><button type="button" className="button ghost" onClick={close}>取消</button><button className="button primary" disabled={submitting}>{submitting ? "正在提交…" : direct ? "保存客户资料" : application ? (user.accountRole === "channel_owner" ? "重新提交总管理员" : "重新提交负责人") : "提交变更申请"}</button></div>
      </form>
    </Modal>
  );
}

function WorkTagField({ workNatures, defaultValues = [] }: { workNatures: WorkNature[]; defaultValues?: string[] }) {
  const [selectedIds, setSelectedIds] = useState<string[]>(defaultValues);
  const selected = new Set(selectedIds);
  const available = workNatures.filter((item) => item.active || selected.has(item.id));
  const selectedTags = available.filter((item) => selected.has(item.id));
  const toggleTag = (id: string) => setSelectedIds((current) => current.includes(id) ? current.filter((item) => item !== id) : [...current, id]);
  return (
    <div className="field work-tag-field full">
      <span>工作标签</span>
      <details className="work-tag-select">
        <summary><span>{selectedTags.length ? `已选择 ${selectedTags.length} 项` : "请选择工作标签"}</span><NavArrowRight width={17} height={17} strokeWidth={1.7} aria-hidden /></summary>
        <div className="work-tag-menu">
          {available.map((item) => (
            <label key={item.id}>
              <input type="checkbox" name="workTagIds" value={item.id} checked={selected.has(item.id)} onChange={() => toggleTag(item.id)} />
              <span>{item.groupName && item.groupName !== "未分组" ? <small>{item.groupName}</small> : null}{item.name}</span>
            </label>
          ))}
          {!available.length && <span className="muted-copy">暂无可用标签</span>}
        </div>
      </details>
      {selectedTags.length > 0 && <div className="work-tag-selection">{selectedTags.map((item) => <button type="button" key={item.id} onClick={() => toggleTag(item.id)}>{item.name}<Xmark width={13} height={13} strokeWidth={1.8} aria-hidden /></button>)}</div>}
    </div>
  );
}

function PlanEditForm({ route, plan, mode = "current", user, teamMembers, workNatures, close, onDone, notify }: { route: RouteItem; plan?: Plan | null; mode?: "current" | "queued"; user: User; teamMembers: TeamMember[]; workNatures: WorkNature[]; close: () => void; onDone: (message: string) => void; notify: (tone: Notice["tone"], message: string) => void }) {
  const [submitting, setSubmitting] = useState(false);
  const editingPlan = plan ?? (mode === "current" ? route.plan : null);
  const activeTeam = teamMembers.filter((member) => member.active);
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const values = Object.fromEntries(formData);
    setSubmitting(true);
    try {
      const payload = {
        mode,
        stage: values.stage,
        nextAction: values.nextAction,
        followUpDate: values.followUpDate,
        collaboration: values.collaboration,
        ownerId: values.ownerId,
        workTagIds: formData.getAll("workTagIds").map(String)
      };
      const response = editingPlan
        ? await api<{ message: string }>(`/api/plans/${editingPlan.id}`, { method: "PATCH", body: JSON.stringify(payload) })
        : await post<{ message: string }>(`/api/routes/${route.id}/plans`, payload);
      onDone(response.message);
    } catch (error) {
      notify("error", error instanceof Error ? error.message : "计划保存失败");
    } finally {
      setSubmitting(false);
    }
  };
  const title = mode === "queued"
    ? "添加下一步计划"
    : editingPlan ? "编辑当前计划" : "建立当前计划";
  return (
    <Modal title={title} description={`${route.customer?.name} · ${route.type} · ${route.channel?.shortName}`} close={close}>
      <form className="form-grid" onSubmit={submit}>
        <label className="field"><span>计划阶段</span><select name="stage" defaultValue={editingPlan?.stage ?? route.stage}><option>待建立联系</option><option>已建立联系</option><option>需求摸排</option><option>技术交流</option><option>采购信息摸排</option><option>方案沟通</option><option>测试验证</option><option>项目推进</option><option>暂停推进</option></select></label>
        <label className="field"><span>计划负责人</span>{user.accountRole === "channel_owner" ? <select name="ownerId" defaultValue={editingPlan?.ownerId ?? route.plan?.ownerId ?? user.id}>{activeTeam.map((member) => <option key={member.id} value={member.id}>{member.name} · {member.accountRole === "channel_owner" ? "负责人" : "渠道销售"}</option>)}</select> : <><input readOnly value={user.role === "manager" ? (editingPlan?.owner ?? route.plan?.owner ?? "沿用通路负责人") : `${user.name} · 渠道销售`} /><input type="hidden" name="ownerId" value={user.role === "manager" ? editingPlan?.ownerId ?? route.plan?.ownerId ?? "" : user.id} /></>}</label>
        <WorkTagField workNatures={workNatures} defaultValues={editingPlan?.workTagIds ?? (editingPlan?.workNatureId ? [editingPlan.workNatureId] : [])} />
        <label className="field full"><span>计划动作 *</span><textarea name="nextAction" required rows={3} defaultValue={editingPlan?.nextAction} placeholder="填写下一项明确、可执行的工作" /></label>
        <Field label="跟进日期" name="followUpDate" type="date" required defaultValue={editingPlan?.followUpDate ?? todayOffset(5)} />
        <label className="field"><span>需要协同</span><select name="collaboration" defaultValue={editingPlan?.collaboration ?? ""}><option value="">暂不需要</option><option>联合拜访</option><option>技术支持</option><option>方案支持</option><option>采购推进协调</option><option>其他</option></select></label>
        <div className="form-flow-note full"><Calendar {...iconProps} /><span>{mode === "queued" ? "后续计划按跟进日期距今天的远近自动排序；当前工作完成后，最近日期的计划会自动成为当前计划。" : "当前计划可随时维护；工作结果请回到计划卡片单独记录。"}</span></div>
        <div className="form-actions full"><button type="button" className="button ghost" onClick={close}>取消</button><button className="button primary" disabled={submitting}>{submitting ? "正在保存…" : mode === "queued" ? "保存到后续队列" : "保存当前计划"}</button></div>
      </form>
    </Modal>
  );
}

function DeletePlanConfirm({ route, plan, close, onDone, notify }: { route: RouteItem; plan: Plan; close: () => void; onDone: (message: string) => void | Promise<void>; notify: (tone: Notice["tone"], message: string) => void }) {
  const [submitting, setSubmitting] = useState(false);
  const state = planStateOf(plan, route);
  const stateLabel = state === "current" ? "当前计划" : state === "queued" ? "后续计划" : "已完成计划";
  const planInTimeline = route.planTimeline?.find((item) => item.id === plan.id);
  const resultCount = planInTimeline?.results?.length ?? route.progressHistory.filter((record) => record.planId === plan.id).length;
  const nextPlan = state === "current" ? route.queuedPlans?.[0] : null;
  const remove = async () => {
    setSubmitting(true);
    try {
      const response = await api<{ message: string }>(`/api/plans/${plan.id}`, { method: "DELETE" });
      await onDone(response.message);
    } catch (error) {
      notify("error", error instanceof Error ? error.message : "计划删除失败");
      setSubmitting(false);
    }
  };
  return <Modal title={`删除${stateLabel}`} description={`${route.customer?.name} · ${route.channel?.shortName} · ${route.type}`} close={close} bottomSheet>
    <section className="delete-plan-confirm">
      <div className="delete-plan-summary"><span>{stateLabel}</span><strong>{plan.nextAction}</strong><small>{formatDate(plan.followUpDate)} · {plan.owner || "待分配"} · {resultCount} 条工作结果</small></div>
      <div className="delete-plan-notice"><WarningTriangle width={21} height={21} strokeWidth={1.7} aria-hidden /><div><strong>删除后将从正常计划、统计、搜索和导出中隐藏</strong><p>{resultCount ? `该计划的 ${resultCount} 条工作结果会随计划一并隐藏，关联关系不会断开。` : "该计划当前没有工作结果。"} 总管理员可在“已删除计划”中恢复。</p>{state === "current" && <p>{nextPlan ? `系统将按日期自动推进“${nextPlan.nextAction}”。` : "删除后该通路将暂时没有当前计划。"}</p>}</div></div>
      <div className="delete-record-actions"><button type="button" className="button ghost" onClick={close}>取消</button><button type="button" className="button danger-button" disabled={submitting} onClick={remove}><Trash width={17} height={17} strokeWidth={1.7} aria-hidden />{submitting ? "正在移入…" : "确认移入已删除计划"}</button></div>
    </section>
  </Modal>;
}

function UnlinkRelationConfirm({ target, close, onDone, notify }: { target: RelationUnlinkTarget; close: () => void; onDone: (message: string) => void | Promise<void>; notify: (tone: Notice["tone"], message: string) => void }) {
  const [submitting, setSubmitting] = useState(false);
  const unlink = async () => {
    setSubmitting(true);
    try {
      const response = await api<{ message: string }>(`/api/relations/${target.relationId}`, { method: "DELETE" });
      await onDone(response.message);
    } catch (error) {
      notify("error", error instanceof Error ? error.message : "客户渠道关联取消失败");
      setSubmitting(false);
    }
  };
  return <Modal title="取消客户渠道关联" description={`${target.customerName} · ${target.channelName}`} close={close} bottomSheet>
    <section className="relation-unlink-confirm">
      <div className="relation-unlink-identity"><span><Xmark width={18} height={18} strokeWidth={2.2} aria-hidden /></span><div><small>将取消以下认领 / 绑定关系</small><strong>{target.channelName} × {target.customerName}</strong><p>{target.routeTypes.join("＋") || "暂无有效通路"} · 共 {target.routeCount} 条通路</p></div></div>
      <div className="relation-unlink-impact" aria-label="取消关联影响"><div><strong>{target.routeCount}</strong><span>条通路停止展示</span></div><div><strong>{target.planCount}</strong><span>项计划审计保留</span></div><div><strong>{target.resultCount}</strong><span>条结果审计保留</span></div></div>
      <div className="delete-plan-notice"><WarningTriangle width={21} height={21} strokeWidth={1.7} aria-hidden /><div><strong>取消后立即从正常客户、渠道、统计、搜索和导出中隐藏</strong><p>该关系下的有效通路会停用，已有计划和工作结果不会物理删除；若该客户已无其他有效渠道关系，将重新进入待认领客户池。</p><p>后续重新认领或审批通过同一客户时，系统会恢复原通路历史，不会重复建立执行链。</p>{target.downstreamCount > 0 && <p>其中 {target.downstreamCount} 条下游执行链也会同步解除，避免保留失效的下游跟进关系。</p>}</div></div>
      <div className="delete-record-actions"><button type="button" className="button ghost" onClick={close}>返回检查</button><button type="button" className="button danger-button" disabled={submitting} onClick={unlink}><Xmark width={17} height={17} strokeWidth={2} aria-hidden />{submitting ? "正在取消…" : "确认取消关联"}</button></div>
    </section>
  </Modal>;
}

function DeleteChannelAccountConfirm({ account, close, onDone, notify }: { account: Pick<TeamMember, "id" | "name" | "accountRole">; close: () => void; onDone: (message: string) => void | Promise<void>; notify: (tone: Notice["tone"], message: string) => void }) {
  const [submitting, setSubmitting] = useState(false);
  const remove = async () => {
    setSubmitting(true);
    try {
      const response = await post<{ message: string }>(`/api/users/${account.id}/delete`, {});
      await onDone(response.message);
    } catch (error) {
      notify("error", error instanceof Error ? error.message : "账号删除失败");
      setSubmitting(false);
    }
  };
  return <Modal title="删除下属账号" description={`${account.name} · ${account.accountRole === "channel_owner" ? "渠道负责人" : "渠道销售"}`} close={close} bottomSheet>
    <section className="delete-plan-confirm">
      <div className="delete-plan-notice"><WarningTriangle width={21} height={21} strokeWidth={1.7} aria-hidden /><div><strong>删除后该账号将立即无法登录</strong><p>登录凭据、MFA 和当前渠道成员关系会被清除；历史申报、计划、工作结果和审计记录仍保留原姓名与用户 ID。</p><p>未完成的通路和计划会自动转交给可用负责人。删除最后一名渠道负责人前，必须先建立另一名负责人。</p></div></div>
      <div className="delete-record-actions"><button type="button" className="button ghost" onClick={close}>取消</button><button type="button" className="button danger-button" disabled={submitting} onClick={remove}><Trash width={17} height={17} strokeWidth={1.7} aria-hidden />{submitting ? "正在删除…" : "确认删除账号"}</button></div>
    </section>
  </Modal>;
}

function DeletedPlansModal({ close, notify, onRestored }: { close: () => void; notify: (tone: Notice["tone"], message: string) => void; onRestored: () => Promise<void> }) {
  const [items, setItems] = useState<DeletedPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [restoringId, setRestoringId] = useState<string | null>(null);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api<{ items: DeletedPlan[] }>("/api/plans/deleted");
      setItems(response.items);
    } catch (error) {
      notify("error", error instanceof Error ? error.message : "已删除计划加载失败");
    } finally {
      setLoading(false);
    }
  }, [notify]);
  useEffect(() => { void load(); }, [load]);
  const restore = async (plan: DeletedPlan) => {
    setRestoringId(plan.id);
    try {
      const response = await post<{ message: string }>(`/api/plans/${plan.id}/restore`, {});
      await onRestored();
      await load();
      notify("success", response.message);
    } catch (error) {
      notify("error", error instanceof Error ? error.message : "计划恢复失败");
    } finally {
      setRestoringId(null);
    }
  };
  const stateLabel = (state: DeletedPlan["deletedFromState"]) => state === "current" ? "原当前计划" : state === "completed" ? "原已完成计划" : "原后续计划";
  return <Modal title="已删除计划" description="仅总管理员可查看和恢复；最近删除的计划排在最前面。" close={close}>
    <section className="deleted-plan-library">
      {loading ? <p className="muted-copy">正在加载已删除计划…</p> : items.length ? <div className="deleted-plan-list">{items.map((plan) => <article key={plan.id}>
        <header><div><span>{stateLabel(plan.deletedFromState)}</span><strong>{plan.nextAction}</strong></div><Status value="已删除" /></header>
        <p>{plan.customer?.name ?? "客户已归档"} · {plan.channel?.shortName || plan.channel?.name || "渠道已归档"} · {plan.routeType}</p>
        <dl><div><dt>跟进日期</dt><dd>{formatDate(plan.followUpDate)}</dd></div><div><dt>工作结果</dt><dd>{plan.resultCount} 条</dd></div><div><dt>删除记录</dt><dd>{plan.deletedBy} · {formatDateTime(plan.deletedAt)}</dd></div></dl>
        <footer><small>{plan.routeActive ? "恢复后将按当前通路状态自动归位" : "所属通路已停用，暂不可恢复"}</small><button type="button" className="button secondary" disabled={!plan.routeActive || restoringId === plan.id} onClick={() => restore(plan)}><CheckCircle width={16} height={16} strokeWidth={1.7} aria-hidden />{restoringId === plan.id ? "正在恢复…" : "恢复计划"}</button></footer>
      </article>)}</div> : <EmptyState compact title="暂无已删除计划" text="删除的计划会保留原计划与工作结果，并出现在这里。" />}
    </section>
  </Modal>;
}

function PoolPage({ state, user, notify, refresh }: { state: AppState; user: User; notify: (tone: Notice["tone"], message: string) => void; refresh: () => Promise<void> }) {
  const [applicationCustomer, setApplicationCustomer] = useState<LookupCustomer | null>(null);
  const [detailCustomer, setDetailCustomer] = useState<LookupCustomer | null>(null);
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [region, setRegion] = useState("all");
  const [quality, setQuality] = useState("all");
  const [sort, setSort] = useState("name");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState<PoolResult>({ items: [], total: 0, page: 1, pageSize: 24, pageCount: 1, facets: { regions: [] } });
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedQuery(query.trim());
      setPage(1);
    }, 220);
    return () => window.clearTimeout(timer);
  }, [query]);
  useEffect(() => {
    let active = true;
    setLoading(true);
    const params = new URLSearchParams({
      q: debouncedQuery,
      region,
      quality,
      sort,
      page: String(page),
      pageSize: "24"
    });
    api<PoolResult>(`/api/customers/pool?${params}`)
      .then((value) => {
        if (!active) return;
        setResult(value);
        if (value.page !== page) setPage(value.page);
      })
      .catch((error) => {
        if (active) notify("error", error instanceof Error ? error.message : "客户池加载失败");
      })
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [debouncedQuery, notify, page, quality, region, sort]);
  const resetFilters = () => {
    setQuery("");
    setDebouncedQuery("");
    setRegion("all");
    setQuality("all");
    setSort("name");
    setPage(1);
  };
  const toggleFavorite = async (customer: Pick<Customer, "id" | "favorite">) => {
    try {
      await api(`/api/customers/${customer.id}/favorite`, { method: customer.favorite ? "DELETE" : "PUT" });
      setResult((current) => ({
        ...current,
        items: current.items
          .map((item) => item.id === customer.id ? { ...item, favorite: !customer.favorite } : item)
          .sort((left, right) => Number(Boolean(right.favorite)) - Number(Boolean(left.favorite)))
      }));
      await refresh();
      notify("success", customer.favorite ? "已取消个人重点标记。" : "已加入我的重点客户。");
    } catch (error) {
      notify("error", error instanceof Error ? error.message : "重点客户标记失败");
    }
  };
  return (
    <>
      <PageHeader eyebrow="OPEN CUSTOMER POOL" title="待认领客户池" description="按关键词、区域和资料质量检索；每页只加载当前范围，数千家客户也无需长距离滚动。" />
      <section className="pool-directory">
        <div className="pool-toolbar">
          <label className="search-field"><Search {...iconProps} /><input aria-label="搜索待认领客户" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="客户全称、简称或任意中间关键词" />{query && <button type="button" aria-label="清除搜索" onClick={() => setQuery("")}><Xmark width={16} height={16} strokeWidth={1.7} aria-hidden /></button>}</label>
          <label><span>区域</span><select value={region} onChange={(event) => { setRegion(event.target.value); setPage(1); }}><option value="all">全部区域</option>{result.facets.regions.map((item) => <option key={item.value} value={item.value}>{item.value}（{item.count}）</option>)}</select></label>
          <label><span>资料状态</span><select value={quality} onChange={(event) => { setQuality(event.target.value); setPage(1); }}><option value="all">全部状态</option><option value="complete">区域与行业完整</option><option value="region_pending">区域待补全</option><option value="industry_pending">行业待补全</option></select></label>
          <label><span>排序</span><select value={sort} onChange={(event) => { setSort(event.target.value); setPage(1); }}><option value="name">按客户名称</option><option value="recent">按最近更新</option></select></label>
        </div>
        <div className="pool-meta">
          <div><strong>{result.total.toLocaleString("zh-CN")}</strong><span>家符合当前条件</span></div>
          <p>{loading ? "正在检索…" : `第 ${result.page} / ${result.pageCount} 页 · 每页 ${result.pageSize} 家`}</p>
          {(query || region !== "all" || quality !== "all" || sort !== "name") && <button className="button ghost" type="button" onClick={resetFilters}>清除筛选</button>}
        </div>
        <div className={`pool-list ${loading ? "is-loading" : ""}`} aria-busy={loading}>
          {result.items.map((customer) => (
            <article className="pool-row" key={customer.id}>
              <div className="pool-row-mark"><FavoriteButton customer={customer} onToggle={toggleFavorite} /></div>
              <button type="button" className="pool-row-name pool-customer-link" onClick={() => setDetailCustomer(customer)}><strong>{customer.name}</strong><small>{customer.shortName || "暂无简称"}</small></button>
              <div><span>所属区域</span><strong>{customer.region || "待补全"}</strong></div>
              <div><span>所属行业</span><strong>{customer.industry || "待补全"}</strong></div>
              <div className="pool-row-actions"><button className="button ghost" onClick={() => setDetailCustomer(customer)}>查看详情</button><button className="button secondary" onClick={() => setApplicationCustomer(customer)}>申报通路<ArrowRight {...iconProps} /></button></div>
            </article>
          ))}
        </div>
        {!loading && !result.items.length && <EmptyState title="没有符合条件的客户" text="可以清除筛选或换一个中间关键词继续检索。" />}
        {result.pageCount > 1 && <nav className="pool-pagination" aria-label="客户池分页"><button className="button ghost" disabled={result.page <= 1 || loading} onClick={() => setPage((current) => Math.max(1, current - 1))}><NavArrowLeft {...iconProps} />上一页</button><span>第 <strong>{result.page}</strong> 页，共 {result.pageCount} 页</span><button className="button ghost" disabled={result.page >= result.pageCount || loading} onClick={() => setPage((current) => Math.min(result.pageCount, current + 1))}>下一页<NavArrowRight {...iconProps} /></button></nav>}
      </section>
      {detailCustomer && <PoolCustomerDrawer customer={detailCustomer} close={() => setDetailCustomer(null)} apply={() => { setApplicationCustomer(detailCustomer); setDetailCustomer(null); }} notify={notify} />}
      {applicationCustomer && <ApplicationForm state={state} user={user} initialCustomer={applicationCustomer} kind="客户池申报" close={() => setApplicationCustomer(null)} notify={notify} onDone={async (message) => { await refresh(); setApplicationCustomer(null); setPage(1); notify("success", message); }} />}
    </>
  );
}

function PoolCustomerDrawer({ customer, close, apply, notify }: { customer: LookupCustomer; close: () => void; apply: () => void; notify: (tone: Notice["tone"], message: string) => void }) {
  const [researchFallbackUrl, setResearchFallbackUrl] = useState("");
  const openResearch = () => {
    const url = `${window.location.origin}${window.location.pathname}#/customer-research/${encodeURIComponent(customer.id)}`;
    const popup = window.open(url, "_blank");
    if (popup) {
      popup.opener = null;
      setResearchFallbackUrl("");
    } else {
      setResearchFallbackUrl(url);
      notify("error", "浏览器阻止了新窗口，请使用备用链接打开 AI 调研。");
    }
  };
  return <div className="drawer-layer" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && close()}>
    <div className="drawer pool-customer-drawer" role="dialog" aria-modal="true" aria-labelledby="pool-customer-title">
      <div className="drawer-head customer-drawer-head">
        <div><p className="eyebrow">OPEN CUSTOMER PROFILE</p><div className="drawer-customer-title"><h2 id="pool-customer-title">{customer.name}</h2></div><p>待认领客户 · 当前没有有效渠道关系</p></div>
        <div className="drawer-head-actions"><button className="button secondary" onClick={openResearch}><Spark {...iconProps} />AI 调研</button><button className="button primary" onClick={apply}><PlusCircle {...iconProps} />申报通路</button><button className="icon-button" aria-label="关闭详情" onClick={close}><Xmark {...iconProps} /></button></div>
        {researchFallbackUrl && <a className="research-fallback-link" href={researchFallbackUrl} target="_blank" rel="noreferrer">在新标签页打开 AI 调研<ArrowRight {...iconProps} /></a>}
      </div>
      <section className="customer-facts-card" aria-label="可见客户基础信息"><div><span>客户全称</span><strong>{customer.name}</strong></div><div><span>客户简称</span><strong>{customer.shortName || "未填写"}</strong></div><div><span>所属区域</span><strong>{customer.region || "待补全"}</strong></div><div><span>所属行业</span><strong>{customer.industry || "待补全"}</strong></div></section>
    </div>
  </div>;
}

function ApplicationsPage({ state, user, refresh, notify }: { state: AppState; user: User; refresh: () => Promise<void>; notify: (tone: Notice["tone"], message: string) => void }) {
  const [form, setForm] = useState<"existing" | "new" | null>(null);
  const [revising, setRevising] = useState<Application | null>(null);
  const [withdrawing, setWithdrawing] = useState<Application | null>(null);
  const [query, setQuery] = useState("");
  const normalizedQuery = query.trim().toLowerCase();
  const matchesQuery = (item: Application) => !normalizedQuery || [item.customer?.name, item.channel?.name, item.applicant, item.ownerName, item.status, item.kind].some((value) => String(value ?? "").toLowerCase().includes(normalizedQuery));
  const activeAll = state.applications.filter((item) => ["待渠道审核", "渠道退回补充", "待总管理员审批", "总管理员退回补充"].includes(item.status));
  const historyAll = state.applications.filter((item) => !activeAll.includes(item));
  const active = activeAll.filter(matchesQuery);
  const history = historyAll.filter(matchesQuery);
  return (
    <>
      <PageHeader eyebrow="ROUTE APPLICATION" title="通路申报" description="通过客户关键词快速定位，再补充通路依据与负责销售。">
        <button className="button primary" onClick={() => setForm("existing")}><PlusCircle {...iconProps} />已有客户申报</button>
        <button className="button secondary" onClick={() => setForm("new")}><Building {...iconProps} />新客户入库</button>
      </PageHeader>
      <div className="large-list-search"><label className="search-field"><Search {...iconProps} /><span className="sr-only">搜索申报记录</span><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="搜索客户、渠道、申请人、负责人或状态" />{query && <button type="button" aria-label="清除搜索" onClick={() => setQuery("")}><Xmark width={16} height={16} strokeWidth={1.7} aria-hidden /></button>}</label><span>当前显示 {active.length + history.length} / {state.applications.length} 条</span></div>
      <div className="application-intro">
        <article><span>01</span><h3>从客户池申报</h3><p>看得到、无人有效跟进的客户，可直接进入申报。</p></article>
        <article><span>02</span><h3>搜索已有客户</h3><p>输入名称关键词，从匹配结果中直接选择客户。</p></article>
        <article><span>03</span><h3>新客户入库申请</h3><p>审批通过后创建客户和获批通路，首次计划可后续补充。</p></article>
      </div>
      <div className="application-record-grid">
        <section className="panel">
          <PanelHead eyebrow="IN PROCESS" title="进行中的申报" />
          <div className="simple-list">{active.map((item) => <div className="application-record-item" key={item.id}>
            <ApplicationRow application={item} />
            <div className="application-record-actions">
              {["渠道退回补充", "总管理员退回补充"].includes(item.status) && <button className="button secondary" onClick={() => setRevising(item)}>补充并重提</button>}
              {item.applicantId === user.id && <button className="button ghost danger" onClick={() => setWithdrawing(item)}>撤销申报</button>}
            </div>
          </div>)}</div>
          {!active.length && <EmptyState compact title={query ? "没有匹配的进行中申报" : "当前没有进行中的申报"} text={query ? "调整搜索关键词后再试。" : "可以从上方任一入口发起。"} />}
        </section>
        <section className="panel">
          <PanelHead eyebrow="HISTORY" title="历史记录" />
          <div className="simple-list">{history.map((item) => <ApplicationRow key={item.id} application={item} />)}</div>
          {!history.length && <EmptyState compact title={query ? "没有匹配的历史记录" : "暂无历史记录"} text={query ? "调整搜索关键词后再试。" : "审批完成或驳回后会自动归档到这里。"} />}
        </section>
      </div>
      {form && <ApplicationForm state={state} user={user} kind={form === "new" ? "新客户入库" : "已有客户申报"} close={() => setForm(null)} notify={notify} onDone={async (message) => { await refresh(); setForm(null); notify("success", message); }} />}
      {revising && (revising.kind === "客户资料变更" && revising.customer?.id
        ? <CustomerUpdateRequestForm customer={state.customers.find((item) => item.id === revising.customer?.id) ?? { id: revising.customer.id, name: revising.customer.name, shortName: "", aliases: [], region: revising.customer.region, industry: "", sector: "", active: true, channelCount: 0, technical: false, procurement: false, planStatus: "暂无计划", updatedAt: revising.createdAt }} user={user} application={revising} close={() => setRevising(null)} notify={notify} onDone={async (message) => { await refresh(); setRevising(null); notify("success", message); }} />
        : <ResubmitApplicationForm application={revising} state={state} user={user} close={() => setRevising(null)} notify={notify} onDone={async (message) => { await refresh(); setRevising(null); notify("success", message); }} />)}
      {withdrawing && <WithdrawApplicationForm application={withdrawing} close={() => setWithdrawing(null)} notify={notify} onDone={async (message) => { await refresh(); setWithdrawing(null); notify("success", message); }} />}
    </>
  );
}

function ApplicationForm({ state, user, initialCustomer, kind, close, onDone, notify }: { state: AppState; user: User; initialCustomer?: LookupCustomer; kind: string; close: () => void; onDone: (message: string) => void; notify: (tone: Notice["tone"], message: string) => void }) {
  const [submitting, setSubmitting] = useState(false);
  const [types, setTypes] = useState<string[]>(["技术口"]);
  const [includePlan, setIncludePlan] = useState(false);
  const [customerQuery, setCustomerQuery] = useState(initialCustomer?.name ?? "");
  const [selectedCustomer, setSelectedCustomer] = useState<LookupCustomer | null>(initialCustomer ?? null);
  const [matches, setMatches] = useState<LookupCustomer[]>([]);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupAttempted, setLookupAttempted] = useState(false);
  const isNew = kind === "新客户入库";
  const activeTeam = (state.teamMembers ?? []).filter((member) => member.active);
  useEffect(() => {
    const normalizedLength = customerQuery.replace(/\s+/gu, "").length;
    if (isNew || selectedCustomer?.name === customerQuery || normalizedLength < 1) {
      setMatches([]);
      setLookupAttempted(false);
      setLookupLoading(false);
      return;
    }
    let active = true;
    setLookupLoading(true);
    const timer = window.setTimeout(() => {
      api<{ items: typeof matches }>(`/api/customers/search?q=${encodeURIComponent(customerQuery.trim())}`)
        .then((result) => {
          if (!active) return;
          setMatches(result.items);
          setLookupAttempted(true);
        })
        .catch(() => {
          if (!active) return;
          setMatches([]);
          setLookupAttempted(true);
        })
        .finally(() => active && setLookupLoading(false));
    }, 220);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [customerQuery, isNew, selectedCustomer?.name]);
  const toggleFavorite = async (customer: Pick<Customer, "id" | "favorite">) => {
    try {
      await api(`/api/customers/${customer.id}/favorite`, { method: customer.favorite ? "DELETE" : "PUT" });
      setMatches((current) => current
        .map((item) => item.id === customer.id ? { ...item, favorite: !customer.favorite } : item)
        .sort((left, right) => Number(Boolean(right.favorite)) - Number(Boolean(left.favorite))));
      setSelectedCustomer((current) => current?.id === customer.id ? { ...current, favorite: !customer.favorite } : current);
      notify("success", customer.favorite ? "已取消个人重点标记。" : "已加入我的重点客户。");
    } catch (error) {
      notify("error", error instanceof Error ? error.message : "重点客户标记失败");
    }
  };
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!isNew && !selectedCustomer) return notify("error", "请从模糊搜索结果中选择客户");
    const formData = new FormData(event.currentTarget);
    const values = Object.fromEntries(formData);
    setSubmitting(true);
    try {
      const response = await post<{ message: string }>("/api/applications", {
        customerId: selectedCustomer?.id,
        customerName: isNew ? values.customerName : selectedCustomer?.name,
        region: values.region,
        industry: values.industry,
        ownerId: values.ownerId,
        routeTypes: types,
        situation: values.situation,
        kind,
        firstPlan: includePlan ? { stage: values.stage, nextAction: values.nextAction, followUpDate: values.followUpDate } : null
      });
      onDone(response.message);
    } catch (error) { notify("error", error instanceof Error ? error.message : "提交失败"); }
    finally { setSubmitting(false); }
  };
  return (
    <Modal title={kind} description={isNew ? "审批通过后创建客户和获批通路；首次工作计划可选填。" : "输入客户名称关键词并从结果中选择。"} close={close}>
      <form className="form-grid" onSubmit={submit}>
        {isNew ? <Field label="客户全称" name="customerName" required placeholder="请输入完整企业名称" /> : (
          <div className="customer-lookup full">
            <label className="field"><span>搜索并选择客户 *</span><input value={customerQuery} onChange={(event) => { setCustomerQuery(event.target.value); setSelectedCustomer(null); }} placeholder="输入任意位置关键词，如“智能装备”或“华迪”" autoComplete="off" /><small>支持客户全称、简称、别名，以及用空格分隔的多个关键词。</small></label>
            {matches.length > 0 && <div className="customer-lookup-results">{matches.map((item) => <div className={`customer-lookup-result ${selectedCustomer?.id === item.id ? "selected" : ""}`} key={item.id}><button type="button" onClick={() => { setSelectedCustomer(item); setCustomerQuery(item.name); setMatches([]); }}><strong>{item.name}</strong><small>{item.shortName || "暂无简称"} · {item.region} · {item.industry || "行业待补充"}</small></button><FavoriteButton customer={item} onToggle={toggleFavorite} /></div>)}</div>}
            {lookupLoading && <div className="customer-lookup-state"><Clock width={16} height={16} strokeWidth={1.6} aria-hidden />正在检索客户主档…</div>}
            {!lookupLoading && lookupAttempted && customerQuery.trim() && !matches.length && !selectedCustomer && <div className="customer-lookup-state empty"><FileNotFound width={16} height={16} strokeWidth={1.6} aria-hidden />没有匹配结果，请更换关键词或使用“新客户入库”。</div>}
            {selectedCustomer && <div className="selected-customer"><CheckCircle {...iconProps} /><span><strong>已选择 {selectedCustomer.name}</strong><small>{selectedCustomer.region} · {selectedCustomer.industry || "行业待补充"}</small></span></div>}
          </div>
        )}
        {isNew && <Field label="所属区域" name="region" required placeholder="如：四川-成都、成都高新区或华西区域" />}
        {isNew && <Field label="所属行业" name="industry" placeholder="选填" />}
        <label className="field"><span>申报通路</span><div className="check-row">{["技术口", "采购口"].map((type) => <label key={type}><input type="checkbox" checked={types.includes(type)} onChange={(event) => setTypes(event.target.checked ? [...types, type] : types.filter((item) => item !== type))} />{type}</label>)}</div></label>
        <label className="field"><span>负责销售</span>{user.accountRole === "channel_owner" ? <select name="ownerId" defaultValue={user.id}>{activeTeam.map((member) => <option value={member.id} key={member.id}>{member.name} · {member.accountRole === "channel_owner" ? "负责人" : "渠道销售"}</option>)}</select> : <><input value={`${user.name} · 渠道销售`} readOnly aria-label="负责销售" /><input type="hidden" name="ownerId" value={user.id} /></>}</label>
        <label className="field full"><span>当前掌握的通路情况 *</span><textarea name="situation" required rows={3} placeholder="简要说明已经形成的关系与依据" /></label>
        <label className={`optional-plan-toggle full ${includePlan ? "active" : ""}`}><input type="checkbox" checked={includePlan} onChange={(event) => setIncludePlan(event.target.checked)} /><span><strong>同时填写首次工作计划</strong><small>选填；也可以审批通过后，从“我的客户”直接建立计划。</small></span></label>
        {includePlan && <>
          <label className="field"><span>当前阶段</span><select name="stage"><option>待建立联系</option><option>已建立联系</option><option>需求摸排</option><option>技术交流</option><option>采购信息摸排</option></select></label>
          <Field label="首次跟进日期" name="followUpDate" type="date" required defaultValue={todayOffset(5)} />
          <label className="field full"><span>首次工作计划 *</span><textarea name="nextAction" required rows={3} placeholder="填写明确、可执行的下一步动作" /></label>
        </>}
        <div className="form-flow-note full"><ShieldCheck {...iconProps} /><span>{user.accountRole === "channel_owner" ? "负责人发起的申报将直接进入总管理员审批。" : "渠道销售发起后先由渠道负责人审核，再提交总管理员。"}</span></div>
        <div className="form-actions full"><button className="button ghost" type="button" onClick={close}>取消</button><button className="button primary" disabled={submitting || !types.length || (!isNew && !selectedCustomer)}>{submitting ? "正在提交…" : "提交申报"}</button></div>
      </form>
    </Modal>
  );
}

function ApprovalsPage({ state, user, refresh, notify }: { state: AppState; user: User; refresh: () => Promise<void>; notify: (tone: Notice["tone"], message: string) => void }) {
  const manager = user.role === "manager";
  const pendingStatus = manager ? "待总管理员审批" : "待渠道审核";
  const pending = state.applications.filter((item) => item.status === pendingStatus);
  const history = state.applications.filter((item) => item.status !== pendingStatus);
  const [tab, setTab] = useState<"pending" | "history">("pending");
  const visibleItems = tab === "pending" ? pending : history;
  const [selectedId, setSelectedId] = useState(pending[0]?.id ?? history[0]?.id ?? "");
  const [opinion, setOpinion] = useState("");
  const [ownerId, setOwnerId] = useState("");
  const [applicationForm, setApplicationForm] = useState<"existing" | "new" | null>(null);
  const [revising, setRevising] = useState<Application | null>(null);
  const [withdrawing, setWithdrawing] = useState<Application | null>(null);
  const selected = visibleItems.find((item) => item.id === selectedId) ?? visibleItems[0];
  const activeTeam = (state.teamMembers ?? []).filter((member) => member.active);

  useEffect(() => {
    if (!visibleItems.some((item) => item.id === selectedId)) setSelectedId(visibleItems[0]?.id ?? "");
  }, [tab, visibleItems, selectedId]);

  useEffect(() => {
    setOwnerId(selected?.ownerId ?? user.id);
    setOpinion("");
  }, [selected?.id, selected?.ownerId, user.id]);

  const managerDecision = async (result: string, approvedTypes?: string[]) => {
    if (!selected) return;
    try {
      const response = await post<{ message: string }>(`/api/applications/${selected.id}/decision`, {
        result,
        approvedTypes,
        opinion: opinion || (result === "驳回" ? "当前依据不足，暂不建立通路。" : "按当前申报内容执行。")
      });
      setSelectedId(pending.find((item) => item.id !== selected.id)?.id ?? "");
      await refresh();
      notify("success", response.message);
    } catch (error) { notify("error", error instanceof Error ? error.message : "审批失败"); }
  };

  const channelDecision = async (action: string) => {
    if (!selected) return;
    try {
      const response = await post<{ message: string }>(`/api/applications/${selected.id}/channel-decision`, {
        action,
        ownerId,
        opinion: opinion || (action === "提交总管理员" ? "负责人已核验申报内容。" : action === "通过并更新" ? "资料变更内容已核验。" : "")
      });
      setSelectedId(pending.find((item) => item.id !== selected.id)?.id ?? "");
      await refresh();
      notify("success", response.message);
    } catch (error) { notify("error", error instanceof Error ? error.message : "审批失败"); }
  };

  return (
    <>
      <PageHeader
        eyebrow={manager ? "ADMIN APPROVAL CENTER" : "CHANNEL REVIEW DESK"}
        title={manager ? "通路申报审批中心" : "渠道申报与审批"}
        description={manager ? "只在待办中展示已通过渠道负责人审核的申请；处理后立即归入审批历史。" : "渠道销售的申报先由负责人审核；负责人自己发起的申报直接提交总管理员。"}
      >
        {!manager && <><button className="button primary" onClick={() => setApplicationForm("existing")}><PlusCircle {...iconProps} />发起申报</button><button className="button secondary" onClick={() => setApplicationForm("new")}><Building {...iconProps} />新客户入库</button></>}
      </PageHeader>
      <div className="approval-tabs" role="tablist" aria-label="审批范围">
        <button role="tab" aria-selected={tab === "pending"} className={tab === "pending" ? "active" : ""} onClick={() => setTab("pending")}>待我审批 <span>{pending.length}</span></button>
        <button role="tab" aria-selected={tab === "history"} className={tab === "history" ? "active" : ""} onClick={() => setTab("history")}>审批历史 <span>{history.length}</span></button>
      </div>
      <div className="approval-layout">
        <aside className="approval-queue">
          <header><span>{tab === "pending" ? "待处理队列" : "已处理与流转记录"}</span><strong>{visibleItems.length}</strong></header>
          {visibleItems.map((item) => <button key={item.id} className={selected?.id === item.id ? "active" : ""} onClick={() => setSelectedId(item.id)}><span><strong>{item.customer?.name}</strong><small>{item.applicant ? `${item.applicant} 发起 · ` : ""}{item.kind}</small></span><Status value={item.status} /></button>)}
        </aside>
        <section className="approval-detail">
          {selected ? <>
            <div className="approval-title"><div><p className="eyebrow">{selected.kind}</p><h2>{selected.customer?.name}</h2><p>{selected.customer?.region} · 申请渠道：{selected.channel?.name}</p></div><Status value={selected.status} /></div>
            <div className="approval-meta-strip"><div><span>申请人</span><strong>{selected.applicant ?? "—"}</strong></div>{selected.kind !== "客户资料变更" && <div><span>负责销售</span><strong>{selected.ownerName ?? "待分配"}</strong></div>}<div><span>提交时间</span><strong>{formatDateTime(selected.createdAt)}</strong></div></div>
            {selected.kind === "客户资料变更" ? <CustomerChangePreview change={selected.customerChange} reason={selected.situation} /> : <>
              <div className="approval-block"><span>申请通路</span><div className="route-tags">{selected.routeTypes.map((type) => <RouteTag key={type} type={type as "技术口" | "采购口"} />)}</div></div>
              <div className="approval-grid"><div><span>当前掌握的通路情况</span><p>{selected.situation}</p></div><div><span>首次工作计划</span>{selected.firstPlan ? <><p>{selected.firstPlan.nextAction}</p><small>{selected.firstPlan.stage} · {formatDate(selected.firstPlan.followUpDate)}</small></> : <p className="muted">暂未填写，可在审批通过后从客户详情建立。</p>}</div></div>
            </>}
            {!manager && selected.kind !== "客户资料变更" && tab === "pending" && <label className="field approval-owner"><span>通过后负责销售</span><select value={ownerId} onChange={(event) => setOwnerId(event.target.value)}>{activeTeam.map((member) => <option key={member.id} value={member.id}>{member.name} · {member.accountRole === "channel_owner" ? "渠道负责人" : "渠道销售"}</option>)}</select><small>可在提交总管理员前重新分配负责人，不会改写销售提交的申报内容。</small></label>}
            {tab === "pending" && <label className="field approval-opinion"><span>审批意见</span><textarea rows={2} value={opinion} onChange={(event) => setOpinion(event.target.value)} placeholder="选填，退回或驳回时建议说明原因" /></label>}
            <ApprovalTimeline application={selected} />
            {tab === "pending" ? (
              manager
                ? <div className="approval-actions"><button className="button ghost danger" onClick={() => managerDecision("驳回")}>驳回</button><button className="button ghost" onClick={() => managerDecision("退回补充")}>退回补充</button>{selected.routeTypes.length > 1 && <button className="button secondary" onClick={() => managerDecision("部分通过", [selected.routeTypes[0]])}>仅通过{selected.routeTypes[0]}</button>}<button className="button primary" onClick={() => managerDecision("全部通过")}><Check {...iconProps} />全部通过</button></div>
                : selected.kind === "客户资料变更"
                  ? <div className="approval-actions"><button className="button ghost danger" onClick={() => channelDecision("驳回")}>驳回</button><button className="button ghost" onClick={() => channelDecision("退回补充")}>退回补充</button><button className="button primary" onClick={() => channelDecision("通过并更新")}><Check {...iconProps} />通过并更新</button></div>
                  : <div className="approval-actions"><button className="button ghost danger" onClick={() => channelDecision("驳回")}>驳回</button><button className="button ghost" onClick={() => channelDecision("退回补充")}>退回补充</button><button className="button primary" onClick={() => channelDecision("提交总管理员")}><Check {...iconProps} />提交总管理员</button></div>
            ) : <div className="approval-finished">该申请当前状态：{selected.status}。已从默认待办队列移入此固定历史视图。{selected.applicantId === user.id && ["渠道退回补充", "总管理员退回补充"].includes(selected.status) && <button className="button secondary" onClick={() => setRevising(selected)}>补充并重新提交</button>}{selected.applicantId === user.id && ["待渠道审核", "渠道退回补充", "待总管理员审批", "总管理员退回补充"].includes(selected.status) && <button className="button ghost danger" onClick={() => setWithdrawing(selected)}>撤销申报</button>}</div>}
          </> : <EmptyState title={tab === "pending" ? "当前没有待审批申报" : "还没有审批历史"} text={tab === "pending" ? "新的待办会按审批层级自动进入这里。" : "完成、驳回、退回或已向上流转的申报会归档在这里。"} />}
        </section>
      </div>
      {applicationForm && <ApplicationForm state={state} user={user} kind={applicationForm === "new" ? "新客户入库" : "已有客户申报"} close={() => setApplicationForm(null)} notify={notify} onDone={async (message) => { await refresh(); setApplicationForm(null); notify("success", message); }} />}
      {revising && (revising.kind === "客户资料变更" && revising.customer?.id
        ? <CustomerUpdateRequestForm customer={state.customers.find((item) => item.id === revising.customer?.id) ?? { id: revising.customer.id, name: revising.customer.name, shortName: "", aliases: [], region: revising.customer.region, industry: "", sector: "", active: true, channelCount: 0, technical: false, procurement: false, planStatus: "暂无计划", updatedAt: revising.createdAt }} user={user} application={revising} close={() => setRevising(null)} notify={notify} onDone={async (message) => { await refresh(); setRevising(null); notify("success", message); }} />
        : <ResubmitApplicationForm application={revising} state={state} user={user} close={() => setRevising(null)} notify={notify} onDone={async (message) => { await refresh(); setRevising(null); notify("success", message); }} />)}
      {withdrawing && <WithdrawApplicationForm application={withdrawing} close={() => setWithdrawing(null)} notify={notify} onDone={async (message) => { await refresh(); setWithdrawing(null); notify("success", message); }} />}
    </>
  );
}

function ApprovalTimeline({ application }: { application: Application }) {
  const events = application.approvalHistory ?? [];
  return (
    <section className="approval-timeline">
      <header><span>流转记录</span><strong>{events.length} 项</strong></header>
      {events.length ? events.map((event) => <article key={event.id}><i /><div><strong>{event.action}</strong><small>{event.stage} · {event.actor} · {formatDateTime(event.at)}</small>{event.opinion && <p>{event.opinion}</p>}</div></article>) : <p className="muted">暂无流转记录。</p>}
    </section>
  );
}

const customerChangeLabels: Record<keyof CustomerChangeSet["proposed"], string> = {
  name: "客户全称",
  shortName: "客户简称",
  region: "所属区域",
  industry: "所属行业",
  contactName: "客户联系人",
  contactInfo: "联系方式"
};

function CustomerChangePreview({ change, reason }: { change?: CustomerChangeSet | null; reason: string }) {
  if (!change) return <div className="approval-grid"><div><span>变更原因或依据</span><p>{reason}</p></div><div><span>变更内容</span><p className="muted">暂无可展示的差异内容。</p></div></div>;
  return (
    <section className="customer-change-preview">
      <header><div><span>变更原因或依据</span><p>{reason}</p></div><strong>{change.changedFields.length} 项变更</strong></header>
      <div className="customer-change-grid">
        {change.changedFields.map((field) => {
          const key = field as keyof CustomerChangeSet["proposed"];
          return <article key={field}><span>{customerChangeLabels[key] ?? field}</span><div><small>当前</small><p>{change.before[key] || "未填写"}</p></div><ArrowRight width={18} height={18} strokeWidth={1.6} aria-hidden /><div className="proposed"><small>拟更新</small><p>{change.proposed[key] || "未填写"}</p></div></article>;
        })}
      </div>
    </section>
  );
}

function WithdrawApplicationForm({ application, close, onDone, notify }: { application: Application; close: () => void; onDone: (message: string) => void; notify: (tone: Notice["tone"], message: string) => void }) {
  const [submitting, setSubmitting] = useState(false);
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    try {
      const values = Object.fromEntries(new FormData(event.currentTarget));
      const response = await post<{ message: string }>(`/api/applications/${application.id}/withdraw`, { reason: values.reason });
      onDone(response.message);
    } catch (error) {
      notify("error", error instanceof Error ? error.message : "撤销申报失败");
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <Modal title="撤销申报" description={`${application.customer?.name} · ${application.status}`} close={close}>
      <form className="form-grid" onSubmit={submit}>
        <div className="delete-record-warning full"><WarningTriangle width={24} height={24} strokeWidth={1.6} aria-hidden /><span><strong>撤销后将移入固定历史记录</strong><p>申请内容、审批轨迹、撤销人、时间和原因都会继续保留，已完成终审的申请无法撤销。</p></span></div>
        <label className="field full"><span>撤销原因 *</span><textarea name="reason" rows={3} required placeholder="请简要说明为什么撤销本次申报" /></label>
        <div className="form-actions full"><button type="button" className="button ghost" onClick={close}>取消</button><button className="button danger-button" disabled={submitting}>{submitting ? "正在撤销…" : "确认撤销申报"}</button></div>
      </form>
    </Modal>
  );
}

function ResubmitApplicationForm({ application, state, user, close, onDone, notify }: { application: Application; state: AppState; user: User; close: () => void; onDone: (message: string) => void; notify: (tone: Notice["tone"], message: string) => void }) {
  const [submitting, setSubmitting] = useState(false);
  const [includePlan, setIncludePlan] = useState(Boolean(application.firstPlan));
  const [routeTypes, setRouteTypes] = useState(application.routeTypes);
  const activeTeam = (state.teamMembers ?? []).filter((member) => member.active);
  const isNewCustomer = application.kind === "新客户入库";
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const values = Object.fromEntries(new FormData(event.currentTarget));
    setSubmitting(true);
    try {
      const response = await post<{ message: string }>(`/api/applications/${application.id}/resubmit`, {
        customerId: application.customer?.id,
        customerName: application.customer?.name,
        region: values.region,
        industry: values.industry,
        routeTypes,
        ownerId: values.ownerId,
        situation: values.situation,
        firstPlan: includePlan ? {
          stage: values.stage,
          nextAction: values.nextAction,
          followUpDate: values.followUpDate
        } : null
      });
      onDone(response.message);
    } catch (error) { notify("error", error instanceof Error ? error.message : "重新提交失败"); }
    finally { setSubmitting(false); }
  };
  return (
    <Modal title="补充并重新提交" description={`${application.customer?.name} · ${application.status}`} close={close}>
      <form className="form-grid" onSubmit={submit}>
        <label className="field full"><span>客户全称（锁定）</span><input readOnly value={application.customer?.name ?? ""} /><small>前后端都会校验客户名称与客户ID，本次不可修改。</small></label>
        {isNewCustomer && <Field label="所属区域" name="region" required defaultValue={application.customer?.region} />}
        {isNewCustomer && <Field label="所属行业" name="industry" defaultValue={application.customer?.industry} placeholder="选填" />}
        <label className="field"><span>申报通路 *</span><div className="check-row">{["技术口", "采购口"].map((type) => <label key={type}><input type="checkbox" checked={routeTypes.includes(type)} onChange={(event) => setRouteTypes(event.target.checked ? [...routeTypes, type] : routeTypes.filter((item) => item !== type))} />{type}</label>)}</div></label>
        <label className="field"><span>负责销售</span>{user.accountRole === "channel_owner" ? <select name="ownerId" defaultValue={application.ownerId ?? user.id}>{activeTeam.map((member) => <option value={member.id} key={member.id}>{member.name} · {member.accountRole === "channel_owner" ? "负责人" : "渠道销售"}</option>)}</select> : <><input readOnly value={`${user.name} · 渠道销售`} /><input type="hidden" name="ownerId" value={user.id} /></>}</label>
        <label className="field full"><span>当前掌握的通路情况 *</span><textarea name="situation" required rows={4} defaultValue={application.situation} /></label>
        <label className={`optional-plan-toggle full ${includePlan ? "active" : ""}`}><input type="checkbox" checked={includePlan} onChange={(event) => setIncludePlan(event.target.checked)} /><span><strong>同时填写首次工作计划</strong><small>选填；也可以审批通过后从客户详情建立。</small></span></label>
        {includePlan && <>
          <label className="field"><span>当前阶段</span><select name="stage" defaultValue={application.firstPlan?.stage ?? "待建立联系"}><option>待建立联系</option><option>已建立联系</option><option>需求摸排</option><option>技术交流</option><option>采购信息摸排</option><option>方案沟通</option></select></label>
          <Field label="首次跟进日期" name="followUpDate" type="date" required defaultValue={application.firstPlan?.followUpDate ?? todayOffset(5)} />
          <label className="field full"><span>首次工作计划 *</span><textarea name="nextAction" required rows={3} defaultValue={application.firstPlan?.nextAction} /></label>
        </>}
        <div className="form-flow-note full"><ShieldCheck {...iconProps} /><span>原申请和此前审批意见会保留在流转记录中，本次补充不会覆盖历史。</span></div>
        <div className="form-actions full"><button type="button" className="button ghost" onClick={close}>取消</button><button className="button primary" disabled={submitting || !routeTypes.length}>{submitting ? "正在提交…" : "重新提交"}</button></div>
      </form>
    </Modal>
  );
}

function PlansPage({ state, refresh, notify, user }: { state: AppState; refresh: () => Promise<void>; notify: (tone: Notice["tone"], message: string) => void; user: User }) {
  const canViewStatistics = user.role === "manager" || user.role === "business" || user.accountRole === "channel_owner";
  const canViewCustomerFollowUp = user.role === "manager" || user.role === "business";
  const [pageView, setPageView] = useState<"work" | "statistics">(() => canViewStatistics && new URLSearchParams(window.location.hash.split("?")[1] ?? "").get("view") === "statistics" ? "statistics" : "work");
  const [filter, setFilter] = useState("全部");
  const [channelFilter, setChannelFilter] = useState("all");
  const [query, setQuery] = useState(() => new URLSearchParams(window.location.hash.split("?")[1] ?? "").get("q") ?? "");
  const [editingPlan, setEditingPlan] = useState<RouteItem | null>(null);
  const [queuedPlanForm, setQueuedPlanForm] = useState<RouteItem | null>(null);
  const [deletingPlan, setDeletingPlan] = useState<{ route: RouteItem; plan: Plan } | null>(null);
  const [deletedPlansOpen, setDeletedPlansOpen] = useState(false);
  const [recordingPlan, setRecordingPlan] = useState<{ route: RouteItem; plan: Plan } | null>(null);
  const [editingRecord, setEditingRecord] = useState<{ route: RouteItem; record: ProgressRecord } | null>(null);
  const [deletingRecord, setDeletingRecord] = useState<{ route: RouteItem; record: ProgressRecord } | null>(null);
  const [announcementForm, setAnnouncementForm] = useState(false);
  const planAnnouncements = state.planAnnouncements ?? [];
  const filters = ["全部", "今日到期", "三日内到期", "已逾期", "推进受阻", "暂无计划"];
  const channelOptions = useMemo(() => {
    const channels = new Map<string, string>();
    state.routes.forEach((route) => {
      if (route.channel?.id) channels.set(route.channel.id, route.channel.name);
    });
    return [...channels].map(([id, name]) => ({ id, name })).sort((left, right) => left.name.localeCompare(right.name, "zh-CN"));
  }, [state.routes]);
  const normalizedQuery = query.trim().toLowerCase();
  const channelScopedRoutes = user.role === "manager" && channelFilter !== "all" ? state.routes.filter((route) => route.channel?.id === channelFilter) : state.routes;
  const searchedRoutes = channelScopedRoutes.filter((route) => !normalizedQuery || [route.customer?.name, route.customer?.region, route.customer?.industry, route.channel?.name, route.channel?.shortName, route.channelPath, route.downstreamFollower?.name, route.type, route.stage, route.plan?.nextAction, route.plan?.owner].some((value) => String(value ?? "").toLowerCase().includes(normalizedQuery)));
  const routes = searchedRoutes.filter((route) => filter === "全部" || (route.plan?.status ?? "暂无计划") === filter);
  const customerGroups = useMemo(() => {
    const groups = new Map<string, { customer: RouteItem["customer"]; routes: RouteItem[] }>();
    routes.forEach((route) => {
      const key = route.customer?.id ?? "unassigned";
      const group = groups.get(key);
      if (group) group.routes.push(route);
      else groups.set(key, { customer: route.customer, routes: [route] });
    });
    return [...groups.values()].sort((left, right) => (left.customer?.name ?? "").localeCompare(right.customer?.name ?? "", "zh-CN"));
  }, [routes]);
  const renderPlanCard = (route: RouteItem) => <PlanCard key={route.id} route={route} user={user} editPlan={() => setEditingPlan(route)} addQueuedPlan={() => setQueuedPlanForm(route)} deletePlan={(plan) => setDeletingPlan({ route, plan })} recordResult={(plan) => setRecordingPlan({ route, plan })} editRecord={(record) => setEditingRecord({ route, record })} deleteRecord={(record) => setDeletingRecord({ route, record })} />;
  const switchView = (next: "work" | "statistics") => {
    setPageView(next);
    window.history.replaceState(null, "", next === "statistics" ? "#/plans?view=statistics" : "#/plans");
  };
  return (
    <>
      <PageHeader eyebrow={user.role === "manager" ? "PLAN SUPERVISION" : "PLAN & PROGRESS"} title={user.role === "manager" ? "计划督办" : user.role === "business" ? "对接渠道计划全景" : "计划与进度"} description="以客户为主题，在每条渠道通路中连续查看当前计划、后续队列、已完成计划及其工作结果。" />
      {(canViewStatistics || user.role === "manager") && <div className="plan-page-toolbar">{canViewStatistics && <div className="plan-page-switch" role="tablist" aria-label="计划页面视图"><button type="button" role="tab" aria-selected={pageView === "work"} className={pageView === "work" ? "active" : ""} onClick={() => switchView("work")}><Calendar {...iconProps} />工作计划</button><button type="button" role="tab" aria-selected={pageView === "statistics"} className={pageView === "statistics" ? "active" : ""} onClick={() => switchView("statistics")}><StatsUpSquare {...iconProps} />统计分析</button></div>}{user.role === "manager" && <button type="button" className="button secondary" onClick={() => setDeletedPlansOpen(true)}><Trash width={17} height={17} strokeWidth={1.7} aria-hidden />已删除计划</button>}</div>}
      <section className="plan-announcements is-prominent" id="plan-noticeboard" aria-label="工作计划公告">
        <header><div><p className="eyebrow">PLAN NOTICEBOARD</p><h2>工作计划公告</h2><span>重要计划信息置顶展示</span></div>{user.role === "manager" && <button className="button secondary" onClick={() => setAnnouncementForm(true)}><PlusCircle {...iconProps} />发布公告</button>}</header>
        {planAnnouncements.length ? <div className="plan-announcement-list">{planAnnouncements.map((announcement, index) => <details key={announcement.id} open={index === 0} onToggle={(event) => { if ((event.currentTarget as HTMLDetailsElement).open && !announcement.read) void post(`/api/plan-announcements/${announcement.id}/read`, {}).then(refresh); }}><summary><span><Bell width={18} height={18} strokeWidth={1.7} aria-hidden /><strong>{announcement.title}</strong><small>{announcement.author} · {formatDateTime(announcement.createdAt)}</small></span><span>{index === 0 ? "最新" : "展开"}<NavArrowRight width={16} height={16} strokeWidth={1.7} aria-hidden /></span></summary><article className="rich-content" dangerouslySetInnerHTML={{ __html: announcement.contentHtml }} /></details>)}</div> : <p className="muted-copy">当前没有计划公告。</p>}
      </section>
      {pageView === "work" ? <>
      <div className={`plan-search-controls ${user.role === "manager" ? "has-channel-filter" : ""}`}>
        <div className="large-list-search plan-search"><label className="search-field"><Search {...iconProps} /><span className="sr-only">搜索客户计划</span><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="搜索客户、渠道、负责人、阶段或下一步动作" />{query && <button type="button" aria-label="清除搜索" onClick={() => setQuery("")}><Xmark width={16} height={16} strokeWidth={1.7} aria-hidden /></button>}</label><span>当前匹配 {searchedRoutes.length} / {channelScopedRoutes.length} 条通路</span></div>
        {user.role === "manager" && <label className="plan-channel-filter"><Community width={18} height={18} strokeWidth={1.6} aria-hidden /><span className="sr-only">按渠道筛选计划</span><select aria-label="按渠道筛选计划" value={channelFilter} onChange={(event) => setChannelFilter(event.target.value)}><option value="all">全部渠道</option>{channelOptions.map((channel) => <option value={channel.id} key={channel.id}>{channel.name}</option>)}</select></label>}
      </div>
      <div className="plan-filter">{filters.map((item) => <button className={filter === item ? "active" : ""} key={item} onClick={() => setFilter(item)}>{item}<span>{item === "全部" ? searchedRoutes.length : searchedRoutes.filter((route) => (route.plan?.status ?? "暂无计划") === item).length}</span></button>)}</div>
      <div className="customer-plan-groups">
        {customerGroups.map((group) => <section className="customer-plan-group" key={group.customer?.id ?? "unassigned"}>
          <header><div><p className="eyebrow">CUSTOMER PLAN CHAIN</p><h2>{group.customer?.name ?? "未关联客户"}</h2><span>{group.customer?.region || "区域待补充"} · {group.customer?.industry || "行业待补充"} · {new Set(group.routes.map((route) => route.channel?.id).filter(Boolean)).size} 个渠道 · {group.routes.length} 条通路</span></div><div><strong>{group.routes.filter((route) => ["已逾期", "推进受阻"].includes(route.plan?.status ?? "")).length}</strong><small>项需关注</small></div></header>
          {canViewCustomerFollowUp && <CustomerFollowUpBoard routes={group.routes} collapseByRouteType={user.role === "manager"} onRecordResult={(route, plan) => setRecordingPlan({ route, plan })} onEditPlan={setEditingPlan} onDeletePlan={(route, plan) => setDeletingPlan({ route, plan })} onEditRecord={(route, record) => setEditingRecord({ route, record })} onDeleteRecord={(route, record) => setDeletingRecord({ route, record })} />}
          {user.role === "manager" ? <details className="customer-plan-archive"><summary onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); const details = event.currentTarget.parentElement as HTMLDetailsElement; details.open = !details.open; } }}><div><p className="eyebrow">ROUTE HISTORY</p><h3>通路队列与历史</h3><small>当前计划已在上方统一展示</small></div><span><span className="archive-collapsed-label">展开 {group.routes.length} 条通路</span><span className="archive-expanded-label">收起通路历史</span><NavArrowRight width={17} height={17} strokeWidth={1.7} aria-hidden /></span></summary><div className="customer-plan-archive-body"><div className="customer-plan-routes">{group.routes.map(renderPlanCard)}</div></div></details> : <><div className="customer-plan-archive-heading"><div><p className="eyebrow">{canViewCustomerFollowUp ? "ROUTE HISTORY" : "MY CHANNEL PLANS"}</p><h3>{canViewCustomerFollowUp ? "通路队列与历史" : "本渠道计划"}</h3></div><span>{canViewCustomerFollowUp ? "当前计划已在上方统一展示" : "仅展示本公司相关通路与计划"}</span></div><div className="customer-plan-routes">{group.routes.map(renderPlanCard)}</div></>}
        </section>)}
      </div>
      {!routes.length && <EmptyState title={query ? "没有匹配的客户计划" : "当前筛选下没有计划"} text={query ? "可按客户全称、中间关键词、负责人或下一步动作继续检索。" : "选择其他状态，或为无计划通路补充下一步动作。"} />}
      {editingPlan && <PlanEditForm route={editingPlan} user={user} teamMembers={state.teamMembers ?? []} workNatures={state.workNatures ?? []} close={() => setEditingPlan(null)} notify={notify} onDone={async (message) => { await refresh(); setEditingPlan(null); notify("success", message); }} />}
      {queuedPlanForm && <PlanEditForm route={queuedPlanForm} mode="queued" user={user} teamMembers={state.teamMembers ?? []} workNatures={state.workNatures ?? []} close={() => setQueuedPlanForm(null)} notify={notify} onDone={async (message) => { await refresh(); setQueuedPlanForm(null); notify("success", message); }} />}
      {deletingPlan && <DeletePlanConfirm route={deletingPlan.route} plan={deletingPlan.plan} close={() => setDeletingPlan(null)} notify={notify} onDone={async (message) => { await refresh(); setDeletingPlan(null); notify("success", message); }} />}
      {recordingPlan && <ProgressForm route={recordingPlan.route} plan={recordingPlan.plan} close={() => setRecordingPlan(null)} notify={notify} onDone={async (message) => { await refresh(); setRecordingPlan(null); notify("success", message); }} />}
      {editingRecord && <EditProgressRecordForm route={editingRecord.route} record={editingRecord.record} close={() => setEditingRecord(null)} notify={notify} onDone={async (message) => { await refresh(); setEditingRecord(null); notify("success", message); }} />}
      {deletingRecord && <DeleteProgressRecordConfirm route={deletingRecord.route} record={deletingRecord.record} close={() => setDeletingRecord(null)} notify={notify} onDone={async (message) => { await refresh(); setDeletingRecord(null); notify("success", message); }} />}
      </> : <PlanStatisticsPage state={state} user={user} refresh={refresh} notify={notify} embedded />}
      {deletedPlansOpen && <DeletedPlansModal close={() => setDeletedPlansOpen(false)} notify={notify} onRestored={refresh} />}
      {announcementForm && <PlanAnnouncementForm close={() => setAnnouncementForm(false)} notify={notify} onDone={async (message) => { await refresh(); setAnnouncementForm(false); notify("success", message); }} />}
    </>
  );
}

function PlanCard({ route, editPlan, addQueuedPlan, deletePlan, recordResult, editRecord, deleteRecord, user }: { route: RouteItem; editPlan: () => void; addQueuedPlan: () => void; deletePlan: (plan: Plan) => void; recordResult: (plan: Plan) => void; editRecord: (record: ProgressRecord) => void; deleteRecord: (record: ProgressRecord) => void; user: User }) {
  return (
    <article className={`plan-card plan-route-workspace ${route.type === "技术口" ? "technical" : "purchase"}`}>
      <header className="plan-route-workspace-head">
        <div><div><RouteTag type={route.type} /><strong>{route.channelPath ?? route.channel?.name ?? "未分配渠道"}</strong></div><span>{route.stage} · 一级负责人 {route.plan?.owner ?? route.owner?.name ?? "待分配"}{route.downstreamFollower ? ` · 下游负责人 ${route.downstreamFollower.name}` : ""}</span></div>
        <div>{user.role === "manager" && <PriorityBadge value={route.priority} />}<Status value={route.plan?.status ?? "暂无计划"} /></div>
      </header>
      <RoutePlanChain route={route} hideCurrentWhenPresent={user.role === "manager" || user.role === "business"} onEditCurrentPlan={route.canOperate ? editPlan : undefined} onAddQueuedPlan={route.canOperate ? addQueuedPlan : undefined} onDeletePlan={deletePlan} onRecordResult={recordResult} onEditRecord={editRecord} onDeleteRecord={deleteRecord} />
    </article>
  );
}

function ProgressForm({ route, plan, close, onDone, notify }: { route: RouteItem; plan: Plan; close: () => void; onDone: (message: string) => void | Promise<void>; notify: (tone: Notice["tone"], message: string) => void }) {
  const [submitting, setSubmitting] = useState(false);
  const [detail, setDetail] = useState("");
  const [completePlan, setCompletePlan] = useState(false);
  const tags = plan.workTags?.length ? plan.workTags : [{ id: "unclassified", name: "未分类", groupName: "未分组" }];
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const normalizedDetail = detail.trim();
    if (!normalizedDetail) return notify("error", "请填写本次跟进的具体情况");
    setSubmitting(true);
    try {
      await post(`/api/routes/${route.id}/progress`, {
        planId: plan.id,
        completePlan,
        detail: normalizedDetail
      });
      await onDone(completePlan ? "工作结果已保存，本计划已完成并按现有规则推进队列" : "工作结果已保存，当前计划继续跟进");
    } catch (error) { notify("error", error instanceof Error ? error.message : "工作结果保存失败"); }
    finally { setSubmitting(false); }
  };
  return (
    <Modal title="记录工作结果" description={`${route.customer?.name} · ${route.channel?.shortName} · ${route.type}`} close={close} bottomSheet>
      <form className="simple-progress-form" onSubmit={submit}>
        <section className="simple-progress-context" aria-label="当前工作计划">
          <header><span>当前工作计划</span><Status value={plan.status} /></header>
          <h3>{plan.nextAction}</h3>
          <div><span><ClipboardCheck width={16} height={16} strokeWidth={1.6} aria-hidden />计划阶段 · {plan.stage}</span><span><Calendar width={16} height={16} strokeWidth={1.6} aria-hidden />{formatDate(plan.followUpDate)}</span><span><UserCircle width={16} height={16} strokeWidth={1.6} aria-hidden />{plan.owner || "待分配"}</span></div>
          <div className="simple-progress-tags"><small>计划标签</small>{tags.map((tag) => <span key={tag.id}>{tag.groupName && tag.groupName !== "未分组" ? <small>{tag.groupName}</small> : null}{tag.name}</span>)}</div>
        </section>
        <section className="simple-progress-fields">
          <label className="field full"><span>具体情况 *</span><textarea name="detail" rows={4} required value={detail} onChange={(event) => setDetail(event.target.value)} aria-describedby="progress-detail-hint" placeholder="填写本次跟进的实际情况、已确认事项或当前结论" /><small id="progress-detail-hint">仅记录实际结果；计划阶段、标签、负责人和后续安排请在“编辑计划”中维护。</small></label>
          <label className={`plan-completion-choice ${completePlan ? "is-complete" : ""}`}>
            <input type="checkbox" name="completePlan" checked={completePlan} onChange={(event) => setCompletePlan(event.target.checked)} />
            <span className="plan-completion-checkbox" aria-hidden>{completePlan && <Check width={17} height={17} strokeWidth={2.2} />}</span>
            <span className="plan-completion-copy"><strong>本次已完成计划</strong><small>{completePlan ? "已选择：保存后归档当前计划，并自动推进后续队列首项。" : "如本次已经完成计划，请勾选；不勾选则只记录本次工作结果。"}</small></span>
            <span className="plan-completion-state">{completePlan ? "已勾选" : "未勾选"}</span>
          </label>
        </section>
        <div className="simple-progress-actions"><span>{completePlan ? "保存后将完成并归档本计划" : "保存后仅记录本次工作结果"}</span><div className="form-actions"><button type="button" className="button ghost" onClick={close}>取消</button><button className="button primary" disabled={submitting || !detail.trim()}>{submitting ? "正在保存…" : "保存工作结果"}</button></div></div>
      </form>
    </Modal>
  );
}

function EditProgressRecordForm({ route, record, close, onDone, notify }: { route: RouteItem; record: ProgressRecord; close: () => void; onDone: (message: string) => void; notify: (tone: Notice["tone"], message: string) => void }) {
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState(() => record.actions.map((action) => ({ id: action.id, action: action.action, result: action.result })));
  const [completePlan, setCompletePlan] = useState(Boolean(record.completePlan));
  const linkedPlan = route.planTimeline?.find((plan) => plan.id === record.planId);
  const linkedPlanState = linkedPlan ? planStateOf(linkedPlan, route) : null;
  const canChangePlanState = Boolean(record.planId && (linkedPlanState === "current" || record.completePlan));
  const updateResult = (id: string, result: string) => setResults((current) => current.map((item) => item.id === id ? { ...item, result } : item));
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (results.some((item) => !item.result.trim())) return notify("error", "请填写本次工作的具体结果");
    setSubmitting(true);
    try {
      const response = await post<{ message: string }>(`/api/progress/${record.id}`, {
        results: results.map(({ id, result }) => ({ id, result: result.trim() })),
        completePlan: canChangePlanState ? completePlan : record.completePlan
      });
      onDone(response.message);
    } catch (error) { notify("error", error instanceof Error ? error.message : "保存修改失败"); }
    finally { setSubmitting(false); }
  };
  return (
    <Modal title="编辑工作结果" description={`${route.customer?.name} · ${formatDate(record.occurredAt)} · ${record.author}`} close={close} bottomSheet>
      <form className="progress-form result-edit-form" onSubmit={submit}>
        <section className="progress-form-section result-edit-section">
          <div className="edit-record-note"><ShieldCheck width={20} height={20} strokeWidth={1.6} aria-hidden /><span><strong>本次修改可追溯</strong><small>原始提交时间和记录人保持不变；保存前内容会进入修订历史，并同步写入审计日志。</small></span></div>
          <div className="progress-section-title"><div><span>01</span><strong>修改本次具体结果</strong></div><small>工作动作来自原计划，不可在结果编辑中新增、删除或更换。</small></div>
          <div className="result-edit-list">
            {results.map((item, index) => <label className="field result-edit-item" key={item.id}><span><small>{String(index + 1).padStart(2, "0")} · 原工作动作</small><strong>{item.action}</strong></span><textarea rows={4} required value={item.result} onChange={(event) => updateResult(item.id, event.target.value)} placeholder="填写本次工作达成了什么、确认了什么或当前存在什么问题" /></label>)}
          </div>
          {canChangePlanState && <label className={`plan-completion-choice result-completion-choice ${completePlan ? "is-complete" : ""}`}>
            <input type="checkbox" name="completePlan" checked={completePlan} onChange={(event) => setCompletePlan(event.target.checked)} />
            <span className="plan-completion-checkbox" aria-hidden>{completePlan && <Check width={17} height={17} strokeWidth={2.2} />}</span>
            <span className="plan-completion-copy"><strong>本次已完成计划</strong><small>{record.completePlan && !completePlan ? "保存后该计划恢复为当前计划，现有当前计划退回后续队列并重新自动排序。" : completePlan ? "保存后归档本计划，并自动推进后续队列首项。" : "未勾选时，本次结果保持进行中。"}</small></span>
            <span className="plan-completion-state">{completePlan ? "已完成" : "进行中"}</span>
          </label>}
        </section>
        <div className="progress-form-actions"><span>仅更新本次具体结果{canChangePlanState ? "与计划完成状态" : ""}，原内容仍可审计</span><div className="form-actions"><button type="button" className="button ghost" onClick={close}>取消</button><button className="button primary" disabled={submitting || results.some((item) => !item.result.trim())}>{submitting ? "正在保存…" : "保存修改"}</button></div></div>
      </form>
    </Modal>
  );
}

function DeleteProgressRecordConfirm({ route, record, close, onDone, notify }: { route: RouteItem; record: ProgressRecord; close: () => void; onDone: (message: string) => void; notify: (tone: Notice["tone"], message: string) => void }) {
  const [submitting, setSubmitting] = useState(false);
  const remove = async () => {
    setSubmitting(true);
    try {
      const response = await post<{ message: string }>(`/api/progress/${record.id}/delete`, {});
      onDone(response.message);
    } catch (error) { notify("error", error instanceof Error ? error.message : "删除失败"); }
    finally { setSubmitting(false); }
  };
  return (
    <Modal title="删除工作记录" description={`${route.customer?.name} · ${formatDate(record.occurredAt)} · ${record.author}`} close={close}>
      <section className="delete-record-confirm">
        <div className="delete-record-warning"><WarningTriangle width={24} height={24} strokeWidth={1.6} aria-hidden /><span><strong>确认从工作时间轴移除这批记录？</strong><p>删除后页面不再展示，但删除前的动作、结果、修订历史、操作人和时间仍会保存在审计快照中。</p></span></div>
        <div className="delete-record-summary"><span>将删除 {record.actions.length} 项工作</span>{record.actions.map((action, index) => <div key={action.id}><strong>{String(index + 1).padStart(2, "0")} · {action.action}</strong><small>{action.result}</small></div>)}</div>
        <div className="delete-record-actions"><button type="button" className="button ghost" onClick={close}>取消</button><button type="button" className="button danger-button" disabled={submitting} onClick={remove}><Trash width={17} height={17} strokeWidth={1.7} aria-hidden />{submitting ? "正在删除…" : "确认删除"}</button></div>
      </section>
    </Modal>
  );
}

function ChannelsPage({ state, user, refresh, notify }: { state: AppState; user: User; refresh: () => Promise<void>; notify: (tone: Notice["tone"], message: string) => void }) {
  const manager = user.role === "manager";
  const [form, setForm] = useState(false);
  const [associationChannelId, setAssociationChannelId] = useState<string | null>(null);
  const [selectedChannelId, setSelectedChannelId] = useState<string | null>(null);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const channels = state.channels ?? [];
  const filteredChannels = useMemo(() => {
    const normalized = query.trim().normalize("NFKC").toLocaleLowerCase("zh-CN").replace(/\s+/gu, "");
    if (!normalized) return channels;
    return channels.filter((channel) => [
      channel.name,
      channel.shortName,
      ...(channel.aliases ?? []),
      channel.region,
      channel.type,
      ...channel.contacts.flatMap((contact) => [contact.name, contact.phone]),
      ...(channel.accounts ?? []).map((account) => account.name)
    ].filter(Boolean).join(" ").normalize("NFKC").toLocaleLowerCase("zh-CN").replace(/\s+/gu, "").includes(normalized));
  }, [channels, query]);
  const channelTypes = useMemo(() => [...new Set(channels.map((channel) => channel.type).filter(Boolean))].sort((a, b) => a.localeCompare(b, "zh-CN")), [channels]);
  const selected = channels.find((channel) => channel.id === selectedChannelId) ?? null;
  const selectedCustomer = state.customers.find((customer) => customer.id === selectedCustomerId) ?? null;
  return <>
    <PageHeader eyebrow="CHANNEL LIBRARY" title={manager ? "渠道资源库" : "我的对接渠道"} description={manager ? "公司主档由管理组维护；删除采用可审计归档，历史业务记录不会被抹除。" : "查看已分配对接渠道的公司档案、账号团队、联系人、覆盖客户与通路；当前角色仅有查看权限。"}>{manager && <button className="button primary" onClick={() => setForm(true)}><PlusCircle {...iconProps} />新增渠道</button>}</PageHeader>
    <div className="large-list-search"><label className="search-field"><Search {...iconProps} /><span className="sr-only">搜索渠道资源</span><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="搜索渠道全称、简称、别名、区域、类型、联系人或成员" />{query && <button type="button" aria-label="清除搜索" onClick={() => setQuery("")}><Xmark width={16} height={16} strokeWidth={1.7} aria-hidden /></button>}</label><span>当前匹配 {filteredChannels.length} / {channels.length} 个渠道</span></div>
    {filteredChannels.length ? <div className="channel-grid">{filteredChannels.map((channel) => (
      <article className="channel-card" key={channel.id}>
        <header>
          <span className="channel-monogram">{channel.shortName.slice(0, 1)}</span>
          <div className="channel-card-copy"><h3>{channel.name}</h3><p>{channel.region || "区域待补全"} · {channel.type}</p></div>
          <span className={`channel-status-badge ${channel.active ? "is-active" : "is-inactive"}`}><CheckCircle width={15} height={15} strokeWidth={1.8} aria-hidden />{channel.active ? "启用" : "停用"}</span>
        </header>
        {channel.associationReviewStatus === "pending" && manager && <button className="association-pending-banner" onClick={() => setAssociationChannelId(channel.id)}><Spark width={16} height={16} strokeWidth={1.7} aria-hidden />待确认关联 {channel.pendingAssociationCount ?? 0} 项<NavArrowRight width={15} height={15} strokeWidth={1.7} aria-hidden /></button>}
        <div className="channel-stats">
          <span className="channel-stat channel-stat-customers"><Building width={19} height={19} strokeWidth={1.7} aria-hidden /><strong>{channel.customerCount}</strong><small>覆盖客户</small></span>
          <span className="channel-stat channel-stat-technical"><Settings width={19} height={19} strokeWidth={1.7} aria-hidden /><strong>{channel.technicalCount}</strong><small>技术口</small></span>
          <span className="channel-stat channel-stat-procurement"><ClipboardCheck width={19} height={19} strokeWidth={1.7} aria-hidden /><strong>{channel.procurementCount}</strong><small>采购口</small></span>
        </div>
        <footer><span>简称：{channel.shortName}</span><button className="button ghost" onClick={() => { setSelectedChannelId(channel.id); setSelectedCustomerId(null); }}>查看详情</button></footer>
      </article>
    ))}</div> : <EmptyState title="没有匹配的渠道" text="请尝试公司名称、别名、负责人姓名、电话、区域或渠道类型。" />}
    {manager && form && <ChannelForm channelTypes={channelTypes} close={() => setForm(false)} notify={notify} onCreated={async (channelId, message) => { await refresh(); setForm(false); setAssociationChannelId(channelId); notify("success", message); }} />}
    {selected && !selectedCustomer && <ChannelDrawer channel={selected} channelTypes={channelTypes} routes={state.routes.filter((route) => route.channel?.id === selected.id)} readOnly={!manager} close={() => setSelectedChannelId(null)} openCustomer={setSelectedCustomerId} openAssociationReview={() => setAssociationChannelId(selected.id)} refresh={refresh} notify={notify} />}
    {selectedCustomer && <CustomerDrawer customer={selectedCustomer} routes={state.routes.filter((route) => route.customer?.id === selectedCustomer.id)} channels={channels} teamMembers={state.teamMembers ?? []} workNatures={state.workNatures ?? []} user={user} close={() => setSelectedCustomerId(null)} onRouteAdded={refresh} notify={notify} returnLabel="返回渠道详情" />}
    {manager && associationChannelId && <ChannelAssociationReviewModal channelId={associationChannelId} close={() => setAssociationChannelId(null)} notify={notify} onDone={async (message) => { await refresh(); setAssociationChannelId(null); notify("success", message); }} />}
  </>;
}

function DownstreamChannelsPage({ state, user, refresh, notify }: { state: AppState; user: User; refresh: () => Promise<void>; notify: (tone: Notice["tone"], message: string) => void }) {
  const [items, setItems] = useState<DownstreamChannel[]>([]);
  const [query, setQuery] = useState("");
  const [mine, setMine] = useState(false);
  const [archived, setArchived] = useState(false);
  const [loading, setLoading] = useState(true);
  const [formTarget, setFormTarget] = useState<DownstreamChannel | "new" | null>(null);
  const [detailTarget, setDetailTarget] = useState<DownstreamChannel | null>(null);
  const [linkTarget, setLinkTarget] = useState<DownstreamChannel | null>(null);
  const owner = user.accountRole === "channel_owner";
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (query.trim()) params.set("q", query.trim());
      if (mine) params.set("mine", "true");
      if (archived) params.set("archived", "true");
      const response = await api<{ items: DownstreamChannel[] }>(`/api/downstream-channels?${params.toString()}`);
      setItems(response.items);
      setDetailTarget((current) => current ? response.items.find((item) => item.id === current.id) ?? null : null);
    } catch (error) {
      notify("error", error instanceof Error ? error.message : "下游渠道资源池加载失败");
    } finally {
      setLoading(false);
    }
  }, [archived, mine, notify, query]);
  useEffect(() => { void load(); }, [load]);
  const done = async (message: string) => {
    await Promise.all([load(), refresh()]);
    setFormTarget(null);
    setLinkTarget(null);
    notify("success", message);
  };
  const archive = async (item: DownstreamChannel) => {
    const action = item.active ? "归档" : "恢复";
    if (!window.confirm(item.active ? "确认归档该下游渠道档案？只有不存在有效通路关联时才能归档，档案可由渠道负责人恢复。" : "确认恢复该下游渠道档案？恢复后可重新分配和关联通路。")) return;
    try {
      const result = await post<{ message: string }>(`/api/downstream-channels/${item.id}/${item.active ? "archive" : "restore"}`, { version: item.version });
      await done(result.message);
    } catch (error) { notify("error", error instanceof Error ? error.message : `${action}失败`); }
  };
  return <>
    <PageHeader eyebrow="DOWNSTREAM CHANNEL POOL" title="下游渠道资源池" description="下游渠道是本渠道私有的业务档案，不创建登录账号，也不参与计划和工作结果权限判断。">{owner && !archived && <button className="button primary" onClick={() => setFormTarget("new")}><PlusCircle {...iconProps} />新建下游档案</button>}</PageHeader>
    <section className="downstream-pool-toolbar">
      <label className="search-field"><Search {...iconProps} /><span className="sr-only">搜索下游渠道</span><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="搜索公司、负责人、销售、档案管理人或关联客户" />{query && <button type="button" aria-label="清除搜索" onClick={() => setQuery("")}><Xmark width={16} height={16} strokeWidth={1.7} aria-hidden /></button>}</label>
      <div className="segmented-control" aria-label="档案分配筛选"><button type="button" className={!mine ? "active" : ""} onClick={() => setMine(false)}>全部档案</button><button type="button" className={mine ? "active" : ""} onClick={() => setMine(true)}>分配给我</button></div>
      {owner && <div className="segmented-control" aria-label="档案状态筛选"><button type="button" className={!archived ? "active" : ""} onClick={() => setArchived(false)}>启用</button><button type="button" className={archived ? "active" : ""} onClick={() => setArchived(true)}>已归档</button></div>}
      <span>{loading ? "正在加载…" : `匹配 ${items.length} 个档案`}</span>
    </section>
    {loading ? <div className="inline-loading">正在加载下游渠道资源池…</div> : items.length ? <div className="downstream-card-grid">{items.map((item) => <article className={`downstream-card ${item.active ? "" : "is-archived"}`} key={item.id}>
      <header><span className="channel-monogram">{item.companyName.slice(0, 1)}</span><div><h3>{item.companyName}</h3><p>{item.active ? "启用档案" : "已归档"} · 管理人 {item.assignedManager?.name ?? "待分配"}</p></div><Status value={item.active ? "启用" : "停用"} /></header>
      <div className="downstream-contact-summary"><div><span>负责人</span><strong>{item.responsibleContact.name || "待补充"}</strong><small>{item.responsibleContact.phone || "姓名与电话均未填写"}</small></div><div><span>销售联系人</span><strong>{item.salesContacts.length} 人</strong><small>{item.salesContacts.map((contact) => contact.name).join("、") || "暂未登记"}</small></div><div><span>关联客户通路</span><strong>{item.linkedRoutes.length} 条</strong><small>{[...new Set(item.linkedRoutes.map((route) => route.customerName))].join("、") || "尚未关联"}</small></div></div>
      <footer><button className="button ghost" onClick={() => setDetailTarget(item)}>查看详情</button>{item.active && item.canEdit && <button className="button secondary" onClick={() => setLinkTarget(item)}><ArrowRight {...iconProps} />关联客户通路</button>}{item.canEdit && item.active && <button className="button ghost" onClick={() => setFormTarget(item)}><EditPencil {...iconProps} />编辑</button>}{owner && <button className={`button ghost ${item.active ? "danger" : ""}`} disabled={item.active && !item.canArchive} title={item.active && !item.canArchive ? "请先解除全部有效通路关联" : undefined} onClick={() => archive(item)}>{item.active ? "归档" : "恢复"}</button>}</footer>
    </article>)}</div> : <EmptyState title={archived ? "没有已归档档案" : "下游渠道资源池为空"} text={owner ? "可从右上角新建下游渠道业务档案，再关联已经审批通过的客户通路。" : "请由渠道负责人先建立档案并分配管理人。"} />}
    {formTarget && <DownstreamChannelForm item={formTarget === "new" ? null : formTarget} teamMembers={state.teamMembers ?? []} user={user} close={() => setFormTarget(null)} notify={notify} onDone={done} />}
    {detailTarget && <DownstreamChannelDetail item={detailTarget} close={() => setDetailTarget(null)} edit={() => { setDetailTarget(null); setFormTarget(detailTarget); }} link={() => { setDetailTarget(null); setLinkTarget(detailTarget); }} />}
    {linkTarget && <DownstreamProfileRouteModal item={linkTarget} close={() => setLinkTarget(null)} notify={notify} onDone={done} />}
  </>;
}

function DownstreamChannelForm({ item, teamMembers, user, close, onDone, notify }: { item: DownstreamChannel | null; teamMembers: TeamMember[]; user: User; close: () => void; onDone: (message: string) => void; notify: (tone: Notice["tone"], message: string) => void }) {
  const [companyName, setCompanyName] = useState(item?.companyName ?? "");
  const [responsibleName, setResponsibleName] = useState(item?.responsibleContact.name ?? "");
  const [responsiblePhone, setResponsiblePhone] = useState(item?.responsibleContact.phone ?? "");
  const [assigneeId, setAssigneeId] = useState(item?.assignedManagerUserId ?? user.id);
  const [salesContacts, setSalesContacts] = useState(() => item?.salesContacts.map((contact) => ({ ...contact })) ?? []);
  const [submitting, setSubmitting] = useState(false);
  const owner = user.accountRole === "channel_owner";
  const addSales = () => setSalesContacts((current) => [...current, { id: `new_${Date.now()}`, name: "", phone: "", active: true }]);
  const updateSales = (index: number, field: "name" | "phone", value: string) => setSalesContacts((current) => current.map((contact, contactIndex) => contactIndex === index ? { ...contact, [field]: value } : contact));
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        companyName,
        responsibleContact: { id: item?.responsibleContact.id, name: responsibleName, phone: responsiblePhone },
        salesContacts: salesContacts.map((contact) => ({ ...contact, id: contact.id.startsWith("new_") ? undefined : contact.id })),
        assignedManagerUserId: assigneeId,
        ...(item ? { version: item.version } : {})
      };
      const result = await api<{ message: string; downstream: DownstreamChannel }>(item ? `/api/downstream-channels/${item.id}` : "/api/downstream-channels", { method: item ? "PATCH" : "POST", body: JSON.stringify(payload) });
      if (item && owner && assigneeId !== item.assignedManagerUserId) {
        const reassigned = await api<{ message: string }>(`/api/downstream-channels/${item.id}/assignee`, { method: "PATCH", body: JSON.stringify({ assignedManagerUserId: assigneeId, version: result.downstream.version }) });
        onDone(`${result.message}；${reassigned.message}`);
      } else {
        onDone(result.message);
      }
    } catch (error) {
      notify("error", error instanceof Error ? error.message : "下游渠道档案保存失败");
      setSubmitting(false);
    }
  };
  return <Modal title={item ? "编辑下游渠道档案" : "新建下游渠道档案"} description="下游渠道不生成账号；联系人只用于执行链记录，姓名与电话必须同时填写或同时留空。" close={close}>
    <form className="downstream-form" onSubmit={submit}>
      <label className="field"><span>公司全称 *</span><input required maxLength={160} value={companyName} onChange={(event) => setCompanyName(event.target.value)} /></label>
      <label className="field"><span>档案管理人 *</span><select required disabled={!owner} value={assigneeId} onChange={(event) => setAssigneeId(event.target.value)}>{teamMembers.filter((member) => member.active).map((member) => <option key={member.id} value={member.id}>{member.name} · {member.accountRole === "channel_owner" ? "渠道负责人" : "渠道销售"}</option>)}</select></label>
      <fieldset><legend>下游渠道负责人</legend><div className="contact-editor-row"><label className="field"><span>姓名</span><input value={responsibleName} onChange={(event) => setResponsibleName(event.target.value)} /></label><label className="field"><span>电话</span><input value={responsiblePhone} onChange={(event) => setResponsiblePhone(event.target.value)} /></label></div></fieldset>
      <fieldset><legend><span>销售人员信息</span><button type="button" className="button ghost" onClick={addSales}><PlusCircle width={16} height={16} strokeWidth={1.7} aria-hidden />增加销售</button></legend>{salesContacts.length ? <div className="contact-editor-list">{salesContacts.map((contact, index) => <div className="contact-editor-row" key={contact.id}><label className="field"><span>姓名</span><input value={contact.name} onChange={(event) => updateSales(index, "name", event.target.value)} /></label><label className="field"><span>电话</span><input value={contact.phone} onChange={(event) => updateSales(index, "phone", event.target.value)} /></label><button type="button" className="icon-button danger" aria-label={`移除销售联系人 ${contact.name || index + 1}`} onClick={() => setSalesContacts((current) => current.filter((_, itemIndex) => itemIndex !== index))}><Trash width={17} height={17} strokeWidth={1.7} aria-hidden /></button></div>)}</div> : <p className="field-help">暂无销售联系人，可按需增加。</p>}</fieldset>
      <div className="form-actions"><button type="button" className="button ghost" onClick={close}>取消</button><button className="button primary" disabled={submitting}>{submitting ? "正在保存…" : "保存档案"}</button></div>
    </form>
  </Modal>;
}

function DownstreamChannelDetail({ item, close, edit, link }: { item: DownstreamChannel; close: () => void; edit: () => void; link: () => void }) {
  return <Modal title={item.companyName} description={`档案管理人：${item.assignedManager?.name ?? "待分配"} · 版本 ${item.version}`} close={close}>
    <section className="downstream-detail-grid"><div><span>负责人</span><strong>{item.responsibleContact.name || "待补充"}</strong><small>{item.responsibleContact.phone || "未登记电话"}</small></div><div><span>销售联系人</span><strong>{item.salesContacts.length} 人</strong><small>{item.salesContacts.map((contact) => `${contact.name} · ${contact.phone}`).join("；") || "暂未登记"}</small></div></section>
    <section className="downstream-linked-routes"><header><div><p className="eyebrow">APPROVED ROUTES</p><h3>已关联客户通路</h3></div><span>{item.linkedRoutes.length} 条</span></header>{item.linkedRoutes.length ? item.linkedRoutes.map((route) => <article key={route.assignmentId}><div><strong>{route.customerName}</strong><small>{route.routeType} · 一级渠道负责人 {route.routeOwnerName}</small></div><div><strong>{route.followerName}</strong><small>下游跟进负责人</small></div></article>) : <EmptyState compact title="尚未关联通路" text="只能关联已通过现有申报审批的客户通路。" />}</section>
    <div className="form-actions downstream-detail-actions"><button className="button ghost" onClick={close}>关闭</button>{item.canEdit && <button className="button secondary" onClick={edit}><EditPencil {...iconProps} />编辑档案</button>}{item.active && item.canEdit && <button className="button primary" onClick={link}><ArrowRight {...iconProps} />关联客户通路</button>}</div>
  </Modal>;
}

function DownstreamProfileRouteModal({ item, close, onDone, notify }: { item: DownstreamChannel; close: () => void; onDone: (message: string) => void; notify: (tone: Notice["tone"], message: string) => void }) {
  const [routes, setRoutes] = useState<EligibleDownstreamRoute[]>([]);
  const [routeId, setRouteId] = useState("");
  const [followerId, setFollowerId] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => {
    api<{ items: EligibleDownstreamRoute[] }>(`/api/downstream-channels/${item.id}/eligible-routes`)
      .then((response) => setRoutes(response.items))
      .catch((error) => notify("error", error instanceof Error ? error.message : "可关联通路加载失败"))
      .finally(() => setLoading(false));
  }, [item.id, notify]);
  const selectedRoute = routes.find((route) => route.id === routeId);
  const contacts = [item.responsibleContact, ...item.salesContacts].filter((contact) => contact.active !== false && contact.name && contact.phone);
  const save = async () => {
    if (!selectedRoute || !followerId) return;
    setSubmitting(true);
    try {
      const result = await api<{ message: string }>(`/api/routes/${selectedRoute.id}/downstream-assignment`, { method: "PUT", body: JSON.stringify({ downstreamChannelId: item.id, followerContactId: followerId, version: selectedRoute.currentAssignment?.version ?? 0 }) });
      onDone(result.message);
    } catch (error) { notify("error", error instanceof Error ? error.message : "客户通路关联失败"); setSubmitting(false); }
  };
  return <Modal title="关联已获批客户通路" description={item.companyName} close={close}>
    {loading ? <div className="inline-loading">正在加载可关联通路…</div> : <div className="form-grid downstream-assignment-form"><label className="field full"><span>客户通路 *</span><select value={routeId} onChange={(event) => setRouteId(event.target.value)}><option value="">请选择已获批且有维护权限的通路</option>{routes.map((route) => <option key={route.id} value={route.id}>{route.customer.name} · {route.type} · {route.owner?.name ?? "待分配"}{route.currentAssignment ? ` · 当前 ${route.currentAssignment.downstreamCompanyName}` : ""}</option>)}</select></label><label className="field full"><span>下游渠道跟进负责人 *</span><select value={followerId} onChange={(event) => setFollowerId(event.target.value)}><option value="">请选择负责人或销售联系人</option>{contacts.map((contact) => <option key={contact.id} value={contact.id}>{contact.name} · {contact.phone}</option>)}</select></label>{selectedRoute?.currentAssignment && selectedRoute.currentAssignment.downstreamChannelId !== item.id && <p className="replacement-warning full"><WarningTriangle width={17} height={17} strokeWidth={1.7} aria-hidden />保存后会替换该通路当前的下游渠道关联，但不会改动计划和结果。</p>}</div>}
    {!loading && !routes.length && <EmptyState compact title="暂无可关联通路" text="新关联必须基于已经通过现有申报审批、且由当前账号负责的客户通路。" />}
    <div className="form-actions downstream-detail-actions"><button type="button" className="button ghost" onClick={close}>取消</button><button type="button" className="button primary" disabled={loading || submitting || !routeId || !followerId} onClick={save}>{submitting ? "正在关联…" : "确认关联"}</button></div>
  </Modal>;
}

type ChannelAccount = NonNullable<Channel["accounts"]>[number];

function ChannelDrawer({ channel, channelTypes, routes, readOnly = false, close, openCustomer, openAssociationReview, refresh, notify }: { channel: Channel; channelTypes: string[]; routes: RouteItem[]; readOnly?: boolean; close: () => void; openCustomer: (customerId: string) => void; openAssociationReview: () => void; refresh: () => Promise<void>; notify: (tone: Notice["tone"], message: string) => void }) {
  const dialog = useRef<HTMLDivElement>(null);
  const [accountForm, setAccountForm] = useState<"channel_owner" | "channel_sales" | null>(null);
  const [passwordTarget, setPasswordTarget] = useState<ChannelAccount | null>(null);
  const [deleteAccountTarget, setDeleteAccountTarget] = useState<ChannelAccount | null>(null);
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [unlinkingRelation, setUnlinkingRelation] = useState<RelationUnlinkTarget | null>(null);
  const accounts = channel.accounts ?? [];
  const owners = accounts.filter((account) => account.accountRole === "channel_owner");
  const ownerById = new Map(owners.map((account) => [account.id, account]));
  const primaryContact = channel.contacts.find((contact) => contact.primary && contact.active !== false) ?? channel.contacts.find((contact) => contact.active !== false);
  useEffect(() => {
    dialog.current?.focus();
    const handler = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      if (accountForm) setAccountForm(null);
      else if (passwordTarget) setPasswordTarget(null);
      else if (deleteAccountTarget) setDeleteAccountTarget(null);
      else if (unlinkingRelation) setUnlinkingRelation(null);
      else if (editing) setEditing(false);
      else if (deleting) setDeleting(false);
      else close();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [accountForm, close, deleteAccountTarget, deleting, editing, passwordTarget, unlinkingRelation]);
  const customers = useMemo(() => {
    const unique = new Map<string, { id: string; relationId: string; name: string; region: string; types: string[]; status: string; canUnlink: boolean; routes: RouteItem[] }>();
    routes.forEach((route) => {
      if (!route.customer) return;
      const current = unique.get(route.customer.id);
      if (current) {
        current.types.push(route.type);
        current.routes.push(route);
        current.canUnlink ||= Boolean(route.canUnlinkRelation);
      } else unique.set(route.customer.id, { id: route.customer.id, relationId: route.relationId ?? "", name: route.customer.name, region: route.customer.region, types: [route.type], status: route.relationStatus, canUnlink: Boolean(route.canUnlinkRelation), routes: [route] });
    });
    return [...unique.values()];
  }, [routes]);
  const unlinkTargetFor = (customer: (typeof customers)[number]): RelationUnlinkTarget => ({
    relationId: customer.relationId,
    customerName: customer.name,
    channelName: channel.name,
    routeCount: customer.routes.length,
    routeTypes: [...new Set(customer.types)],
    planCount: customer.routes.reduce((total, route) => total + (route.planTimeline?.length ?? 0), 0),
    resultCount: customer.routes.reduce((total, route) => total + (route.progressHistory?.length ?? 0), 0),
    downstreamCount: customer.routes.filter((route) => route.downstreamChannel).length
  });
  const changeAccountStatus = async (account: ChannelAccount) => {
    try {
      const response = await post<{ message: string }>(`/api/admin/users/${account.id}/status`, { active: !account.active });
      await refresh();
      notify("success", response.message);
    } catch (error) {
      notify("error", error instanceof Error ? error.message : "账号状态更新失败");
    }
  };
  return (
    <>
    <div className="drawer-layer" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && close()}>
      <div className="drawer channel-drawer" role="dialog" aria-modal="true" aria-labelledby="channel-detail-title" tabIndex={-1} ref={dialog}>
        <div className="drawer-head"><div><p className="eyebrow">CHANNEL PROFILE</p><h2 id="channel-detail-title">{channel.name}</h2><p>{channel.region || "区域待补充"} · {channel.type} · 最近更新 {formatDate(channel.updatedAt)}</p></div><div className="drawer-head-actions">{!readOnly && <button className="button secondary" onClick={() => setEditing(true)}><EditPencil {...iconProps} />编辑渠道资料</button>}<button className="icon-button" aria-label="关闭渠道详情" onClick={close}><Xmark {...iconProps} /></button></div></div>
        <section className="channel-detail-stats"><div><strong>{channel.customerCount}</strong><span>覆盖客户</span></div><div className="technical"><strong>{channel.technicalCount}</strong><span>技术口</span></div><div className="purchase"><strong>{channel.procurementCount}</strong><span>采购口</span></div></section>
        {channel.associationReviewStatus === "pending" && !readOnly && <button className="association-review-callout" onClick={openAssociationReview}><span><Spark width={22} height={22} strokeWidth={1.6} aria-hidden /><span><strong>历史客户关联尚待确认</strong><small>系统已生成 {channel.pendingAssociationCount ?? 0} 项规则 / AI 候选，确认前不会创建客户—渠道关系。</small></span></span><span>继续确认<NavArrowRight width={18} height={18} strokeWidth={1.7} aria-hidden /></span></button>}
        <section className="channel-profile-grid"><div><span>渠道简称</span><strong>{channel.shortName}</strong></div><div><span>渠道类型</span><strong>{channel.type}</strong></div><div><span>主要负责人</span><strong>{primaryContact?.name || "未填写"}</strong></div><div><span>联系电话</span><strong>{primaryContact?.phone || "未填写"}</strong></div><div><span>启用状态</span><Status value={channel.active ? "启用" : "停用"} /></div><div><span>下游渠道资源池</span><Status value={channel.capabilities?.responsibleDownstreamChannel ? "已开放" : "未开放"} /></div><div><span>渠道备注</span><strong>{channel.note || "暂无"}</strong></div></section>
        <section className="drawer-section channel-account-section">
          <header><div><p className="eyebrow">CHANNEL ACCOUNTS</p><h3>渠道负责人及销售账号</h3></div><div className="drawer-section-actions"><span>{accounts.length} 个账号</span>{!readOnly && <><button className="button secondary" onClick={() => setAccountForm("channel_owner")}><PlusCircle width={16} height={16} strokeWidth={1.7} aria-hidden />新建负责人</button><button className="button secondary" disabled={!owners.length} onClick={() => setAccountForm("channel_sales")}><PlusCircle width={16} height={16} strokeWidth={1.7} aria-hidden />新建销售</button></>}</div></header>
          {accounts.length ? <div className="admin-account-list">{accounts.map((account) => <article key={account.id}><span className={`team-avatar ${account.accountRole === "channel_owner" ? "owner" : ""}`}>{account.name.slice(0, 1)}</span><div><strong>{account.name}<em>{account.accountRole === "channel_owner" ? "渠道负责人" : "渠道销售"}</em></strong><small>{account.title} · 登录账号 {account.login}</small><small>{account.contactInfo || "联系方式未填写"} · {account.parentUserId ? `由 ${ownerById.get(account.parentUserId)?.name ?? "渠道负责人"} 创建` : "渠道负责人账号"}</small></div><div className="admin-account-actions"><Status value={account.active ? "启用" : "停用"} />{!readOnly && <><button className="button ghost" onClick={() => setPasswordTarget(account)}>重置密码</button><button className={`button ghost ${account.active ? "danger" : ""}`} onClick={() => changeAccountStatus(account)}>{account.active ? "停用" : "启用"}</button><button className="button ghost danger" onClick={() => setDeleteAccountTarget(account)}><Trash width={16} height={16} strokeWidth={1.7} aria-hidden />删除</button></>}</div></article>)}</div> : <EmptyState compact title="渠道档案已建立，尚未配置负责人" text="可建立多名渠道负责人，再配置销售账号。" />}
          {readOnly && <p className="security-hint"><ShieldCheck width={16} height={16} strokeWidth={1.6} aria-hidden />商业组销售仅可查看账号归属和联系方式，不能创建、停用或重置账号。</p>}
        </section>
        <section className="drawer-section">
          <header><div><p className="eyebrow">CONTACTS</p><h3>渠道联系人</h3></div><span>{channel.contacts.length} 人</span></header>
          {channel.contacts.length ? <div className="contact-list">{channel.contacts.map((contact) => <article key={contact.id}><span className="contact-avatar">{contact.name.slice(0, 1)}</span><div><strong>{contact.name}{contact.primary && <em>主要联系人</em>}</strong><small>{contact.title || "职务待补充"} · {contact.type}</small></div><div><span>{contact.phone || "未填写手机"}</span><small>{contact.email || "未填写邮箱"}</small></div></article>)}</div> : <EmptyState compact title="暂无渠道联系人" text="可在渠道资料中补充联系人。" />}
        </section>
        <section className="drawer-section">
          <header><div><p className="eyebrow">CUSTOMER COVERAGE</p><h3>覆盖客户与通路</h3></div><span>{customers.length} 家</span></header>
          {customers.length ? <div className="coverage-list">{customers.map((customer) => <article className="coverage-customer-row" key={customer.id}><button className="coverage-customer-main" aria-label={`查看${customer.name}客户详情`} onClick={() => openCustomer(customer.id)}><div><strong>{customer.name}</strong><small>{customer.region} · {customer.status}</small></div><div className="coverage-route-action"><span className="route-tags">{[...new Set(customer.types)].map((type) => <RouteTag key={type} type={type as "技术口" | "采购口"} />)}</span><NavArrowRight {...iconProps} /></div></button>{customer.canUnlink && customer.relationId && <button type="button" className="relation-unlink-button compact" aria-label={`取消${channel.name}与${customer.name}的关联`} title="取消客户渠道关联" onClick={() => setUnlinkingRelation(unlinkTargetFor(customer))}><Xmark width={17} height={17} strokeWidth={2} aria-hidden /><span>取消关联</span></button>}</article>)}</div> : <EmptyState compact title="暂无覆盖客户" text="新建关系或审批通过后会自动出现在这里。" />}
        </section>
        {!readOnly && <section className="drawer-section channel-danger-zone">
          <div><p className="eyebrow">DANGER ZONE</p><h3>删除渠道档案</h3><p>删除采用可审计归档：渠道账号、客户关系、通路和计划会停用，历史记录不会被物理抹除。</p></div>
          <button className="button danger-outline" onClick={() => setDeleting(true)}><Trash width={17} height={17} strokeWidth={1.7} aria-hidden />删除渠道</button>
        </section>}
      </div>
    </div>
    {!readOnly && accountForm && <AdminChannelUserForm channel={channel} accountRole={accountForm} close={() => setAccountForm(null)} notify={notify} onDone={async (message) => { await refresh(); setAccountForm(null); notify("success", message); }} />}
    {!readOnly && passwordTarget && <PasswordForm user={{ id: passwordTarget.id, name: passwordTarget.name, role: "channel", accountRole: passwordTarget.accountRole, title: passwordTarget.title }} administratorReset close={() => setPasswordTarget(null)} notify={notify} onDone={async (message) => { await refresh(); setPasswordTarget(null); notify("success", message); }} />}
    {!readOnly && deleteAccountTarget && <DeleteChannelAccountConfirm account={deleteAccountTarget} close={() => setDeleteAccountTarget(null)} notify={notify} onDone={async (message) => { await refresh(); setDeleteAccountTarget(null); notify("success", message); }} />}
    {unlinkingRelation && <UnlinkRelationConfirm target={unlinkingRelation} close={() => setUnlinkingRelation(null)} notify={notify} onDone={async (message) => { await refresh(); setUnlinkingRelation(null); notify("success", message); }} />}
    {!readOnly && editing && <ChannelEditForm channel={channel} channelTypes={channelTypes} close={() => setEditing(false)} notify={notify} onDone={async (message) => { await refresh(); setEditing(false); notify("success", message); }} />}
    {!readOnly && deleting && <DeleteChannelConfirm channel={channel} close={() => setDeleting(false)} notify={notify} onDone={async (message) => { await refresh(); setDeleting(false); close(); notify("success", message); }} />}
    </>
  );
}

function ChannelTypeField({ channelTypes, initialValue = "" }: { channelTypes: string[]; initialValue?: string }) {
  const options = useMemo(() => [...new Set([...channelTypes, initialValue].filter(Boolean))].sort((a, b) => a.localeCompare(b, "zh-CN")), [channelTypes, initialValue]);
  const [selection, setSelection] = useState(initialValue || "");
  const [customValue, setCustomValue] = useState("");
  const isNew = selection === "__new__";
  return (
    <div className="field channel-type-field">
      <label htmlFor="channel-type-select">渠道类型 *</label>
      <select id="channel-type-select" value={selection} onChange={(event) => { setSelection(event.target.value); if (event.target.value !== "__new__") setCustomValue(""); }} required>
        <option value="" disabled>选择已有类型</option>
        {options.map((type) => <option value={type} key={type}>{type}</option>)}
        <option value="__new__">＋ 新建类型</option>
      </select>
      {isNew
        ? <input name="type" value={customValue} onChange={(event) => setCustomValue(event.target.value)} maxLength={40} placeholder="输入新的渠道类型名称" aria-label="新渠道类型名称" required autoFocus />
        : <input type="hidden" name="type" value={selection} />}
      <small>{isNew ? "新类型保存后会进入后续渠道的可选列表。" : "可复用现有类型，或在下拉列表底部直接新建。"}</small>
    </div>
  );
}

function ChannelEditForm({ channel, channelTypes, close, onDone, notify }: { channel: Channel; channelTypes: string[]; close: () => void; onDone: (message: string) => void; notify: (tone: Notice["tone"], message: string) => void }) {
  const [submitting, setSubmitting] = useState(false);
  const primaryContact = channel.contacts.find((contact) => contact.primary && contact.active !== false) ?? channel.contacts.find((contact) => contact.active !== false);
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    try {
      const formData = new FormData(event.currentTarget);
      const result = await post<{ message: string }>(`/api/channels/${channel.id}/update`, {
        ...Object.fromEntries(formData),
        responsibleDownstreamChannel: formData.get("responsibleDownstreamChannel") === "on"
      });
      onDone(result.message);
    } catch (error) {
      notify("error", error instanceof Error ? error.message : "渠道资料保存失败");
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <Modal title="编辑渠道资料" description="管理员修改后立即生效；全称、简称变化会同步触发历史待对齐渠道重新研判。" close={close}>
      <form className="form-grid" onSubmit={submit}>
        <Field label="渠道全称" name="name" required defaultValue={channel.name} />
        <Field label="渠道简称" name="shortName" required defaultValue={channel.shortName} />
        <Field label="所属区域" name="region" defaultValue={channel.region} />
        <ChannelTypeField channelTypes={channelTypes} initialValue={channel.type} />
        <Field label="联系人（负责人）" name="contactName" defaultValue={primaryContact?.name ?? ""} placeholder="选填，渠道主要负责人姓名" />
        <Field label="联系电话" name="contactPhone" defaultValue={primaryContact?.phone ?? ""} placeholder="选填，手机或座机" />
        <label className="capability-toggle full"><input type="checkbox" name="responsibleDownstreamChannel" defaultChecked={Boolean(channel.capabilities?.responsibleDownstreamChannel)} /><span><strong>开放下游渠道资源池</strong><small>开放后，渠道侧可维护私有下游档案并关联已获批通路；存在有效关联时不能关闭。</small></span></label>
        <label className="field full"><span>渠道备注</span><textarea name="note" rows={4} defaultValue={channel.note} placeholder="选填，仅用于管理组维护渠道档案" /></label>
        <div className="form-actions full"><button type="button" className="button ghost" onClick={close}>取消</button><button className="button primary" disabled={submitting}>{submitting ? "正在保存…" : "保存渠道资料"}</button></div>
      </form>
    </Modal>
  );
}

function DeleteChannelConfirm({ channel, close, onDone, notify }: { channel: Channel; close: () => void; onDone: (message: string) => void; notify: (tone: Notice["tone"], message: string) => void }) {
  const [seconds, setSeconds] = useState(5);
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => {
    if (seconds <= 0) return;
    const timer = window.setTimeout(() => setSeconds((value) => Math.max(0, value - 1)), 1000);
    return () => window.clearTimeout(timer);
  }, [seconds]);
  const remove = async () => {
    if (seconds > 0 || submitting) return;
    setSubmitting(true);
    try {
      const result = await post<{ message: string }>(`/api/channels/${channel.id}/delete`, {});
      onDone(result.message);
    } catch (error) {
      notify("error", error instanceof Error ? error.message : "渠道删除失败");
      setSubmitting(false);
    }
  };
  return (
    <Modal title="二次确认删除渠道" description={channel.name} close={close}>
      <section className="delete-record-confirm">
        <div className="delete-record-warning"><WarningTriangle width={24} height={24} strokeWidth={1.6} aria-hidden /><span><strong>该操作会立即停用整个渠道</strong><p>负责人和销售账号将无法登录，客户关系、通路与当前计划会停止推进。历史申报、进展和审计记录仍会保留。</p></span></div>
        <div className="channel-delete-impact">
          <div><strong>{channel.accounts?.length ?? 0}</strong><span>个账号将停用</span></div>
          <div><strong>{channel.customerCount}</strong><span>家覆盖客户受影响</span></div>
          <div><strong>{channel.technicalCount + channel.procurementCount}</strong><span>条通路将归档</span></div>
        </div>
        <p className="countdown-note"><Clock width={18} height={18} strokeWidth={1.7} aria-hidden />{seconds > 0 ? `为避免误操作，请等待 ${seconds} 秒后进行第二次确认。` : "等待结束，请再次确认是否删除该渠道。"}</p>
        <div className="delete-record-actions"><button type="button" className="button ghost" onClick={close}>取消</button><button type="button" className="button danger-button" disabled={seconds > 0 || submitting} onClick={remove}><Trash width={17} height={17} strokeWidth={1.7} aria-hidden />{submitting ? "正在归档删除…" : seconds > 0 ? `请等待 ${seconds} 秒` : "二次确认：删除渠道"}</button></div>
      </section>
    </Modal>
  );
}

function ChannelForm({ channelTypes, close, onCreated, notify }: { channelTypes: string[]; close: () => void; onCreated: (channelId: string, message: string) => void; notify: (tone: Notice["tone"], message: string) => void }) {
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    try {
      const formData = new FormData(event.currentTarget);
      const result = await post<{ channel: { id: string }; duplicate: unknown[]; associationReview: { items: unknown[] } }>("/api/channels", {
        ...Object.fromEntries(formData),
        responsibleDownstreamChannel: formData.get("responsibleDownstreamChannel") === "on"
      });
      onCreated(result.channel.id, result.associationReview.items.length ? `渠道档案已建立，发现 ${result.associationReview.items.length} 项历史客户关联候选，请继续确认。` : "渠道档案已建立，当前没有历史客户关联候选。");
    } catch (error) { notify("error", error instanceof Error ? error.message : "保存失败"); }
  };
  return <Modal title="新增渠道 · 第 1 步" description="先建立渠道公司档案；保存后进入历史客户关联确认，确认前不会创建任何客户关系。" close={close}><form className="form-grid" onSubmit={submit}><Field label="渠道全称" name="name" required /><Field label="渠道简称" name="shortName" required /><Field label="所属区域" name="region" /><ChannelTypeField channelTypes={channelTypes} /><Field label="联系人（负责人）" name="contactName" placeholder="选填，可后续补充" /><Field label="联系电话" name="contactPhone" placeholder="选填，手机或座机" /><label className="capability-toggle full"><input type="checkbox" name="responsibleDownstreamChannel" /><span><strong>开放下游渠道资源池</strong><small>开放后，该渠道可维护私有下游档案并关联已获批客户通路。</small></span></label><div className="form-actions full"><button type="button" className="button ghost" onClick={close}>取消</button><button className="button primary">保存档案并查看关联候选</button></div></form></Modal>;
}

type AssociationReviewResponse = {
  channel: { id: string; name: string; shortName: string };
  review: {
    id: string;
    status: "pending" | "completed";
    items: {
      id: string;
      customerId: string;
      customerName: string;
      region: string;
      rawName: string;
      routeType: "技术口" | "采购口";
      sourceBatchId: string;
      sourceRow: number;
      score: number;
      ruleScore?: number;
      ruleBasis?: string;
      basis: string;
      source: "规则匹配" | "规则候选" | "AI建议";
      defaultSelected: boolean;
      aiStatus?: string | null;
      aiScore?: number | null;
      aiReason?: string;
      selected?: boolean;
    }[];
    summary?: { selected: number; excluded: number; createdRelations: number; createdRoutes: number };
  } | null;
  ai: { enabled: boolean; configured: boolean; model: string; autoThreshold: number; manualThreshold: number };
};

type AssociationReview = NonNullable<AssociationReviewResponse["review"]>;
type AssociationReviewItem = AssociationReview["items"][number];
type AssociationReferenceGroup = {
  key: string;
  rawName: string;
  score: number;
  source: AssociationReviewItem["source"];
  basis: string;
  items: AssociationReviewItem[];
};

function AssociationReferenceCard({
  group,
  selectedIds,
  toggleItem,
  toggleGroup
}: {
  group: AssociationReferenceGroup;
  selectedIds: Set<string>;
  toggleItem: (id: string) => void;
  toggleGroup: (group: AssociationReferenceGroup, selected: boolean) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [page, setPage] = useState(1);
  const groupCheckbox = useRef<HTMLInputElement>(null);
  const pageSize = 8;
  const selectedCount = group.items.filter((item) => selectedIds.has(item.id)).length;
  const allSelected = selectedCount === group.items.length;
  const pageCount = Math.max(1, Math.ceil(group.items.length / pageSize));
  const visibleItems = group.items.slice((page - 1) * pageSize, page * pageSize);
  const routeCounts = group.items.reduce<Record<string, number>>((counts, item) => {
    counts[item.routeType] = (counts[item.routeType] ?? 0) + 1;
    return counts;
  }, {});
  useEffect(() => {
    if (groupCheckbox.current) groupCheckbox.current.indeterminate = selectedCount > 0 && !allSelected;
  }, [allSelected, selectedCount]);
  return (
    <article className={`association-reference-card ${selectedCount ? "selected" : ""}`}>
      <div className="association-reference-summary">
        <input
          ref={groupCheckbox}
          type="checkbox"
          checked={allSelected}
          aria-label={`选择原始渠道文本 ${group.rawName} 的全部客户`}
          onChange={(event) => toggleGroup(group, event.currentTarget.checked)}
        />
        <div>
          <small>原始渠道文本</small>
          <strong>{group.rawName}</strong>
          <p>{group.basis}</p>
        </div>
        <div className="association-reference-evidence">
          <span className={`ai-source-tag ${group.source !== "AI建议" ? "rule" : ""}`}>{group.source}</span>
          <strong>{group.score} 分</strong>
          <small>{routeCounts["技术口"] ? `技术口 ${routeCounts["技术口"]}` : ""}{routeCounts["技术口"] && routeCounts["采购口"] ? " · " : ""}{routeCounts["采购口"] ? `采购口 ${routeCounts["采购口"]}` : ""}</small>
        </div>
        <div className="association-reference-actions">
          <span>{selectedCount} / {group.items.length} 家已选</span>
          <button type="button" className="button ghost compact" onClick={() => setExpanded((value) => !value)}>{expanded ? "收起客户" : "查看并调整客户"}</button>
        </div>
      </div>
      {expanded && <div className="association-reference-details">
        <header><span>关联客户</span><small>按客户逐项取消不会影响同组其他客户。</small></header>
        {visibleItems.map((item) => <label className="association-customer-row" key={item.id}>
          <input type="checkbox" checked={selectedIds.has(item.id)} onChange={() => toggleItem(item.id)} />
          <span><strong>{item.customerName}</strong><small>{item.region || "区域待补充"} · 来源行 {item.sourceRow}</small></span>
          <RouteTag type={item.routeType} />
        </label>)}
        {pageCount > 1 && <div className="association-pagination">
          <button type="button" className="button ghost compact" disabled={page === 1} onClick={() => setPage((value) => Math.max(1, value - 1))}>上一页</button>
          <span>第 {page} / {pageCount} 页</span>
          <button type="button" className="button ghost compact" disabled={page === pageCount} onClick={() => setPage((value) => Math.min(pageCount, value + 1))}>下一页</button>
        </div>}
      </div>}
    </article>
  );
}

function ChannelAssociationReviewModal({ channelId, close, onDone, notify }: { channelId: string; close: () => void; onDone: (message: string) => void; notify: (tone: Notice["tone"], message: string) => void }) {
  const [data, setData] = useState<AssociationReviewResponse | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [groupFilter, setGroupFilter] = useState<"all" | "high" | "manual">("all");
  const [groupQuery, setGroupQuery] = useState("");
  const [groupPage, setGroupPage] = useState(1);
  useEffect(() => {
    let active = true;
    post<AssociationReviewResponse>(`/api/channels/${channelId}/association-review/refresh`, {})
      .then((value) => {
        if (!active) return;
        setData(value);
        setSelectedIds(new Set((value.review?.items ?? []).filter((item) => item.defaultSelected || item.selected).map((item) => item.id)));
      })
      .catch((error) => notify("error", error instanceof Error ? error.message : "关联候选加载失败"))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [channelId, notify]);
  const items = data?.review?.items ?? [];
  const autoThreshold = data?.ai.autoThreshold ?? 95;
  const referenceGroups = useMemo(() => {
    const grouped = new Map<string, AssociationReferenceGroup>();
    items.forEach((item) => {
      const key = [item.rawName, item.source, item.score, item.basis].join("\u0000");
      const current = grouped.get(key);
      if (current) current.items.push(item);
      else grouped.set(key, { key, rawName: item.rawName, score: item.score, source: item.source, basis: item.basis, items: [item] });
    });
    return [...grouped.values()].sort((left, right) => right.score - left.score || right.items.length - left.items.length || left.rawName.localeCompare(right.rawName, "zh-CN"));
  }, [items]);
  const normalizedGroupQuery = groupQuery.trim().toLocaleLowerCase("zh-CN");
  const filteredGroups = referenceGroups.filter((group) => {
    if (groupFilter === "high" && group.score < autoThreshold) return false;
    if (groupFilter === "manual" && group.score >= autoThreshold) return false;
    if (!normalizedGroupQuery) return true;
    return group.rawName.toLocaleLowerCase("zh-CN").includes(normalizedGroupQuery)
      || group.items.some((item) => item.customerName.toLocaleLowerCase("zh-CN").includes(normalizedGroupQuery));
  });
  const groupPageSize = 6;
  const groupPageCount = Math.max(1, Math.ceil(filteredGroups.length / groupPageSize));
  const visibleGroups = filteredGroups.slice((groupPage - 1) * groupPageSize, groupPage * groupPageSize);
  const highGroupCount = referenceGroups.filter((group) => group.score >= autoThreshold).length;
  const manualGroupCount = referenceGroups.length - highGroupCount;
  const toggle = (id: string) => setSelectedIds((current) => {
    const next = new Set(current);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  });
  const toggleGroup = (group: AssociationReferenceGroup, selected: boolean) => setSelectedIds((current) => {
    const next = new Set(current);
    group.items.forEach((item) => selected ? next.add(item.id) : next.delete(item.id));
    return next;
  });
  const confirm = async () => {
    if (!data?.review || data.review.status !== "pending") return onDone("该渠道当前没有待确认关联。");
    setSubmitting(true);
    try {
      const response = await post<{ message: string; createdRelations: number; createdRoutes: number }>(`/api/channels/${channelId}/association-review`, { selectedItemIds: [...selectedIds] });
      onDone(`${response.message}：新增 ${response.createdRelations} 条关系、${response.createdRoutes} 条通路。`);
    } catch (error) {
      notify("error", error instanceof Error ? error.message : "关联确认保存失败");
      setSubmitting(false);
    }
  };
  return (
    <Modal title="新增渠道 · 第 2 步" description={data ? `${data.channel.name} · 历史客户关联确认` : "正在加载历史关联候选"} close={close} bottomSheet>
      {loading ? <div className="association-loading">正在汇总规则匹配与 AI 建议…</div> : data ? <div className="association-review-workbench">
        <div className="association-ai-status"><Spark width={22} height={22} strokeWidth={1.6} aria-hidden /><div><strong>渠道匹配研判</strong><p>规则匹配 / 规则候选 / AI建议 / 人工确认均保留审计轨迹。AI 服务：{!data.ai.configured ? "未配置，使用规则降级" : data.ai.enabled ? `已启用（${data.ai.model}）` : "密钥已配置，但辅助匹配尚未启用"}；自动阈值 {data.ai.autoThreshold} 分。</p><small>同一原始渠道文本只研判一次并复用于关联客户，所以相同文本的得分一致；每个客户仍可单独勾选或取消。</small></div><span>{selectedIds.size} / {items.length} 已选择</span></div>
        {data.review?.status === "completed" && !items.length ? <EmptyState title="没有发现历史客户关联候选" text="渠道档案已正常建立；未来仍可通过申报、手工通路或待对齐中心建立关系。" /> : <>
          <div className="association-bulk-actions"><button className="button ghost" onClick={() => setSelectedIds(new Set(items.map((item) => item.id)))}>全部选择</button><button className="button ghost" onClick={() => setSelectedIds(new Set())}>全部取消</button><span>高置信候选默认全选，中置信候选默认不选，可逐项调整。</span></div>
          <section className="association-reference-toolbar">
            <label><Search width={17} height={17} strokeWidth={1.7} aria-hidden /><input value={groupQuery} onChange={(event) => { setGroupQuery(event.target.value); setGroupPage(1); }} placeholder="搜索原始渠道文本或关联客户" /></label>
            <div className="segmented">
              <button type="button" className={groupFilter === "all" ? "active" : ""} onClick={() => { setGroupFilter("all"); setGroupPage(1); }}>全部文本 {referenceGroups.length}</button>
              <button type="button" className={groupFilter === "high" ? "active" : ""} onClick={() => { setGroupFilter("high"); setGroupPage(1); }}>高置信 {highGroupCount}</button>
              <button type="button" className={groupFilter === "manual" ? "active" : ""} onClick={() => { setGroupFilter("manual"); setGroupPage(1); }}>待判断 {manualGroupCount}</button>
            </div>
          </section>
          <section className="association-reference-list">
            <header><div><h3>按原始渠道文本分组</h3><p>先判断一个来源文本，再按需展开并取消个别客户；不会因长名单影响页面操作。</p></div><span>{filteredGroups.length} 组 · {filteredGroups.reduce((count, group) => count + group.items.length, 0)} 家客户</span></header>
            {visibleGroups.map((group) => <AssociationReferenceCard key={group.key} group={group} selectedIds={selectedIds} toggleItem={toggle} toggleGroup={toggleGroup} />)}
            {!visibleGroups.length && <EmptyState compact title="当前筛选没有候选" text="调整搜索或筛选条件后重试；未达阈值的来源仍保留在待对齐池。" />}
            {groupPageCount > 1 && <div className="association-pagination">
              <button type="button" className="button ghost compact" disabled={groupPage === 1} onClick={() => setGroupPage((value) => Math.max(1, value - 1))}>上一页</button>
              <span>第 {groupPage} / {groupPageCount} 页</span>
              <button type="button" className="button ghost compact" disabled={groupPage === groupPageCount} onClick={() => setGroupPage((value) => Math.min(groupPageCount, value + 1))}>下一页</button>
            </div>}
          </section>
        </>}
        <div className="form-actions"><button className="button ghost" onClick={close}>稍后处理</button><button className="button primary" disabled={submitting || data.review?.status !== "pending"} onClick={confirm}>{submitting ? "正在回填…" : data.review?.status === "pending" ? `确认 ${selectedIds.size} 项并回填` : "关联确认已完成"}</button></div>
      </div> : <EmptyState title="无法加载关联候选" text="关闭后可从渠道详情的“待确认关联”入口继续处理。" />}
    </Modal>
  );
}

type ColumnProfile = { field: string; nonEmpty: number; nonEmptyRatio: number; unique: number; samples: string[]; booleanRatio: number };
type ImportRowPreview = {
  sourceRow: number;
  customerName: string;
  region: string;
  regionPending: boolean;
  customerDisposition: string;
  channelNames: string[];
  channelMatches: { rawName: string; status: string; score: number; basis: string; channelId?: string | null }[];
  issues: { type: string; message: string }[];
};
type ImportPreview = {
  quality: { profiles: ColumnProfile[]; warnings: string[]; blockers: string[]; mappingBlocked: boolean };
  summary: Record<string, number>;
  rows: ImportRowPreview[];
  normalized: { row: number; customerName: string; region: string; channelNames: string[]; routeType: string; planStatus: string }[];
  errors: { row: number | null; reason: string }[];
  possibleAliases: string[];
  channelReferences?: PendingChannelReference[];
};
type PendingChannelReference = {
  id?: string;
  rawName: string;
  status: string;
  occurrences?: number;
  count?: number;
  ruleScore?: number;
  score?: number;
  ruleBasis?: string;
  basis?: string;
  customerNames: string[];
  candidates: { channelId: string; name: string; shortName: string; region: string; score: number; basis: string }[];
  ai?: { score?: number; reason?: string; suggestedChannelId?: string };
};
type ImportWorkbench = {
  config: { aiMatchingEnabled: boolean; aiAutoThreshold: number; aiManualThreshold: number; deepseekConfigured: boolean };
  batches: { id: string; fileName: string; sheetName: string; sourceRowCount: number; uploadedRowCount: number; status: string; summary?: Record<string, number>; createdBy: string; createdAt: string; committedAt?: string }[];
  pendingReferences: PendingChannelReference[];
  aliasProposals: { id: string; channelId: string; value: string; status: string; proposedBy: string; createdAt: string }[];
  aliases: { id: string; channelId: string; value: string; source: string; status: string; matchCount: number }[];
  channels: { id: string; name: string; shortName: string; region: string }[];
};

function ImportPage({ notify, refreshState }: { notify: (tone: Notice["tone"], message: string) => void; refreshState: () => Promise<void> }) {
  const [fileName, setFileName] = useState("");
  const [fileHash, setFileHash] = useState("");
  const [sheets, setSheets] = useState<Record<string, Record<string, unknown>[]>>({});
  const [sheetName, setSheetName] = useState("");
  const [mapping, setMapping] = useState({ customerName: "", region: "", channelNames: "" });
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [batchId, setBatchId] = useState("");
  const [busy, setBusy] = useState("");
  const [view, setView] = useState<"import" | "matching" | "history">("import");
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [workbench, setWorkbench] = useState<ImportWorkbench | null>(null);
  const [resolution, setResolution] = useState<Record<string, string>>({});
  const rows = sheets[sheetName] ?? [];
  const fields = rows.length ? Object.keys(rows[0]) : [];
  const profiles = useMemo<ColumnProfile[]>(() => fields.map((field) => {
    const values = rows.map((row) => String(row[field] ?? "").trim());
    const nonEmpty = values.filter(Boolean);
    const unique = [...new Set(nonEmpty)];
    const booleanTokens = new Set(["是", "否", "true", "false", "0", "1", "yes", "no"]);
    return {
      field,
      nonEmpty: nonEmpty.length,
      nonEmptyRatio: rows.length ? Math.round(nonEmpty.length / rows.length * 1000) / 10 : 0,
      unique: unique.length,
      samples: unique.slice(0, 5),
      booleanRatio: nonEmpty.length ? Math.round(nonEmpty.filter((value) => booleanTokens.has(value.toLowerCase())).length / nonEmpty.length * 1000) / 10 : 0
    };
  }), [fields, rows]);
  const customerProfile = profiles.find((profile) => profile.field === mapping.customerName);
  const customerSuspicious = Boolean(customerProfile && (customerProfile.booleanRatio >= 60 || (rows.length >= 20 && customerProfile.unique <= 2) || customerProfile.nonEmptyRatio < 20));
  const step = !fileName ? 0 : !preview ? 2 : batchId ? 5 : 4;

  const refreshWorkbench = async () => {
    try { setWorkbench(await api<ImportWorkbench>("/api/import/workbench")); }
    catch (error) { notify("error", error instanceof Error ? error.message : "导入工作台读取失败"); }
  };
  const refreshAfterDomainChange = async () => {
    await Promise.all([refreshWorkbench(), refreshState()]);
  };
  useEffect(() => { void refreshWorkbench(); }, []);

  const readWorkbook = async (file: File) => {
    try {
      const XLSX = await import("xlsx");
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: "array", cellDates: true });
      const parsed = Object.fromEntries(workbook.SheetNames.map((name) => [name, XLSX.utils.sheet_to_json<Record<string, unknown>>(workbook.Sheets[name], { defval: "" })]));
      setSheets(parsed);
      setFileName(file.name);
      const digest = await crypto.subtle.digest("SHA-256", buffer);
      setFileHash([...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, "0")).join(""));
      setSheetName(workbook.SheetNames[0] ?? "");
      const firstFields = Object.keys(parsed[workbook.SheetNames[0]]?.[0] ?? {});
      setMapping({
        customerName: firstFields.find((item) => /企业.*名称|客户.*(全称|名称)|客户/u.test(item)) ?? "",
        region: firstFields.find((item) => /区域|地区/u.test(item)) ?? "",
        channelNames: firstFields.find((item) => /联合.*渠道/u.test(item)) ?? firstFields.find((item) => /渠道/u.test(item)) ?? ""
      });
      setPreview(null);
      setBatchId("");
    } catch { notify("error", "Excel读取失败，请确认文件格式。"); }
  };

  const makePreview = async () => {
    if (!mapping.customerName) return notify("error", "客户全称是唯一必需字段，请先完成映射。");
    if (customerSuspicious) return notify("error", `“${mapping.customerName}”疑似布尔或低唯一值列，已阻止预览。`);
    setBusy("正在保存 0%");
    try {
      const created = await post<{ batch: { id: string; status: string }; duplicate: boolean }>("/api/import/batches", {
        fileName, fileHash, sheetName, sourceRowCount: rows.length, mapping
      });
      const currentBatchId = created.batch.id;
      if (created.duplicate && created.batch.status === "committed") {
        notify("success", "相同文件哈希与工作表已完成导入，系统未重复创建客户或关系。");
        await refreshAfterDomainChange();
        setView("history");
        return;
      }
      for (let offset = 0; offset < rows.length; offset += 500) {
        await post(`/api/import/batches/${currentBatchId}/rows`, { offset, rows: rows.slice(offset, offset + 500) });
        setBusy(`正在保存 ${Math.min(100, Math.round((offset + 500) / rows.length * 100))}%`);
      }
      setBusy("正在执行去重与渠道研判");
      const processed = await post<{ batch: { id: string; summary: Record<string, number>; channelReferences: PendingChannelReference[] }; preview: ImportPreview }>(`/api/import/batches/${currentBatchId}/process`, { mapping });
      setPreview({ ...processed.preview, summary: processed.batch.summary, channelReferences: processed.batch.channelReferences });
      setBatchId(processed.batch.id);
      setPage(1);
      notify("success", `已保留并处理 ${rows.length} 条来源行，尚未写入客户主档。`);
      await refreshWorkbench();
    } catch (error) { notify("error", error instanceof Error ? error.message : "预览失败"); }
    finally { setBusy(""); }
  };

  const commit = async () => {
    if (!batchId || !preview) return;
    setBusy("正在确认导入");
    try {
      const result = await post<{ createdCustomers: number; createdRelations: number; pendingReferences: number }>(`/api/import/batches/${batchId}/commit`, {});
      notify("success", `导入完成：新增 ${result.createdCustomers} 个客户、${result.createdRelations} 条关系，${result.pendingReferences} 个渠道文本进入待对齐。`);
      await refreshAfterDomainChange();
      setView("history");
    } catch (error) { notify("error", error instanceof Error ? error.message : "确认导入失败"); }
    finally { setBusy(""); }
  };

  const displayedRows = useMemo(() => {
    const keyword = query.trim().toLowerCase();
    return (preview?.rows ?? []).filter((row) => {
      const issue = row.issues.map((item) => item.type);
      if (filter === "blocked" && !issue.includes("blocked")) return false;
      if (filter === "pending" && !issue.includes("pending")) return false;
      if (filter === "channel" && !row.channelMatches.some((item) => item.status !== "matched")) return false;
      if (keyword && !`${row.sourceRow} ${row.customerName} ${row.region} ${row.channelNames.join(" ")}`.toLowerCase().includes(keyword)) return false;
      return true;
    });
  }, [preview, filter, query]);
  const pageSize = 50;
  const pagedRows = displayedRows.slice((page - 1) * pageSize, page * pageSize);
  const pageCount = Math.max(1, Math.ceil(displayedRows.length / pageSize));

  const downloadIssues = () => {
    const issueRows = (preview?.rows ?? []).filter((row) => row.issues.length || row.channelMatches.some((item) => item.status !== "matched"));
    const escape = (value: unknown) => `"${String(value ?? "").replaceAll("\"", "\"\"")}"`;
    const csv = ["原行,客户全称,区域,数据问题,渠道文本,渠道状态", ...issueRows.map((row) => [
      row.sourceRow, row.customerName, row.region, row.issues.map((item) => item.message).join("；"),
      row.channelNames.join(" / "), row.channelMatches.map((item) => `${item.rawName}:${item.status}`).join("；")
    ].map(escape).join(","))].join("\n");
    const link = document.createElement("a");
    link.href = URL.createObjectURL(new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8" }));
    link.download = `${fileName || "导入"}-${sheetName || "工作表"}-问题明细.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const resolveReference = async (reference: PendingChannelReference, action: "bind" | "defer" | "invalid") => {
    if (!reference.id) return;
    try {
      await post(`/api/import/references/${reference.id}/resolve`, {
        action,
        channelId: resolution[reference.id],
        registerAlias: false
      });
      notify("success", "研判结果已保存，相关历史来源将按幂等规则回填。");
      await refreshAfterDomainChange();
    } catch (error) { notify("error", error instanceof Error ? error.message : "研判保存失败"); }
  };

  const updateAiConfig = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const values = Object.fromEntries(new FormData(event.currentTarget));
    try {
      await post("/api/import/config", { aiMatchingEnabled: values.aiMatchingEnabled === "on", aiAutoThreshold: Number(values.aiAutoThreshold) });
      notify("success", "匹配配置已更新并写入审计日志。");
      await refreshWorkbench();
    } catch (error) { notify("error", error instanceof Error ? error.message : "配置更新失败"); }
  };
  return <>
    <PageHeader eyebrow="TRACEABLE IMPORT" title="Excel 全量导入与渠道对齐" description="原始行全留、客户主档去重、渠道关系独立研判；未匹配渠道不会阻塞客户入库。">
      <div className="segmented import-view-switch"><button className={view === "import" ? "active" : ""} onClick={() => setView("import")}>导入批次</button><button className={view === "matching" ? "active" : ""} onClick={() => { setView("matching"); void refreshWorkbench(); }}>渠道匹配工作台</button><button className={view === "history" ? "active" : ""} onClick={() => { setView("history"); void refreshWorkbench(); }}>批次与回滚</button></div>
    </PageHeader>
    {view === "import" && <>
    <div className="import-steps">{["选择文件", "选择工作表", "字段质量", "客户去重", "渠道匹配", "汇总确认"].map((item, index) => <span className={index <= step ? "active" : ""} key={item}><i>{index + 1}</i>{item}</span>)}</div>
    <section className="import-panel panel">
      {!fileName ? <label className="drop-zone"><input type="file" accept=".xlsx,.xls" onChange={(event) => event.target.files?.[0] && readWorkbook(event.target.files[0])} /><CloudUpload width={34} height={34} strokeWidth={1.4} aria-hidden="true" /><h3>选择本地Excel文件</h3><p>选择后将解析当前工作表并生成导入预览。</p><span className="button primary">浏览文件</span></label> : <>
        <div className="file-banner"><span className="file-icon"><TableRows {...iconProps} /></span><div><strong>{fileName}</strong><small>{Object.keys(sheets).length} 个工作表 · 当前 {rows.length} 行</small></div><label className="button ghost">重新选择<input type="file" accept=".xlsx,.xls" onChange={(event) => event.target.files?.[0] && readWorkbook(event.target.files[0])} /></label></div>
        <div className="mapping-layout">
          <div className="sheet-list"><span>工作表</span>{Object.keys(sheets).map((name) => <button className={sheetName === name ? "active" : ""} onClick={() => { setSheetName(name); setPreview(null); }} key={name}>{name}<em>{sheets[name].length}</em></button>)}</div>
          <div className="mapping-fields"><span>字段映射与质量检查 · 仅客户全称必填</span>{([["customerName", "客户全称 *"], ["region", "所属区域"], ["channelNames", "联合经营渠道"]] as const).map(([key, label]) => {
            const profile = profiles.find((item) => item.field === mapping[key]);
            return <div className={`mapping-profile ${key === "customerName" && customerSuspicious ? "blocked" : ""}`} key={key}><label><strong>{label}</strong><NavArrowRight {...iconProps} /><select value={mapping[key]} onChange={(event) => { setMapping({ ...mapping, [key]: event.target.value }); setPreview(null); }}><option value="">不映射</option>{fields.map((field) => <option key={field}>{field}</option>)}</select></label>{profile && <div className="column-profile"><span>非空 {profile.nonEmptyRatio}%</span><span>唯一值 {profile.unique}</span><span>样本：{profile.samples.join(" / ") || "无"}</span>{profile.booleanRatio >= 60 && <em>布尔值占比 {profile.booleanRatio}%，疑似误映射</em>}</div>}</div>;
          })}<div className="mapping-guidance"><ShieldCheck {...iconProps} /><span><strong>字段质量检查</strong><small>“是/否、0/1、极低唯一值或大量空值”的客户列将阻止预览；区域为空只标记待补全。</small></span></div><button className="button primary" disabled={Boolean(busy) || customerSuspicious} onClick={makePreview}>{busy || "生成去重与匹配预览"}<ArrowRight {...iconProps} /></button></div>
        </div>
      </>}
    </section>
    {preview && <section className="preview-section">
      <div className="preview-summary">{["sourceRows", "importableRows", "blockedRows", "pendingCompletion", "newCustomers", "matchedExistingCustomers", "batchDuplicates", "customerConflicts", "channelReferences", "ruleMatches", "aiAutoMatches", "manualReview", "unmatched"].map((key) => <div key={key}><strong>{preview.summary[key] ?? 0}</strong><small>{importLabel(key)}</small></div>)}</div>
      {(preview.quality.warnings.length > 0 || preview.possibleAliases.length > 0) && <div className="alias-warning"><WarningTriangle {...iconProps} /><span><strong>需要关注但不阻塞导入</strong><small>{[...preview.quality.warnings, preview.possibleAliases.length ? `疑似渠道别名：${preview.possibleAliases.slice(0, 8).join("、")}${preview.possibleAliases.length > 8 ? "…" : ""}` : ""].filter(Boolean).join("；")}</small></span></div>}
      <div className="preview-toolbar"><div className="segmented"><button className={filter === "all" ? "active" : ""} onClick={() => { setFilter("all"); setPage(1); }}>全部 {preview.rows.length}</button><button className={filter === "blocked" ? "active" : ""} onClick={() => { setFilter("blocked"); setPage(1); }}>阻断 {preview.summary.blockedRows}</button><button className={filter === "pending" ? "active" : ""} onClick={() => { setFilter("pending"); setPage(1); }}>待补全 {preview.summary.pendingCompletion}</button><button className={filter === "channel" ? "active" : ""} onClick={() => { setFilter("channel"); setPage(1); }}>渠道待研判</button></div><label className="compact-search"><Search {...iconProps} /><input value={query} onChange={(event) => { setQuery(event.target.value); setPage(1); }} placeholder="搜索原行号、客户或渠道" /></label><button className="button ghost" onClick={downloadIssues}><Download {...iconProps} />下载问题明细</button></div>
      <div className="preview-table import-preview-table"><div className="table-row table-head"><span>原行</span><span>客户 / 去重</span><span>区域质量</span><span>渠道研判</span><span>问题</span></div>{pagedRows.map((row) => <div className="table-row" key={row.sourceRow}><span>{row.sourceRow}</span><span><strong>{row.customerName || "客户名称为空"}</strong><small>{customerDispositionLabel(row.customerDisposition)}</small></span><span>{row.region || "空值"}{row.regionPending && <em>待补全</em>}</span><span>{row.channelMatches.length ? row.channelMatches.map((match) => <small key={match.rawName}>{match.rawName} · {channelMatchLabel(match.status)} {match.score ? `${match.score}分` : ""}</small>) : <small>无渠道引用</small>}</span><span>{row.issues.length ? row.issues.map((issue) => <small key={issue.message}>{issue.message}</small>) : <small>可入库</small>}</span></div>)}</div>
      <div className="table-pagination"><span>当前 {displayedRows.length} 行 · 第 {page}/{pageCount} 页</span><div><button className="button ghost" disabled={page <= 1} onClick={() => setPage((value) => value - 1)}>上一页</button><button className="button ghost" disabled={page >= pageCount} onClick={() => setPage((value) => value + 1)}>下一页</button></div></div>
      <div className="import-lock"><ShieldCheck {...iconProps} /><span><strong>确认后事务性写入</strong><small>全部来源行已按批次保存；客户主档按规范化全称去重。未匹配渠道进入待对齐池，不生成临时假渠道。</small></span><button className="button primary" disabled={Boolean(busy)} onClick={commit}>{busy || `确认导入 ${preview.summary.importableRows} 条可入库行`}</button></div>
    </section>}
    </>}
    {view === "matching" && <section className="matching-workbench">
      <div className="matching-overview panel"><div><p className="eyebrow">MATCH CONTROL</p><h2>渠道智能匹配配置</h2><p>规则精确命中优先；无法确定的结果进入人工研判。</p></div>{workbench && <form onSubmit={updateAiConfig}><label className="switch-field"><input type="checkbox" name="aiMatchingEnabled" defaultChecked={workbench.config.aiMatchingEnabled} /><span>启用 DeepSeek 辅助</span></label><label><span>自动确认阈值</span><input name="aiAutoThreshold" type="number" min="70" max="100" defaultValue={workbench.config.aiAutoThreshold} /></label><small>{workbench.config.deepseekConfigured ? "服务已配置" : "服务未配置：导入仍可完成，自动转人工"}</small><button className="button primary">保存配置</button></form>}</div>
      <div className="matching-list">{workbench?.pendingReferences.length ? workbench.pendingReferences.map((reference) => <article className="matching-card panel" key={reference.id}><header><div><strong>{reference.rawName}</strong><small>出现 {reference.occurrences ?? 0} 次 · 涉及客户 {reference.customerNames?.length ?? 0} 个</small></div><Status value={reference.status === "manual" ? "待人工研判" : reference.status === "deferred" ? "暂缓处理" : "未匹配"} /></header><div className="matching-evidence"><section><span>规则依据</span><strong>{reference.ruleBasis || "规则未命中"}</strong><small>规则分 {reference.ruleScore ?? 0}</small></section><section><span>涉及客户</span><p>{reference.customerNames?.slice(0, 5).join("、") || "暂无摘要"}</p></section><section><span>候选渠道</span>{reference.candidates?.length ? reference.candidates.map((candidate) => <button key={candidate.channelId} onClick={() => reference.id && setResolution({ ...resolution, [reference.id]: candidate.channelId })} className={reference.id && resolution[reference.id] === candidate.channelId ? "selected" : ""}><strong>{candidate.name}</strong><small>{candidate.basis} · {candidate.score}分</small></button>) : <p>暂无规则候选，可先新建渠道档案后重新研判。</p>}</section></div><footer><select aria-label={`${reference.rawName}绑定渠道`} value={reference.id ? resolution[reference.id] ?? "" : ""} onChange={(event) => reference.id && setResolution({ ...resolution, [reference.id]: event.target.value })}><option value="">选择现有渠道</option>{workbench.channels.map((channel) => <option value={channel.id} key={channel.id}>{channel.name}（{channel.shortName}）</option>)}</select><button className="button primary" disabled={!reference.id || !resolution[reference.id]} onClick={() => resolveReference(reference, "bind")}>确认绑定渠道</button><button className="button ghost" onClick={() => resolveReference(reference, "defer")}>暂缓</button><button className="button ghost danger" onClick={() => resolveReference(reference, "invalid")}>标记无效</button></footer></article>) : <EmptyState title="暂无待对齐渠道文本" text="导入批次中的未匹配渠道会聚合出现在这里。" />}</div>
    </section>}
    {view === "history" && <section className="import-history panel"><header><div><p className="eyebrow">IMPORT HISTORY</p><h2>批次、来源行与回滚状态</h2></div><button className="button ghost" onClick={() => void refreshAfterDomainChange()}>刷新</button></header>{workbench?.batches.length ? workbench.batches.map((batch) => <article key={batch.id}><div><strong>{batch.fileName}</strong><small>{batch.sheetName} · {batch.createdBy} · {formatDateTime(batch.createdAt)}</small></div><div><strong>{batch.sourceRowCount}</strong><small>来源行 / 已上传 {batch.uploadedRowCount}</small></div><Status value={batchStatusLabel(batch.status)} /><div className="batch-summary"><small>新增客户 {batch.summary?.newCustomers ?? "—"}</small><small>阻断 {batch.summary?.blockedRows ?? "—"}</small><small>待人工 {batch.summary?.manualReview ?? "—"}</small></div>{batch.status === "committed" && <button className="button ghost danger" onClick={async () => { if (!window.confirm("仅无后续计划、进展或申报的数据会自动移除；冲突将保留并生成报告。确认回滚？")) return; try { const result = await post<{ conflicts: unknown[] }>(`/api/import/batches/${batch.id}/rollback`, {}); notify(result.conflicts.length ? "error" : "success", result.conflicts.length ? `部分数据存在 ${result.conflicts.length} 个业务冲突，已停止删除。` : "批次已安全回滚。"); await refreshAfterDomainChange(); } catch (error) { notify("error", error instanceof Error ? error.message : "回滚失败"); } }}>安全回滚</button>}</article>) : <EmptyState title="暂无导入批次" text="每个工作表会形成一个独立、可审计的导入批次。" />}</section>}
  </>;
}

function MessagesPage({ state, user, refresh, notify }: { state: AppState; user: User; refresh: () => Promise<void>; notify: (tone: Notice["tone"], message: string) => void }) {
  const manager = user.role === "manager";
  const messages = state.messages ?? [];
  const [form, setForm] = useState<"通知" | "公告" | null>(null);
  const [selectedId, setSelectedId] = useState(messages[0]?.id ?? "");
  const selected = messages.find((item) => item.id === selectedId) ?? messages[0];
  useEffect(() => {
    if (!messages.some((item) => item.id === selectedId)) setSelectedId(messages[0]?.id ?? "");
  }, [messages, selectedId]);
  const openMessage = async (message: MessageItem) => {
    setSelectedId(message.id);
    if (!manager && !message.read) {
      try {
        await post(`/api/messages/${message.id}/read`, {});
        await refresh();
      } catch (error) {
        notify("error", error instanceof Error ? error.message : "消息状态更新失败");
      }
    }
  };
  return <>
    <PageHeader eyebrow={manager ? "MESSAGE OPERATIONS" : "MESSAGE CENTER"} title={manager ? "通知与公告" : "消息中心"} description={manager ? "通知可定向到具体渠道或账号，公告面向全部启用用户；所有消息均可追踪查看情况。" : "集中查看总管理员下发的定向通知与全员公告。"}>
      {manager && <><button className="button primary" onClick={() => setForm("通知")}><Bell {...iconProps} />下发通知</button><button className="button secondary" onClick={() => setForm("公告")}><JournalPage {...iconProps} />发布公告</button></>}
    </PageHeader>
    <div className="message-layout">
      <aside className="message-list">
        <header><span>{manager ? "已发布消息" : "我的消息"}</span><strong>{messages.length}</strong></header>
        {messages.map((message) => <button className={`${selected?.id === message.id ? "active" : ""} ${!message.read && !manager ? "unread" : ""}`} key={message.id} onClick={() => openMessage(message)}><span className={`message-type ${message.type === "公告" ? "announcement" : ""}`}>{message.type}</span><div><strong>{message.title}</strong><small>{message.targetLabel} · {formatDate(message.createdAt)}</small></div>{!manager && !message.read && <i aria-label="未读" />}{manager && <em>{message.readCount ?? 0}/{message.recipientCount ?? 0} 已读</em>}</button>)}
        {!messages.length && <EmptyState compact title={manager ? "尚未发布消息" : "当前没有消息"} text={manager ? "可从上方发布定向通知或全员公告。" : "新通知和公告会自动出现在这里。"} />}
      </aside>
      <section className="message-detail">
        {selected ? <>
          <header><div><span className={`message-type ${selected.type === "公告" ? "announcement" : ""}`}>{selected.type}</span><h2>{selected.title}</h2><p>{selected.targetLabel} · {selected.author} 发布于 {formatDate(selected.createdAt)}</p></div>{!manager && <Status value={selected.read ? "已读" : "未读"} />}</header>
          <article className="message-content rich-content" dangerouslySetInnerHTML={{ __html: selected.content }} />
          {manager && <section className="message-read-tracking"><header><div><span>查看情况</span><strong>{selected.readCount ?? 0} / {selected.recipientCount ?? 0} 已读</strong></div><span>{selected.recipientCount ? Math.round(((selected.readCount ?? 0) / selected.recipientCount) * 100) : 0}%</span></header><div className="read-progress"><i style={{ width: `${selected.recipientCount ? ((selected.readCount ?? 0) / selected.recipientCount) * 100 : 0}%` }} /></div>{selected.readGroups?.length ? <div className="read-group-list">{selected.readGroups.map((group) => <details key={group.id}><summary><span><strong>{group.label}</strong><small>{group.readCount}/{group.recipientCount} 已读</small></span><em>{group.percentage}%</em></summary><div>{group.members.map((member) => <span key={member.userId} className={member.read ? "read" : "unread"}>{member.read ? <CheckCircle width={15} height={15} strokeWidth={1.7} aria-hidden /> : <Clock width={15} height={15} strokeWidth={1.7} aria-hidden />}{member.name}<small>{member.readAt ? formatDateTime(member.readAt) : "未查看"}</small></span>)}</div></details>)}</div> : <div className="read-people"><section><h3>已查看</h3>{selected.readBy?.length ? selected.readBy.map((item) => <span key={item.userId}><CheckCircle width={15} height={15} strokeWidth={1.7} aria-hidden />{item.name}<small>{formatDate(item.readAt)}</small></span>) : <p>暂无账号查看</p>}</section><section><h3>未查看</h3>{selected.unreadBy?.length ? selected.unreadBy.map((item) => <span key={item.userId}><Clock width={15} height={15} strokeWidth={1.7} aria-hidden />{item.name}</span>) : <p>全部接收人均已查看</p>}</section></div>}</section>}
        </> : <EmptyState title="选择一条消息查看详情" text={manager ? "发布后可在这里查看正文和阅读情况。" : "消息正文和发布范围会显示在这里。"} />}
      </section>
    </div>
    {form && <MessageForm type={form} channels={state.channels ?? []} close={() => setForm(null)} notify={notify} onDone={async (message) => { await refresh(); setForm(null); notify("success", message); }} />}
  </>;
}

function AutoMessagePopup({ message, refresh, openCenter, notify }: { message: MessageItem; refresh: () => Promise<void>; openCenter: () => void; notify: (tone: Notice["tone"], message: string) => void }) {
  const [marking, setMarking] = useState(false);
  useEffect(() => setMarking(false), [message.id]);
  const acknowledge = async (showCenter = false) => {
    if (marking) return;
    setMarking(true);
    try {
      await post(`/api/messages/${message.id}/read`, {});
      await refresh();
      if (showCenter) openCenter();
    } catch (error) {
      setMarking(false);
      notify("error", error instanceof Error ? error.message : "消息状态更新失败");
    }
  };
  return (
    <Modal
      title={message.type === "公告" ? "收到一条新公告" : "收到一条新通知"}
      description={`${message.targetLabel} · ${message.author} 发布于 ${formatDate(message.createdAt)}`}
      close={() => { void acknowledge(); }}
    >
      <div className="incoming-message">
        <div className="incoming-message-heading">
          <span className={`message-type ${message.type === "公告" ? "announcement" : ""}`}>{message.type}</span>
          <h3>{message.title}</h3>
        </div>
        <article className="rich-content" dangerouslySetInnerHTML={{ __html: message.content }} />
        <div className="incoming-message-note"><Eye {...iconProps} /><span>关闭弹窗或确认查看后将记为已读；如有多条未读消息，会继续逐条展示。</span></div>
        <div className="form-actions">
          <button type="button" className="button ghost" disabled={marking} onClick={() => { void acknowledge(true); }}>进入消息中心</button>
          <button type="button" className="button primary" disabled={marking} onClick={() => { void acknowledge(); }}>{marking ? "正在确认…" : "我已查看"}</button>
        </div>
      </div>
    </Modal>
  );
}

function normalizeRichHtml(html: string) {
  const sizes: Record<string, string> = { "1": "12px", "2": "14px", "3": "16px", "4": "20px", "5": "24px", "6": "30px", "7": "36px" };
  if (typeof document === "undefined") return html;
  const template = document.createElement("template");
  template.innerHTML = html;
  template.content.querySelectorAll("font").forEach((font) => {
    const replacement = document.createElement("span");
    const size = font.getAttribute("size");
    const color = font.getAttribute("color");
    if (size) replacement.style.fontSize = sizes[size] ?? "16px";
    if (color) replacement.style.color = color;
    while (font.firstChild) replacement.append(font.firstChild);
    font.replaceWith(replacement);
  });
  return template.innerHTML;
}

function RichTextEditor({ name, initialValue = "", placeholder, notify }: { name: string; initialValue?: string; placeholder: string; notify: (tone: Notice["tone"], message: string) => void }) {
  const editor = useRef<HTMLDivElement>(null);
  const hiddenInput = useRef<HTMLInputElement>(null);
  const savedRange = useRef<Range | null>(null);
  const initialized = useRef(false);
  const composing = useRef(false);
  const [uploading, setUploading] = useState(false);
  const colors = [
    { name: "默认墨绿", value: "#24362e" },
    { name: "深黑", value: "#18221d" },
    { name: "中性灰", value: "#6f7973" },
    { name: "强调红", value: "#c95f54" },
    { name: "提醒橙", value: "#b97a35" },
    { name: "信息蓝", value: "#356d9a" },
    { name: "辅助紫", value: "#765c92" }
  ];
  const [selectedColor, setSelectedColor] = useState(colors[0]);
  const [colorOpen, setColorOpen] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const [customColor, setCustomColor] = useState("#24362e");
  const sync = () => {
    if (hiddenInput.current) hiddenInput.current.value = normalizeRichHtml(editor.current?.innerHTML ?? "");
  };
  const saveSelection = () => {
    const selection = window.getSelection();
    if (!selection?.rangeCount || !editor.current) return;
    const range = selection.getRangeAt(0);
    if (editor.current.contains(range.commonAncestorContainer)) savedRange.current = range.cloneRange();
  };
  const restoreSelection = () => {
    if (!savedRange.current) return;
    const selection = window.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(savedRange.current);
  };
  useEffect(() => {
    if (!editor.current || initialized.current) return;
    editor.current.innerHTML = initialValue;
    if (hiddenInput.current) hiddenInput.current.value = normalizeRichHtml(initialValue);
    initialized.current = true;
  }, [initialValue]);
  const command = (name: string, value?: string) => {
    editor.current?.focus();
    restoreSelection();
    document.execCommand("styleWithCSS", false, ["fontSize", "foreColor"].includes(name) ? "false" : "true");
    document.execCommand(name, false, value);
    sync();
    saveSelection();
  };
  const uploadImage = async (file: File) => {
    if (!/^image\/(png|jpeg|webp)$/i.test(file.type)) return notify("error", "仅支持 PNG、JPEG 或 WebP 图片");
    if (file.size > 3_000_000) return notify("error", "单张图片不能超过 3MB");
    setUploading(true);
    try {
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result));
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const result = await post<{ url: string }>("/api/announcement-assets", { dataUrl });
      editor.current?.focus();
      restoreSelection();
      document.execCommand("insertImage", false, result.url);
      sync();
      saveSelection();
    } catch (error) {
      notify("error", error instanceof Error ? error.message : "图片插入失败");
    } finally { setUploading(false); }
  };
  const chooseColor = (color: { name: string; value: string }) => {
    setSelectedColor(color);
    setColorOpen(false);
    command("foreColor", color.value);
  };
  return <div className="field full rich-editor-field"><span>正文 *</span><div className="rich-editor"><div className="rich-editor-toolbar" aria-label="正文格式工具栏" onMouseDown={saveSelection}><button type="button" aria-label="加粗" onMouseDown={(event) => event.preventDefault()} onClick={() => command("bold")}><strong>B</strong></button><button type="button" aria-label="斜体" onMouseDown={(event) => event.preventDefault()} onClick={() => command("italic")}><em>I</em></button><button type="button" aria-label="下划线" onMouseDown={(event) => event.preventDefault()} onClick={() => command("underline")}><u>U</u></button><label className="rich-toolbar-select"><span className="sr-only">字号</span><select aria-label="字号" defaultValue="3" onMouseDown={saveSelection} onChange={(event) => command("fontSize", event.target.value)}><option value="2">字号：小</option><option value="3">字号：标准</option><option value="4">字号：大</option><option value="5">字号：特大</option></select></label><div className="rich-color-picker"><button type="button" className="rich-color-trigger" aria-haspopup="listbox" aria-expanded={colorOpen} onMouseDown={(event) => { event.preventDefault(); saveSelection(); }} onClick={() => { setColorOpen((value) => !value); setCustomOpen(false); }}><i style={{ backgroundColor: selectedColor.value }} /><span>{selectedColor.name}</span><b>⌄</b></button>{colorOpen && <div className="rich-color-popover" role="listbox" aria-label="文字颜色">{colors.map((color) => <button type="button" role="option" aria-selected={selectedColor.value === color.value} key={color.value} onMouseDown={(event) => event.preventDefault()} onClick={() => chooseColor(color)}><i style={{ backgroundColor: color.value }} /><span>{color.name}</span>{selectedColor.value === color.value && <Check width={17} height={17} strokeWidth={2} aria-hidden />}</button>)}<button type="button" className="custom-color-toggle" onMouseDown={(event) => event.preventDefault()} onClick={() => setCustomOpen((value) => !value)}><i className="custom-color-preview" style={{ backgroundColor: customColor }} /><span>自定义颜色</span><b>{customColor.toUpperCase()}</b></button>{customOpen && <div className="custom-color-panel"><label><span>选择颜色</span><input type="color" value={customColor} onChange={(event) => setCustomColor(event.target.value)} /></label><code>{customColor.toUpperCase()}</code><button type="button" onMouseDown={(event) => event.preventDefault()} onClick={() => chooseColor({ name: "自定义颜色", value: customColor })}>应用</button></div>}</div>}</div><label className="rich-image-button"><span>{uploading ? "插入中…" : "插入图片"}</span><input type="file" accept="image/png,image/jpeg,image/webp" disabled={uploading} onChange={(event) => { const file = event.target.files?.[0]; if (file) { saveSelection(); void uploadImage(file); } event.currentTarget.value = ""; }} /></label></div><div ref={editor} className="rich-editor-surface" contentEditable suppressContentEditableWarning data-placeholder={placeholder} onInput={() => { if (!composing.current) sync(); }} onBlur={() => { sync(); saveSelection(); }} onKeyUp={saveSelection} onMouseUp={saveSelection} onCompositionStart={() => { composing.current = true; }} onCompositionEnd={() => { composing.current = false; sync(); saveSelection(); }} onPaste={(event) => { const file = [...event.clipboardData.items].find((item) => item.kind === "file" && item.type.startsWith("image/"))?.getAsFile(); if (file) { event.preventDefault(); saveSelection(); void uploadImage(file); } }} /><input ref={hiddenInput} type="hidden" name={name} defaultValue={normalizeRichHtml(initialValue)} /></div><small>支持选中文字后设置格式，也可把截图直接粘贴到光标位置。</small></div>;
}

function PlanAnnouncementForm({ close, onDone, notify }: { close: () => void; onDone: (message: string) => void; notify: (tone: Notice["tone"], message: string) => void }) {
  const [submitting, setSubmitting] = useState(false);
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const values = Object.fromEntries(new FormData(event.currentTarget));
    const contentHtml = String(values.contentHtml ?? "").trim();
    if (!contentHtml || !contentHtml.replace(/<[^>]+>/g, "").trim() && !/<img\b/i.test(contentHtml)) return notify("error", "请填写公告正文");
    setSubmitting(true);
    try { const result = await post<{ message: string }>("/api/plan-announcements", { title: values.title, contentHtml }); onDone(result.message); }
    catch (error) { notify("error", error instanceof Error ? error.message : "计划公告发布失败"); }
    finally { setSubmitting(false); }
  };
  return <Modal title="发布工作计划公告" description="公告固定展示在计划页面顶部，最新公告默认展开，历史公告可按需展开。" close={close}><form className="form-grid" onSubmit={submit}><Field label="公告标题" name="title" required placeholder="用一句话说明计划工作重点" /><RichTextEditor name="contentHtml" placeholder="输入公告正文，也可以直接粘贴截图…" notify={notify} /><div className="form-actions full"><button type="button" className="button ghost" onClick={close}>取消</button><button className="button primary" disabled={submitting}>{submitting ? "正在发布…" : "发布到计划页"}</button></div></form></Modal>;
}

function MessageForm({ type, channels, close, onDone, notify }: { type: "通知" | "公告"; channels: Channel[]; close: () => void; onDone: (message: string) => void; notify: (tone: Notice["tone"], message: string) => void }) {
  const [submitting, setSubmitting] = useState(false);
  const [targetType, setTargetType] = useState<"channel" | "user">("channel");
  const accounts = channels.flatMap((channel) => (channel.accounts ?? []).filter((account) => account.active).map((account) => ({ ...account, channelName: channel.name })));
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const values = Object.fromEntries(new FormData(event.currentTarget));
    setSubmitting(true);
    try {
      const response = await post<{ message: string }>("/api/messages", {
        type,
        title: values.title,
        content: values.content,
        targetType: type === "公告" ? "all" : targetType,
        targetId: type === "公告" ? null : values.targetId
      });
      onDone(response.message);
    } catch (error) {
      notify("error", error instanceof Error ? error.message : "消息发布失败");
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <Modal title={type === "公告" ? "发布全员公告" : "下发定向通知"} description={type === "公告" ? "公告将送达所有启用中的管理员、商业组销售、渠道负责人和渠道销售。" : "选择一个具体渠道或具体账号作为接收范围。"} close={close}>
      <form className="form-grid" onSubmit={submit}>
        <Field label={`${type}标题`} name="title" required placeholder="用一句话说明重点" />
        {type === "公告" ? <div className="message-audience full"><Community {...iconProps} /><span><strong>全部启用用户</strong><small>当前所有角色均会收到，并分别记录查看状态。</small></span></div> : <>
          <label className="field"><span>通知范围</span><select value={targetType} onChange={(event) => setTargetType(event.target.value as "channel" | "user")}><option value="channel">指定渠道</option><option value="user">指定账号</option></select></label>
          <label className="field"><span>{targetType === "channel" ? "目标渠道" : "目标账号"} *</span><select name="targetId" required defaultValue=""><option value="">请选择</option>{targetType === "channel" ? channels.filter((channel) => channel.active).map((channel) => <option value={channel.id} key={channel.id}>{channel.name}</option>) : accounts.map((account) => <option value={account.id} key={account.id}>{account.name} · {account.title} · {account.channelName}</option>)}</select></label>
        </>}
        <RichTextEditor name="content" placeholder="填写需要接收人知悉的完整内容，也可以直接粘贴截图…" notify={notify} />
        <div className="form-actions full"><button type="button" className="button ghost" onClick={close}>取消</button><button className="button primary" disabled={submitting}>{submitting ? "正在发布…" : type === "公告" ? "发布全员公告" : "下发定向通知"}</button></div>
      </form>
    </Modal>
  );
}

function PlanStatisticsPage({ state, user, refresh, notify, embedded = false }: { state: AppState; user: User; refresh: () => Promise<void>; notify: (tone: Notice["tone"], message: string) => void; embedded?: boolean }) {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [channelId, setChannelId] = useState("all");
  const [ownerId, setOwnerId] = useState("all");
  const [workTagGroupId, setWorkTagGroupId] = useState("all");
  const [workTagId, setWorkTagId] = useState("all");
  const [status, setStatus] = useState("all");
  const [mode, setMode] = useState<"summary" | "full">("summary");
  const [statistics, setStatistics] = useState<PlanStatistics | null>(null);
  const [analysis, setAnalysis] = useState<PlanAnalysis | null>(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const filterPayload = useCallback(() => ({ from, to, channelId, ownerId, workTagGroupId, workTagId, status, mode }), [channelId, from, mode, ownerId, status, to, workTagGroupId, workTagId]);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams(filterPayload());
      setStatistics(await api<PlanStatistics>(`/api/plan-statistics?${params}`));
    } catch (error) { notify("error", error instanceof Error ? error.message : "统计加载失败"); }
    finally { setLoading(false); }
  }, [filterPayload, notify]);
  useEffect(() => { void load(); }, [load]);
  const analyze = async () => {
    setAnalyzing(true);
    try {
      const result = await post<{ statistics: PlanStatistics; analysis: PlanAnalysis }>("/api/plan-statistics/analyze", filterPayload());
      setStatistics(result.statistics); setAnalysis(result.analysis);
    } catch (error) { notify("error", error instanceof Error ? error.message : "AI研判失败"); }
    finally { setAnalyzing(false); }
  };
  const maxChannel = Math.max(1, ...(statistics?.byChannel.map((item) => Math.max(item.planned, item.effectiveActions)) ?? [1]));
  const maxPerson = Math.max(1, ...(statistics?.byPerson.map((item) => Math.max(item.planned, item.effectiveActions)) ?? [1]));
  const maxTag = Math.max(1, ...(statistics?.byTag.map((item) => Math.max(item.planned, item.effectiveActions)) ?? [1]));
  const visibleTags = (state.workNatures ?? []).filter((tag) => workTagGroupId === "all" || tag.groupId === workTagGroupId);
  const personOptions = statistics?.byPerson.filter((item) => item.ownerId) ?? [];
  const drill = (patch: { channelId?: string; ownerId?: string; workTagId?: string }) => { if (patch.channelId) setChannelId(patch.channelId); if (patch.ownerId) setOwnerId(patch.ownerId); if (patch.workTagId) setWorkTagId(patch.workTagId); setMode("full"); };
  return <>
    {embedded ? <section className="embedded-stats-header"><div><p className="eyebrow">CHANNEL PLAN INTELLIGENCE</p><h2>计划统计分析</h2><p>按渠道、人员、标签和结果查看完成情况，并可下钻到具体工作。</p></div><button className="button primary" disabled={analyzing} onClick={analyze}><Spark {...iconProps} />{analyzing ? "DeepSeek 研判中…" : "AI 经营研判"}</button></section> : <PageHeader eyebrow="CHANNEL PLAN INTELLIGENCE" title="渠道工作计划统计" description="从渠道、人员、标签和计划结果四个维度查看经营完成情况，并下钻核对每一项工作。"><button className="button primary" disabled={analyzing} onClick={analyze}><Spark {...iconProps} />{analyzing ? "DeepSeek 研判中…" : "AI 经营研判"}</button></PageHeader>}
    <section className="stats-mode-switch"><button className={mode === "summary" ? "active" : ""} onClick={() => setMode("summary")}>汇总统计</button><button className={mode === "full" ? "active" : ""} onClick={() => setMode("full")}>全量统计</button></section>
    <section className="stats-filter-bar"><label className="field"><span>开始日期</span><input type="date" value={from} onChange={(event) => setFrom(event.target.value)} /></label><label className="field"><span>结束日期</span><input type="date" value={to} onChange={(event) => setTo(event.target.value)} /></label><label className="field"><span>渠道</span><select value={channelId} onChange={(event) => setChannelId(event.target.value)}><option value="all">全部有权渠道</option>{(state.channels ?? []).map((channel) => <option key={channel.id} value={channel.id}>{channel.name}</option>)}</select></label><label className="field"><span>负责人</span><select value={ownerId} onChange={(event) => setOwnerId(event.target.value)}><option value="all">全部负责人</option>{personOptions.map((person) => <option key={person.ownerId!} value={person.ownerId!}>{person.ownerName}</option>)}</select></label><label className="field"><span>标签组</span><select value={workTagGroupId} onChange={(event) => { setWorkTagGroupId(event.target.value); setWorkTagId("all"); }}><option value="all">全部标签组</option>{(state.workNatureGroups ?? []).map((group) => <option key={group.id} value={group.id}>{group.name}</option>)}</select></label><label className="field"><span>工作标签</span><select value={workTagId} onChange={(event) => setWorkTagId(event.target.value)}><option value="all">全部工作标签</option>{visibleTags.map((tag) => <option key={tag.id} value={tag.id}>{tag.name}</option>)}</select></label><label className="field"><span>计划状态</span><select value={status} onChange={(event) => setStatus(event.target.value)}><option value="all">全部状态</option>{["已完成", "正常推进", "今日到期", "三日内到期", "已逾期", "推进受阻", "暂无计划"].map((item) => <option key={item}>{item}</option>)}</select></label><button className="button secondary" disabled={loading} onClick={() => void load()}>{loading ? "统计中…" : "刷新统计"}</button></section>
    {statistics && <>
      <section className="stats-kpi-grid"><article><span>覆盖渠道</span><strong>{statistics.summary.channelCount}</strong></article><article><span>计划总量</span><strong>{statistics.summary.planCount}</strong></article><article><span>完成计划</span><strong>{statistics.summary.completedPlanCount}</strong></article><article><span>有效动作</span><strong>{statistics.summary.effectiveWorkCount}</strong></article><article><span>完成率</span><strong>{statistics.summary.completionRate}%</strong></article></section>
      <section className="stats-visual-grid three"><StatsBars eyebrow="CHANNEL PERFORMANCE" title="渠道完成情况" max={maxChannel} rows={statistics.byChannel.map((item) => ({ id: item.channelId, label: item.channelName, planned: item.planned, completed: item.completed, actions: item.effectiveActions }))} onSelect={(id) => drill({ channelId: id })} /><StatsBars eyebrow="PEOPLE CONTRIBUTION" title="人员贡献" max={maxPerson} rows={statistics.byPerson.map((item) => ({ id: item.ownerId ?? `name:${item.ownerName}`, label: item.ownerName, planned: item.planned, completed: item.completed, actions: item.effectiveActions }))} onSelect={(id) => !id.startsWith("name:") && drill({ ownerId: id })} /><StatsBars eyebrow="WORK TAGS" title="工作标签分布" max={maxTag} rows={statistics.byTag.map((item) => ({ id: item.workTagId, label: item.name, sublabel: item.groupName, planned: item.planned, completed: item.completed, actions: item.effectiveActions }))} onSelect={(id) => drill({ workTagId: id })} /></section>
      <section className="stats-detail-panel"><header><div><p className="eyebrow">TRACEABLE DETAILS</p><h2>{mode === "full" ? "全量计划与结果明细" : "计划明细预览"}</h2></div><span>{statistics.details.length} 项</span></header>{statistics.details.length ? <div className="stats-detail-list">{statistics.details.map((detail) => <details key={detail.planId}><summary><div><strong>{detail.customer}</strong><small>{detail.channel} · {detail.routeType} · {detail.owner}</small></div><span><Status value={detail.status} /><b>{detail.results.length} 项结果</b><NavArrowRight width={16} height={16} strokeWidth={1.7} aria-hidden /></span></summary><div className="stats-plan-content"><p><strong>计划内容</strong>{detail.nextAction}</p><p><strong>工作标签</strong>{detail.tags.length ? detail.tags.join("、") : "未分类"}</p><p><strong>跟进日期</strong>{formatDate(detail.followUpDate)}</p>{detail.results.length ? <div className="stats-result-list">{detail.results.map((result) => <article key={`${result.progressId}-${result.action}`}><header><strong>{result.action}</strong><Status value={result.progressStatus} /></header><p>{result.result}</p><small>{result.author} · {formatDateTime(result.occurredAt)}</small></article>)}</div> : <EmptyState compact title="尚未形成工作结果" text="计划仍保留在统计明细中。" />}</div></details>)}</div> : <EmptyState title="当前筛选范围没有计划" text="调整时间或筛选条件后重试。" />}</section>
    </>}
    {analysis && <section className="ai-plan-analysis"><header><div><p className="eyebrow">DEEPSEEK REVIEW</p><h2>{analysis.headline}</h2></div><span>{analysis.status === "ok" ? analysis.model : "本地规则降级"}</span></header><p>{analysis.overview}</p><div><article><h3>关键洞察</h3>{analysis.insights.map((item) => <p key={item}><CheckCircle {...iconProps} />{item}</p>)}</article><article><h3>风险提示</h3>{analysis.risks.length ? analysis.risks.map((item) => <p key={item}><WarningTriangle {...iconProps} />{item}</p>) : <p>当前未识别到突出风险。</p>}</article><article><h3>建议动作</h3>{analysis.actions.map((item) => <p key={item}><ArrowRight {...iconProps} />{item}</p>)}</article></div></section>}
    {user.role === "manager" && <WorkNatureAdmin state={state} refresh={refresh} notify={notify} />}
  </>;
}

function StatsBars({ eyebrow, title, rows, max, onSelect }: { eyebrow: string; title: string; rows: { id: string; label: string; sublabel?: string; planned: number; completed: number; actions: number }[]; max: number; onSelect: (id: string) => void }) {
  return <article className="stats-panel"><header><p className="eyebrow">{eyebrow}</p><h2>{title}</h2></header>{rows.length ? <div className="bar-chart">{rows.map((item) => <button type="button" className="bar-row" key={item.id} onClick={() => onSelect(item.id)}><span title={item.label}>{item.label}{item.sublabel && <small>{item.sublabel}</small>}</span><div><i style={{ width: `${item.planned / max * 100}%` }} /><b style={{ width: `${item.actions / max * 100}%` }} /></div><strong>{item.completed}/{item.planned} · {item.actions} 项动作</strong></button>)}</div> : <EmptyState compact title="暂无可统计数据" text="当前筛选范围没有形成计划或结果。" />}</article>;
}

function WorkNatureAdmin({ state, refresh, notify }: { state: AppState; refresh: () => Promise<void>; notify: (tone: Notice["tone"], message: string) => void }) {
  const createGroup = async (event: FormEvent<HTMLFormElement>) => { event.preventDefault(); const form = event.currentTarget; const values = Object.fromEntries(new FormData(form)); try { const result = await post<{ message: string }>("/api/work-nature-groups", values); form.reset(); await refresh(); notify("success", result.message); } catch (error) { notify("error", error instanceof Error ? error.message : "新增标签组失败"); } };
  const createNature = async (event: FormEvent<HTMLFormElement>) => { event.preventDefault(); const form = event.currentTarget; const values = Object.fromEntries(new FormData(form)); try { const result = await post<{ message: string }>("/api/work-natures", values); form.reset(); await refresh(); notify("success", result.message); } catch (error) { notify("error", error instanceof Error ? error.message : "新增工作标签失败"); } };
  const toggle = async (nature: WorkNature) => { try { const result = await api<{ message: string }>(`/api/work-natures/${nature.id}`, { method: "PATCH", body: JSON.stringify({ active: !nature.active }) }); await refresh(); notify("success", result.message); } catch (error) { notify("error", error instanceof Error ? error.message : "更新标签失败"); } };
  return <section className="work-nature-admin"><header><div><p className="eyebrow">ADMIN CONFIGURATION</p><h2>工作标签管理</h2></div></header><div className="work-nature-create"><form onSubmit={createGroup}><Field label="新增标签组" name="name" required placeholder="例如：六大场景" /><button className="button secondary">新增组别</button></form><form onSubmit={createNature}><Field label="新增工作标签" name="name" required placeholder="例如：研发防泄密" /><label className="field"><span>所属组别（选填）</span><select name="groupId"><option value="">未分组</option>{(state.workNatureGroups ?? []).filter((group) => group.active).map((group) => <option key={group.id} value={group.id}>{group.name}</option>)}</select></label><button className="button primary">新增标签</button></form></div><div className="work-nature-tags">{(state.workNatures ?? []).map((nature) => <button key={nature.id} className={nature.active ? "active" : "inactive"} onClick={() => void toggle(nature)}><span>{nature.groupName}</span><strong>{nature.name}</strong><small>{nature.active ? "启用中 · 点击停用" : "已停用 · 点击启用"}</small></button>)}</div></section>;
}

function ExportPage({ state, notify }: { state: AppState; notify: (tone: Notice["tone"], message: string) => void }) {
  const [loading, setLoading] = useState(false);
  const exportData = async () => { setLoading(true); try { await downloadFile("/api/exports/operations.xlsx", `渠道经营数据-${todayOffset(0)}.xlsx`); notify("success", "Excel 已生成"); } catch (error) { notify("error", error instanceof Error ? error.message : "导出失败"); } finally { setLoading(false); } };
  return <><PageHeader eyebrow="TRACEABLE EXPORT" title="渠道经营数据导出" description="一次导出客户渠道关系、计划与工作结果明细、计划统计汇总三张工作表。" /><section className="export-workbench simplified"><article><Database width={34} height={34} strokeWidth={1.4} aria-hidden /><div><h2>完整经营数据包</h2><p>计划明细已包含工作标签、完成状态、工作动作、结果和发生时间。</p><strong>{state.routes.length} 条当前通路</strong></div></article><div className="export-sheet-list"><span>客户与渠道关系</span><span>计划及工作结果</span><span>计划统计汇总</span></div><button className="button primary" disabled={loading} onClick={exportData}><Download {...iconProps} />{loading ? "正在生成…" : "导出 Excel"}</button></section></>;
}

function ChannelAssignmentPicker({ channels, defaultSelected = [] }: { channels: Channel[]; defaultSelected?: string[] }) {
  const [selected, setSelected] = useState(() => new Set(defaultSelected));
  const selectedChannels = channels.filter((channel) => selected.has(channel.id));
  const selectionSummary = selectedChannels.length
    ? `${selectedChannels.length} 个渠道已选择 · ${selectedChannels.slice(0, 2).map((channel) => channel.shortName || channel.name).join("、")}${selectedChannels.length > 2 ? "等" : ""}`
    : "尚未选择渠道";
  const toggle = (channelId: string) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(channelId)) next.delete(channelId);
      else next.add(channelId);
      return next;
    });
  };

  return <details className="channel-assignment-picker">
    <summary>
      <span><strong>对接渠道</strong><small>{selectionSummary}</small></span>
      <NavArrowRight {...iconProps} />
    </summary>
    <div className="channel-assignment-options">
      {channels.length ? channels.map((channel) => <label key={channel.id}>
        <input
          type="checkbox"
          name="assignedChannelIds"
          value={channel.id}
          checked={selected.has(channel.id)}
          onChange={() => toggle(channel.id)}
        />
        <span>
          <strong>{channel.name}</strong>
          <small>{channel.region || "区域待补全"} · {channel.type || "类型待补全"}</small>
        </span>
      </label>) : <p className="channel-assignment-empty">暂无可分配渠道</p>}
    </div>
  </details>;
}

function BusinessUsersPage({ state, refresh, notify }: { state: AppState; refresh: () => Promise<void>; notify: (tone: Notice["tone"], message: string) => void }) {
  const [creating, setCreating] = useState(false);
  const create = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    const assignedChannelIds = data.getAll("assignedChannelIds").map(String);
    try {
      const result = await post<{ message: string }>("/api/business-users", { name: data.get("name"), login: data.get("login"), password: data.get("password"), assignedChannelIds });
      form.reset();
      setCreating(false);
      await refresh();
      notify("success", result.message);
    } catch (error) {
      notify("error", error instanceof Error ? error.message : "创建账号失败");
    }
  };
  const saveChannels = async (userId: string, form: HTMLFormElement) => {
    const assignedChannelIds = new FormData(form).getAll("assignedChannelIds").map(String);
    try {
      const result = await api<{ message: string }>(`/api/business-users/${userId}/channels`, { method: "PATCH", body: JSON.stringify({ assignedChannelIds }) });
      await refresh();
      notify("success", result.message);
    } catch (error) {
      notify("error", error instanceof Error ? error.message : "保存范围失败");
    }
  };
  const deleteUser = async (businessUser: User) => {
    if (!window.confirm(`确认删除 ${businessUser.name} 的商业组销售账号？该账号会立即无法登录，历史审计记录仍保留。`)) return;
    try {
      const result = await post<{ message: string }>(`/api/users/${businessUser.id}/delete`, {});
      await refresh();
      notify("success", result.message);
    } catch (error) {
      notify("error", error instanceof Error ? error.message : "账号删除失败");
    }
  };

  return <>
    <PageHeader eyebrow="BUSINESS SALES ACCESS" title="商业组销售" description="商业组销售可查看其对接渠道的全量业务信息和经营统计，但不能修改客户、渠道、通路、计划或申报。">
      <button className="button primary" onClick={() => setCreating((value) => !value)}><PlusCircle {...iconProps} />新增商业组销售</button>
    </PageHeader>
    {creating && <form className="business-create-form" onSubmit={create}>
      <Field label="姓名" name="name" required />
      <Field label="登录账号" name="login" required placeholder="字母、数字、点或下划线" />
      <Field label="初始密码" name="password" type="password" required />
      <ChannelAssignmentPicker channels={state.channels ?? []} />
      <div className="form-actions"><button type="button" className="button ghost" onClick={() => setCreating(false)}>取消</button><button className="button primary">创建账号</button></div>
    </form>}
    <section className="business-user-grid">
      {(state.businessUsers ?? []).map((businessUser) => <article key={businessUser.id}>
        <header><span className="avatar">{businessUser.name.slice(0, 1)}</span><div><h2>{businessUser.name}</h2><p>{businessUser.title} · {businessUser.active === false ? "停用" : "启用"}</p></div></header>
        <form onSubmit={(event) => { event.preventDefault(); void saveChannels(businessUser.id, event.currentTarget); }}>
          <ChannelAssignmentPicker
            key={`${businessUser.id}:${(businessUser.assignedChannelIds ?? []).join(",")}`}
            channels={state.channels ?? []}
            defaultSelected={businessUser.assignedChannelIds ?? []}
          />
          <button className="button secondary">保存对接范围</button>
        </form>
        <button type="button" className="button ghost danger" onClick={() => void deleteUser(businessUser)}><Trash width={16} height={16} strokeWidth={1.7} aria-hidden />删除账号</button>
      </article>)}
    </section>
  </>;
}

type AuditView = "overview" | "events" | "security";
type AuditPreset = "today" | "7d" | "30d" | "all" | "custom";
type AuditEventFilters = { category: string; outcome: string; actorId: string; actorRole: string; channelId: string; query: string; page: number; pageSize: number };

const auditRoleLabels: Record<string, string> = {
  manager: "总管理员",
  business_sales: "商业组销售",
  channel_owner: "渠道负责人",
  channel_sales: "渠道销售",
  channel: "渠道账号",
  system: "系统"
};

function AuditPage({ state, notify }: { state: AppState; notify: (tone: Notice["tone"], message: string) => void }) {
  const [view, setView] = useState<AuditView>("overview");
  const [preset, setPreset] = useState<AuditPreset>("30d");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [summary, setSummary] = useState<AuditSummary | null>(null);
  const [events, setEvents] = useState<AuditEventPage | null>(null);
  const [security, setSecurity] = useState<AuditSecurity | null>(null);
  const [selected, setSelected] = useState<AuditEvent | null>(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState<AuditEventFilters>({ category: "all", outcome: "all", actorId: "all", actorRole: "all", channelId: "all", query: "", page: 1, pageSize: 30 });
  const commonParams = useCallback(() => {
    const params = new URLSearchParams();
    if (preset === "custom") {
      if (from) params.set("from", from);
      if (to) params.set("to", to);
      params.set("preset", "all");
    } else params.set("preset", preset);
    return params;
  }, [from, preset, to]);
  const eventParams = useCallback((includePaging = true) => {
    const params = commonParams();
    if (filters.category !== "all") params.set("category", filters.category);
    if (filters.outcome !== "all") params.set("outcome", filters.outcome);
    if (filters.actorId !== "all") params.set("actorId", filters.actorId);
    if (filters.actorRole !== "all") params.set("actorRole", filters.actorRole);
    if (filters.channelId !== "all") params.set("channelId", filters.channelId);
    if (filters.query.trim()) params.set("q", filters.query.trim());
    if (includePaging) {
      params.set("page", String(filters.page));
      params.set("pageSize", String(filters.pageSize));
    }
    return params;
  }, [commonParams, filters]);
  const loadSummary = useCallback(async () => {
    const result = await api<AuditSummary>(`/api/audits/summary?${commonParams()}`);
    setSummary(result);
  }, [commonParams]);
  const loadEvents = useCallback(async () => {
    const result = await api<AuditEventPage>(`/api/audits/events?${eventParams()}`);
    setEvents(result);
  }, [eventParams]);
  const loadSecurity = useCallback(async () => {
    const params = commonParams();
    if (filters.outcome !== "all") params.set("outcome", filters.outcome);
    if (filters.channelId !== "all") params.set("channelId", filters.channelId);
    if (filters.query.trim()) params.set("q", filters.query.trim());
    const result = await api<AuditSecurity>(`/api/audits/security?${params}`);
    setSecurity(result);
  }, [commonParams, filters.channelId, filters.outcome, filters.query]);
  useEffect(() => {
    setLoading(true);
    const timer = window.setTimeout(() => {
      const request = view === "overview" ? loadSummary() : view === "events" ? Promise.all([loadSummary(), loadEvents()]) : Promise.all([loadSummary(), loadSecurity()]);
      void request.catch((error) => notify("error", error instanceof Error ? error.message : "审计数据加载失败")).finally(() => setLoading(false));
    }, view === "overview" ? 0 : 180);
    return () => window.clearTimeout(timer);
  }, [loadEvents, loadSecurity, loadSummary, notify, view]);
  const openEvent = async (item: AuditEvent) => {
    setSelected(item);
    try { setSelected(await api<AuditEvent>(`/api/audits/events/${item.id}`)); }
    catch (error) { notify("error", error instanceof Error ? error.message : "日志详情加载失败"); }
  };
  const switchToChannel = (channelId: string) => {
    setFilters((current) => ({ ...current, channelId, page: 1 }));
    setView("events");
  };
  const exportEvents = async () => {
    try { await downloadFile(`/api/audits/export.xlsx?${eventParams(false)}`, `审计日志-${todayOffset(0)}.xlsx`); notify("success", "审计 Excel 已生成"); }
    catch (error) { notify("error", error instanceof Error ? error.message : "审计日志导出失败"); }
  };
  const metricCards = summary ? [
    ["平台操作总数", summary.metrics.operationCount, "当前时间范围内"],
    ["登录次数", summary.metrics.loginCount, "成功与失败记录"],
    ["活跃账号", summary.metrics.activeAccountCount, "发生过操作的账号"],
    ["申报数量", summary.metrics.applicationCount, "提交与审批动作"],
    ["计划结果", summary.metrics.planResultCount, "结果及进展记录"],
    ["导入及 AI", summary.metrics.importAiCount, "数据与智能研判动作"]
  ] as const : [];
  const maxTrend = Math.max(1, ...(summary?.trend.map((item) => item.value) ?? [1]));
  const maxCategory = Math.max(1, ...(summary?.categories.map((item) => item.value) ?? [1]));
  const maxChannel = Math.max(1, ...(summary?.channelUsage.map((item) => item.value) ?? [1]));
  const maxActor = Math.max(1, ...(summary?.actorContribution.map((item) => item.count) ?? [1]));
  const channelName = (channelId?: string | null) => summary?.channelOptions.find((item) => item.id === channelId)?.name ?? state.channels?.find((item) => item.id === channelId)?.name ?? "—";
  return <>
    <PageHeader eyebrow="GLOBAL DATA AUDIT" title="全局数据审计" description="从平台经营、逐项操作和登录安全三个视角查看可追溯数据。" />
    <div className="audit-view-tabs" role="tablist" aria-label="审计视图">
      <button role="tab" aria-selected={view === "overview"} className={view === "overview" ? "active" : ""} onClick={() => setView("overview")}><StatsUpSquare {...iconProps} />概览</button>
      <button role="tab" aria-selected={view === "events"} className={view === "events" ? "active" : ""} onClick={() => setView("events")}><TableRows {...iconProps} />操作日志</button>
      <button role="tab" aria-selected={view === "security"} className={view === "security" ? "active" : ""} onClick={() => setView("security")}><ShieldCheck {...iconProps} />登录安全</button>
    </div>
    <section className="audit-period-bar">
      <div className="audit-period-presets">{([['today', '今日'], ['7d', '近 7 天'], ['30d', '近 30 天'], ['all', '全部'], ['custom', '自定义']] as [AuditPreset, string][]).map(([value, label]) => <button key={value} className={preset === value ? "active" : ""} onClick={() => { setPreset(value); setFilters((current) => ({ ...current, page: 1 })); }}>{label}</button>)}</div>
      {preset === "custom" && <div className="audit-custom-range"><label>开始<input type="date" value={from} onChange={(event) => { setFrom(event.target.value); setFilters((current) => ({ ...current, page: 1 })); }} /></label><span>至</span><label>结束<input type="date" value={to} onChange={(event) => { setTo(event.target.value); setFilters((current) => ({ ...current, page: 1 })); }} /></label></div>}
      <span className={loading ? "audit-loading is-loading" : "audit-loading"}>{loading ? "正在更新…" : summary ? `更新于 ${formatDateTime(summary.generatedAt)}` : "等待加载"}</span>
    </section>
    {view === "overview" && <>
      <section className="audit-kpi-grid">{metricCards.map(([label, value, note]) => <article key={label}><span>{label}</span><strong>{value.toLocaleString("zh-CN")}</strong><small>{note}</small></article>)}</section>
      <section className="audit-overview-grid">
        <article className="audit-chart-panel wide"><header><div><p className="eyebrow">OPERATION TREND</p><h2>操作趋势</h2></div><span>{summary?.trend.length ?? 0} 个时间点</span></header>{summary?.trend.length ? <div className="audit-trend-chart">{summary.trend.map((item) => <div key={item.label}><span>{item.value}</span><i style={{ height: `${Math.max(6, item.value / maxTrend * 100)}%` }} /><small>{item.label}</small></div>)}</div> : <EmptyState compact title="暂无趋势数据" text="当前时间范围没有可统计操作。" />}</article>
        <article className="audit-chart-panel"><header><div><p className="eyebrow">EVENT TYPES</p><h2>事件类型分布</h2></div></header><div className="audit-horizontal-bars">{summary?.categories.map((item) => <div key={item.category}><span>{item.label}</span><div><i style={{ width: `${item.value / maxCategory * 100}%` }} /></div><strong>{item.value}</strong></div>)}</div></article>
        <article className="audit-chart-panel"><header><div><p className="eyebrow">CHANNEL ACTIVITY</p><h2>渠道活跃度</h2></div><small>点击渠道查看日志</small></header><div className="audit-horizontal-bars">{summary?.channelUsage.filter((item) => item.value > 0).slice(0, 10).map((item) => <button key={item.channelId} onClick={() => switchToChannel(item.channelId)}><span>{item.channelName}</span><div><i style={{ width: `${item.value / maxChannel * 100}%` }} /></div><strong>{item.value}</strong></button>)}</div></article>
        <article className="audit-chart-panel"><header><div><p className="eyebrow">ACTOR CONTRIBUTION</p><h2>操作人员贡献</h2></div></header><div className="audit-horizontal-bars">{summary?.actorContribution.map((item) => <div key={`${item.login}-${item.name}`}><span>{item.name}<small>{item.login}</small></span><div><i style={{ width: `${item.count / maxActor * 100}%` }} /></div><strong>{item.count}</strong></div>)}</div></article>
        <article className="audit-chart-panel important"><header><div><p className="eyebrow">IMPORTANT EVENTS</p><h2>最近重要操作</h2></div></header><div className="audit-important-list">{summary?.recentImportant.length ? summary.recentImportant.map((item) => <button key={item.id} onClick={() => void openEvent(item)}><i className={item.outcome === "failure" ? "failure" : "success"} /><span><strong>{item.action}</strong><small>{item.actor} · {formatDateTime(item.at)}</small></span><em>{item.categoryLabel}</em></button>) : <EmptyState compact title="暂无重要操作" text="当前时间范围没有失败或关键业务事件。" />}</div></article>
      </section>
    </>}
    {view === "events" && <>
      <section className="audit-filter-panel">
        <label className="audit-query"><Search {...iconProps} /><input value={filters.query} onChange={(event) => setFilters((current) => ({ ...current, query: event.target.value, page: 1 }))} placeholder="搜索操作、账号、对象或变化摘要" /></label>
        <label><span>事件分类</span><select value={filters.category} onChange={(event) => setFilters((current) => ({ ...current, category: event.target.value, page: 1 }))}><option value="all">全部分类</option>{summary?.categoryOptions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</select></label>
        <label><span>执行结果</span><select value={filters.outcome} onChange={(event) => setFilters((current) => ({ ...current, outcome: event.target.value, page: 1 }))}><option value="all">全部结果</option><option value="success">成功</option><option value="failure">失败</option></select></label>
        <label><span>操作人</span><select value={filters.actorId} onChange={(event) => setFilters((current) => ({ ...current, actorId: event.target.value, page: 1 }))}><option value="all">全部人员</option>{summary?.actorOptions.map((item) => <option key={item.id} value={item.id}>{item.name} · {item.login}</option>)}</select></label>
        <label><span>角色</span><select value={filters.actorRole} onChange={(event) => setFilters((current) => ({ ...current, actorRole: event.target.value, page: 1 }))}><option value="all">全部角色</option>{Object.entries(auditRoleLabels).filter(([key]) => key !== "system" && key !== "channel").map(([key, label]) => <option key={key} value={key}>{label}</option>)}</select></label>
        <label><span>渠道</span><select value={filters.channelId} onChange={(event) => setFilters((current) => ({ ...current, channelId: event.target.value, page: 1 }))}><option value="all">全部渠道</option>{summary?.channelOptions.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label>
        <button className="button secondary audit-export" onClick={() => void exportEvents()}><Download {...iconProps} />导出当前范围</button>
      </section>
      <section className="audit-table-panel">
        <header><div><strong>{events?.total.toLocaleString("zh-CN") ?? 0}</strong><span>条操作记录</span></div><label>每页<select value={filters.pageSize} onChange={(event) => setFilters((current) => ({ ...current, pageSize: Number(event.target.value), page: 1 }))}><option value={30}>30</option><option value={50}>50</option><option value={100}>100</option></select>条</label></header>
        <div className="audit-table audit-table-head"><span>时间</span><span>操作</span><span>操作人</span><span>分类</span><span>对象</span><span>结果</span><span /></div>
        <div className="audit-table-body">{events?.items.length ? events.items.map((item) => <button className="audit-table" key={item.id} onClick={() => void openEvent(item)}><time data-label="时间">{formatDateTime(item.at)}</time><span className="audit-cell-primary" data-label="操作"><strong>{item.action}</strong><small>{item.detail || "暂无变化摘要"}</small></span><span className="audit-cell-actor" data-label="操作人"><strong>{item.actor}</strong><small>{item.actorLogin || auditRoleLabels[item.actorRole ?? ""]}</small></span><span className="audit-cell-category" data-label="分类">{item.categoryLabel}</span><span className="audit-cell-target" data-label="对象">{item.target || "—"}</span><span className={`audit-outcome ${item.outcome}`}>{item.outcome === "success" ? "成功" : "失败"}</span><NavArrowRight width={17} height={17} strokeWidth={1.8} aria-hidden /></button>) : <EmptyState title="没有匹配的审计记录" text="调整筛选条件或时间范围后重试。" />}</div>
        <footer><button disabled={!events || events.page <= 1} onClick={() => setFilters((current) => ({ ...current, page: current.page - 1 }))}><NavArrowLeft {...iconProps} />上一页</button><span>第 <strong>{events?.page ?? 1}</strong> / {events?.pageCount ?? 1} 页</span><label>跳至<input type="number" min={1} max={events?.pageCount ?? 1} value={filters.page} onChange={(event) => setFilters((current) => ({ ...current, page: Math.min(events?.pageCount ?? 1, Math.max(1, Number(event.target.value) || 1)) }))} />页</label><button disabled={!events || events.page >= events.pageCount} onClick={() => setFilters((current) => ({ ...current, page: current.page + 1 }))}>下一页<NavArrowRight {...iconProps} /></button></footer>
      </section>
    </>}
    {view === "security" && <>
      <section className="audit-security-filter"><label className="audit-query"><Search {...iconProps} /><input value={filters.query} onChange={(event) => setFilters((current) => ({ ...current, query: event.target.value, page: 1 }))} placeholder="搜索登录账号或操作人" /></label><label><span>结果</span><select value={filters.outcome} onChange={(event) => setFilters((current) => ({ ...current, outcome: event.target.value, page: 1 }))}><option value="all">全部结果</option><option value="success">成功</option><option value="failure">失败</option></select></label><label><span>渠道</span><select value={filters.channelId} onChange={(event) => setFilters((current) => ({ ...current, channelId: event.target.value, page: 1 }))}><option value="all">全部渠道</option>{summary?.channelOptions.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label></section>
      <section className="audit-security-metrics"><article><span>登录成功率</span><strong>{security?.metrics.successRate ?? 0}%</strong><small>当前筛选范围</small></article><article><span>登录失败</span><strong>{security?.metrics.failureCount ?? 0}</strong><small>已记录失败次数</small></article><article><span>最近成功登录</span><strong className="date-value">{security?.metrics.latestLoginAt ? formatDateTime(security.metrics.latestLoginAt) : "—"}</strong><small>精确到秒</small></article><article><span>最近失败账号</span><strong className="account-value">{security?.metrics.recentFailedAccounts.length ? security.metrics.recentFailedAccounts.join("、") : "—"}</strong><small>最多展示 8 个</small></article></section>
      <section className="audit-security-panel"><header><div><p className="eyebrow">LOGIN SECURITY EVENTS</p><h2>账号与登录记录</h2></div><span>{security?.total ?? 0} 条</span></header><div className="audit-security-list">{security?.items.length ? security.items.map((item) => <button key={item.id} onClick={() => void openEvent(item)}><span className={`security-event-icon ${item.outcome}`}><ShieldCheck width={19} height={19} strokeWidth={1.7} aria-hidden /></span><span className="audit-security-action"><strong>{item.action}</strong><small>{item.actor} · {item.actorLogin || "无登录账号"}</small></span><span className="audit-security-channel" data-label="渠道"><strong>{channelName(item.channelId)}</strong><small>{auditRoleLabels[item.actorRole ?? ""] ?? item.actorRole}</small></span><span className={`audit-outcome ${item.outcome}`}>{item.outcome === "success" ? "成功" : "失败"}</span><time data-label="时间">{formatDateTime(item.at)}</time><NavArrowRight width={17} height={17} strokeWidth={1.7} aria-hidden /></button>) : <EmptyState title="暂无登录安全记录" text="当前筛选范围没有登录、密码、MFA 或账号启停事件。" />}</div></section>
    </>}
    {selected && <div className="drawer-layer" role="presentation" onMouseDown={(event) => { if (event.currentTarget === event.target) setSelected(null); }}><aside className="audit-detail-drawer" role="dialog" aria-modal="true" aria-label="审计日志详情"><header><div><p className="eyebrow">AUDIT EVENT DETAIL</p><h2>{selected.action}</h2><span className={`audit-outcome ${selected.outcome}`}>{selected.outcome === "success" ? "执行成功" : "执行失败"}</span></div><button aria-label="关闭" onClick={() => setSelected(null)}><Xmark width={24} height={24} strokeWidth={1.7} aria-hidden /></button></header><dl className="audit-detail-grid"><div><dt>事件编号</dt><dd><code>{selected.id}</code></dd></div><div><dt>操作时间</dt><dd>{formatDateTime(selected.at)}</dd></div><div><dt>操作人</dt><dd>{selected.actor}</dd></div><div><dt>登录账号</dt><dd>{selected.actorLogin || "—"}</dd></div><div><dt>角色</dt><dd>{auditRoleLabels[selected.actorRole ?? ""] ?? selected.actorRole ?? "—"}</dd></div><div><dt>所属渠道</dt><dd>{channelName(selected.channelId)}</dd></div><div><dt>事件分类</dt><dd>{selected.categoryLabel}</dd></div><div><dt>操作对象</dt><dd>{selected.target || "—"}</dd></div></dl><section><h3>变化摘要</h3><p>{selected.detail || "该历史日志未记录变化摘要。"}</p></section>{selected.changes && <section><h3>修改前后字段</h3><div className="audit-changes">{Object.entries(selected.changes).map(([key, value]) => <div key={key}><strong>{key}</strong><code>{typeof value === "string" ? value : JSON.stringify(value, null, 2)}</code></div>)}</div></section>}<section><h3>请求来源与设备</h3><dl className="audit-request-context"><div><dt>来源地址</dt><dd>{selected.requestContext?.ip || selected.requestContext?.fingerprint || "历史记录未采集"}</dd></div><div><dt>设备摘要</dt><dd>{selected.requestContext?.userAgent || "历史记录未采集"}</dd></div></dl></section></aside></div>}
  </>;
}

type RuntimeStatus = {
  version: string;
  environment: string;
  environmentLabel: string;
  backend: string;
  database?: { ok: boolean; revision?: number | null; updatedAt?: string | null };
  jobs: Record<string, number>;
  backups: { id: string; kind: string; revision?: number | null; checksum?: string; createdBy?: string; createdAt: string }[];
  metrics: { requests_24h?: number; errors_24h?: number; avg_duration_ms?: number };
  security: { events_24h?: number; activeAccounts: number; mfaEnabledAccounts: number; managersWithoutMfa: string[] };
  services: { deepseek: boolean; alertWebhook: boolean };
  generatedAt: string;
};

function SystemPage({ user, notify }: { user: User; notify: (tone: Notice["tone"], message: string) => void }) {
  const [runtime, setRuntime] = useState<RuntimeStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [creatingBackup, setCreatingBackup] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRuntime(await api<RuntimeStatus>("/api/admin/runtime"));
    } catch (error) {
      notify("error", error instanceof Error ? error.message : "运行状态读取失败");
    } finally {
      setLoading(false);
    }
  }, [notify]);
  useEffect(() => { void load(); }, [load]);
  const createBackup = async () => {
    setCreatingBackup(true);
    try {
      const result = await post<{ message: string }>("/api/admin/backups", {});
      notify("success", result.message);
      await load();
    } catch (error) {
      notify("error", error instanceof Error ? error.message : "备份创建失败");
    } finally {
      setCreatingBackup(false);
    }
  };
  return <>
    <PageHeader eyebrow="PRODUCTION OPERATIONS" title="系统运行" description="集中查看正式数据库、任务队列、安全策略、服务依赖和可恢复备份。">
      <button className="button secondary" onClick={load} disabled={loading}>刷新状态</button>
      <button className="button primary" onClick={createBackup} disabled={creatingBackup}>{creatingBackup ? "正在备份…" : "创建即时备份"}</button>
    </PageHeader>
    {loading && !runtime ? <section className="panel runtime-loading">正在读取运行底座…</section> : runtime && <>
      <section className="runtime-hero">
        <div>
          <span className={`runtime-pulse ${runtime.database?.ok ? "healthy" : "danger"}`} />
          <div><p className="eyebrow">ENVIRONMENT</p><h2>{runtime.environmentLabel}</h2><small>{runtime.backend} · V{runtime.version} · 修订 {runtime.database?.revision ?? "本地"}</small></div>
        </div>
        <Status value={runtime.database?.ok ? "运行正常" : "需要处理"} />
      </section>
      <section className="runtime-metric-grid">
        <article><Database {...iconProps} /><span>数据库</span><strong>{runtime.database?.ok ? "可读写" : "异常"}</strong><small>{runtime.database?.updatedAt ? `最近写入 ${formatDateTime(runtime.database.updatedAt)}` : "本地兼容存储"}</small></article>
        <article><CloudUpload {...iconProps} /><span>任务队列</span><strong>{(runtime.jobs.queued ?? 0) + (runtime.jobs.running ?? 0)}</strong><small>等待 {runtime.jobs.queued ?? 0} · 运行 {runtime.jobs.running ?? 0} · 失败 {runtime.jobs.failed ?? 0}</small></article>
        <article><StatsUpSquare {...iconProps} /><span>24 小时请求</span><strong>{runtime.metrics.requests_24h ?? 0}</strong><small>平均 {runtime.metrics.avg_duration_ms ?? 0} ms · 错误 {runtime.metrics.errors_24h ?? 0}</small></article>
        <article><ShieldCheck {...iconProps} /><span>MFA 覆盖</span><strong>{runtime.security.mfaEnabledAccounts}/{runtime.security.activeAccounts}</strong><small>{runtime.security.managersWithoutMfa.length ? `管理员待启用：${runtime.security.managersWithoutMfa.join("、")}` : "管理员已完成 MFA 覆盖"}</small></article>
      </section>
      <section className="runtime-grid">
        <article className="panel runtime-service-panel">
          <PanelHead eyebrow="SERVICE READINESS" title="服务依赖与安全能力" />
          <div className="runtime-check-list">
            <span><CheckCircle {...iconProps} /><strong>PostgreSQL 正式持久化</strong><small>{runtime.backend === "postgres" ? "已启用" : "本地验证模式"}</small></span>
            <span><CheckCircle {...iconProps} /><strong>登录限流与安全事件</strong><small>已启用 · 24h {runtime.security.events_24h ?? 0} 条</small></span>
            <span className={runtime.services.deepseek ? "" : "warning"}>{runtime.services.deepseek ? <CheckCircle {...iconProps} /> : <WarningTriangle {...iconProps} />}<strong>DeepSeek 作战助手</strong><small>{runtime.services.deepseek ? "已配置" : "未配置，业务将规则降级"}</small></span>
            <span className={runtime.services.alertWebhook ? "" : "warning"}>{runtime.services.alertWebhook ? <CheckCircle {...iconProps} /> : <WarningTriangle {...iconProps} />}<strong>外部告警通道</strong><small>{runtime.services.alertWebhook ? "已启用" : "未配置，结构化日志仍正常记录"}</small></span>
          </div>
        </article>
        <article className="panel runtime-backup-panel">
          <PanelHead eyebrow="RECOVERY POINTS" title="最近可恢复备份" />
          {runtime.backups.length ? <div className="runtime-backup-list">{runtime.backups.map((backup) => <div key={backup.id}><span><strong>{backup.kind}</strong><small>{formatDateTime(backup.createdAt)} · 修订 {backup.revision ?? "本地"}</small></span><code>{backup.checksum?.slice(0, 12) ?? backup.id.slice(-12)}</code></div>)}</div> : <EmptyState compact title="尚无运行备份" text="正式数据库首次迁移会自动创建迁移备份，也可由总管理员立即创建。" />}
        </article>
      </section>
      <p className="runtime-footnote">状态生成于 {formatDateTime(runtime.generatedAt)} · 当前查看人 {user.name} · 页面不展示数据库连接串、API 密钥或 MFA 密钥。</p>
    </>}
  </>;
}

function ProfilePage({ state, user, refresh, notify }: { state: AppState; user: User; refresh: () => Promise<void>; notify: (tone: Notice["tone"], message: string) => void }) {
  const channel = state.channel ?? {};
  const [form, setForm] = useState(false);
  const [contactForm, setContactForm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<TeamMember | null>(null);
  const members = state.teamMembers ?? [];
  const primaryContact = channel.contacts?.find((contact) => contact.primary && contact.active !== false)
    ?? channel.contacts?.find((contact) => contact.active !== false);
  const changeStatus = async (member: TeamMember) => {
    try {
      const response = await post<{ message: string }>(`/api/channel-users/${member.id}/status`, { active: !member.active });
      await refresh();
      notify("success", response.message);
    } catch (error) { notify("error", error instanceof Error ? error.message : "账号状态更新失败"); }
  };
  return <>
    <PageHeader eyebrow="CHANNEL PROFILE" title={user.accountRole === "channel_owner" ? "渠道与账号" : "我的渠道身份"} description={user.accountRole === "channel_owner" ? "同一渠道可配置多名负责人，共同管理渠道销售账号；历史工作和审批记录不会随账号删除而消失。" : "渠道销售仅查看自己的账号与所负责客户，渠道整体规模和其他成员信息不在当前权限范围内。"}>
      {user.accountRole === "channel_owner" && <><button className="button secondary" onClick={() => setContactForm(true)}><EditPencil {...iconProps} />维护联系人</button><button className="button primary" onClick={() => setForm(true)}><PlusCircle {...iconProps} />新增销售账号</button></>}
    </PageHeader>
    <section className="profile-card"><div className="channel-monogram large">{String(channel.shortName ?? "渠").slice(0, 1)}</div><div><p>已绑定渠道</p><h2>{String(channel.name ?? "")}</h2><span>{String(channel.region ?? "")} · {String(channel.type ?? "")}</span><span className="profile-contact-line">主要负责人：{primaryContact?.name || "未填写"} · {primaryContact?.phone || "电话未填写"}</span></div><Status value={channel.active ? "启用" : "停用"} /></section>
    <section className="account-role-banner"><ShieldCheck {...iconProps} /><div><span>当前身份</span><strong>{user.accountRole === "channel_owner" ? "渠道负责人" : "渠道销售"}</strong><p>{user.accountRole === "channel_owner" ? "可查看和分配全渠道工作、审批销售申报、管理销售账号；不可查看渠道优先级。" : "只可查看本人负责的客户、通路、计划和工作记录，不展示渠道整体客户数、通路数或其他成员。"}</p></div></section>
    <section className="panel">
      <PanelHead eyebrow="CHANNEL ACCOUNTS" title={user.accountRole === "channel_owner" ? "主账号与销售子账号" : "我的账号"} />
      <div className="team-list">
        {members.map((member) => <article key={member.id}><span className={`team-avatar ${member.accountRole === "channel_owner" ? "owner" : ""}`}>{member.name.slice(0, 1)}</span><div><strong>{member.name}<em>{member.accountRole === "channel_owner" ? "渠道负责人" : "渠道销售"}</em></strong><small>{member.title} · 登录账号 {member.login}</small>{member.contactInfo && <small>{member.contactInfo}</small>}</div><Status value={member.active ? "启用" : "停用"} />{user.accountRole === "channel_owner" && member.accountRole === "channel_sales" && <><button className={`button ghost ${member.active ? "danger" : ""}`} onClick={() => changeStatus(member)}>{member.active ? "停用" : "重新启用"}</button><button className="button ghost danger" onClick={() => setDeleteTarget(member)}><Trash width={16} height={16} strokeWidth={1.7} aria-hidden />删除</button></>}</article>)}
      </div>
    </section>
    {form && <ChannelUserForm close={() => setForm(false)} notify={notify} onDone={async (message) => { await refresh(); setForm(false); notify("success", message); }} />}
    {contactForm && channel.id && <PrimaryChannelContactForm channelId={channel.id} contact={primaryContact} close={() => setContactForm(false)} notify={notify} onDone={async (message) => { await refresh(); setContactForm(false); notify("success", message); }} />}
    {deleteTarget && <DeleteChannelAccountConfirm account={deleteTarget} close={() => setDeleteTarget(null)} notify={notify} onDone={async (message) => { await refresh(); setDeleteTarget(null); notify("success", message); }} />}
  </>;
}

function PrimaryChannelContactForm({ channelId, contact, close, onDone, notify }: { channelId: string; contact?: Channel["contacts"][number]; close: () => void; onDone: (message: string) => void; notify: (tone: Notice["tone"], message: string) => void }) {
  const [submitting, setSubmitting] = useState(false);
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    try {
      const result = await post<{ message: string }>(`/api/channels/${channelId}/primary-contact`, Object.fromEntries(new FormData(event.currentTarget)));
      onDone(result.message);
    } catch (error) {
      notify("error", error instanceof Error ? error.message : "联系人保存失败");
    } finally {
      setSubmitting(false);
    }
  };
  return <Modal title="维护渠道主要联系人" description="负责人姓名和联系电话会展示在本渠道资料与总管理员渠道档案中；不会自动创建或修改登录账号。" close={close}>
    <form className="form-grid" onSubmit={submit}>
      <Field label="联系人（负责人）" name="contactName" defaultValue={contact?.name ?? ""} placeholder="渠道主要负责人姓名" />
      <Field label="联系电话" name="contactPhone" defaultValue={contact?.phone ?? ""} placeholder="手机或座机" />
      <div className="form-flow-note full"><ShieldCheck {...iconProps} /><span>渠道销售仅可查看；只有渠道负责人和总管理员可以维护。清空姓名与电话后，原联系人将审计归档。</span></div>
      <div className="form-actions full"><button type="button" className="button ghost" onClick={close}>取消</button><button className="button primary" disabled={submitting}>{submitting ? "正在保存…" : "保存联系人"}</button></div>
    </form>
  </Modal>;
}

function ChannelUserForm({ close, onDone, notify }: { close: () => void; onDone: (message: string) => void; notify: (tone: Notice["tone"], message: string) => void }) {
  const [submitting, setSubmitting] = useState(false);
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    try {
      const values = Object.fromEntries(new FormData(event.currentTarget));
      const response = await post<{ message: string }>("/api/channel-users", values);
      onDone(response.message);
    } catch (error) { notify("error", error instanceof Error ? error.message : "账号创建失败"); }
    finally { setSubmitting(false); }
  };
  return (
    <Modal title="新增渠道销售账号" description="子账号自动绑定当前渠道；销售只能以自己的名字发起申报和更新已分配工作。" close={close}>
      <form className="form-grid" onSubmit={submit}>
        <Field label="销售姓名" name="name" required placeholder="请输入真实使用人姓名" />
        <Field label="登录账号" name="login" required placeholder="如 qinghe.zhangsan" />
        <Field label="初始密码" name="password" type="password" required placeholder="至少 6 位" />
        <Field label="联系方式" name="contactInfo" placeholder="选填，手机或邮箱" />
        <div className="form-flow-note full"><ShieldCheck {...iconProps} /><span>停用账号不会删除其历史申报、计划、工作记录或审计信息。</span></div>
        <div className="form-actions full"><button type="button" className="button ghost" onClick={close}>取消</button><button className="button primary" disabled={submitting}>{submitting ? "正在创建…" : "创建子账号"}</button></div>
      </form>
    </Modal>
  );
}

function AdminChannelUserForm({ channel, accountRole, close, onDone, notify }: { channel: Channel; accountRole: "channel_owner" | "channel_sales"; close: () => void; onDone: (message: string) => void; notify: (tone: Notice["tone"], message: string) => void }) {
  const [submitting, setSubmitting] = useState(false);
  const label = accountRole === "channel_owner" ? "渠道负责人" : "渠道销售";
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    try {
      const values = Object.fromEntries(new FormData(event.currentTarget));
      const response = await post<{ message: string }>("/api/admin/channel-users", { ...values, channelId: channel.id, accountRole });
      onDone(response.message);
    } catch (error) {
      notify("error", error instanceof Error ? error.message : "账号创建失败");
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <Modal title={`新建${label}账号`} description={`${channel.name} · ${accountRole === "channel_owner" ? "同一渠道可配置多名负责人，共同管理渠道业务和销售账号。" : "销售账号绑定当前渠道，由渠道负责人共同管理。"}`} close={close}>
      <form className="form-grid" onSubmit={submit}>
        <Field label={`${label}姓名`} name="name" required placeholder="请输入真实使用人姓名" />
        <Field label="登录账号" name="login" required placeholder={accountRole === "channel_owner" ? "如 partner.owner" : "如 partner.zhangsan"} />
        <Field label="初始密码" name="password" type="password" required placeholder="至少 6 位" />
        <Field label="联系方式" name="contactInfo" placeholder="选填，手机或邮箱" />
        <div className="form-flow-note full"><ShieldCheck {...iconProps} /><span>渠道档案与账号解耦：可以先建档，再在详情中配置负责人和销售账号。</span></div>
        <div className="form-actions full"><button type="button" className="button ghost" onClick={close}>取消</button><button className="button primary" disabled={submitting}>{submitting ? "正在创建…" : `创建${label}账号`}</button></div>
      </form>
    </Modal>
  );
}

function PasswordForm({ user, administratorReset = false, close, onDone, notify, onUserUpdated }: { user: User; administratorReset?: boolean; close: () => void; onDone: (message: string) => void; notify: (tone: Notice["tone"], message: string) => void; onUserUpdated?: (user: User) => void }) {
  const [submitting, setSubmitting] = useState(false);
  const [mfaSetup, setMfaSetup] = useState<{ secret: string; otpAuthUrl: string; expiresAt: string } | null>(null);
  const [mfaOtp, setMfaOtp] = useState("");
  const formRef = useRef<HTMLFormElement>(null);
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const values = Object.fromEntries(new FormData(event.currentTarget));
    if (values.newPassword !== values.confirmPassword) return notify("error", "两次输入的新密码不一致");
    setSubmitting(true);
    try {
      const response = await post<{ message: string }>(`/api/users/${user.id}/password`, {
        currentPassword: values.currentPassword,
        newPassword: values.newPassword
      });
      onDone(response.message);
    } catch (error) {
      notify("error", error instanceof Error ? error.message : "密码修改失败");
    } finally {
      setSubmitting(false);
    }
  };
  const currentPassword = () => String(new FormData(formRef.current ?? undefined).get("currentPassword") ?? "");
  const beginMfaSetup = async () => {
    if (!currentPassword()) return notify("error", "请先填写当前密码");
    try {
      const setup = await post<{ secret: string; otpAuthUrl: string; expiresAt: string }>("/api/auth/mfa/setup", {
        currentPassword: currentPassword()
      });
      setMfaSetup(setup);
      setMfaOtp("");
      notify("success", "绑定密钥已生成，请在身份验证器中添加后输入动态码");
    } catch (error) {
      notify("error", error instanceof Error ? error.message : "MFA 绑定初始化失败");
    }
  };
  const confirmMfa = async () => {
    try {
      const result = await post<{ user: User; message: string }>("/api/auth/mfa/confirm", { otp: mfaOtp });
      onUserUpdated?.(result.user);
      setMfaSetup(null);
      setMfaOtp("");
      notify("success", result.message);
    } catch (error) {
      notify("error", error instanceof Error ? error.message : "MFA 验证失败");
    }
  };
  const disableMfa = async () => {
    if (!currentPassword()) return notify("error", "请先填写当前密码");
    try {
      const result = await post<{ user: User; message: string }>("/api/auth/mfa/disable", {
        currentPassword: currentPassword(),
        otp: mfaOtp
      });
      onUserUpdated?.(result.user);
      setMfaOtp("");
      notify("success", result.message);
    } catch (error) {
      notify("error", error instanceof Error ? error.message : "MFA 停用失败");
    }
  };
  return (
    <Modal title={administratorReset ? "重置下级账号密码" : "账号安全"} description={administratorReset ? `${user.name} · 请设置新的登录密码。` : "维护登录密码与双因素认证。"} close={close}>
      <form className="form-grid security-settings-form" onSubmit={submit} ref={formRef}>
        {!administratorReset && <Field label="当前密码" name="currentPassword" type="password" required />}
        <Field label={administratorReset ? "设置新密码" : "新密码"} name="newPassword" type="password" required placeholder="至少 6 位" />
        <Field label="再次输入新密码" name="confirmPassword" type="password" required />
        {!administratorReset && <section className="mfa-settings full">
          <header>
            <div><strong>双因素认证（MFA）</strong><small>{user.mfaEnabled ? "已启用：登录时必须输入身份验证器动态码" : "建议总管理员和渠道负责人启用"}</small></div>
            <Status value={user.mfaEnabled ? "启用" : "未启用"} />
          </header>
          {!user.mfaEnabled && !mfaSetup && <button type="button" className="button secondary" onClick={beginMfaSetup}><ShieldCheck {...iconProps} />生成绑定密钥</button>}
          {mfaSetup && <div className="mfa-enrollment">
            <p>在 Microsoft Authenticator、Google Authenticator 或其他 TOTP 应用中手动输入以下密钥：</p>
            <code>{mfaSetup.secret}</code>
            <small>密钥仅在本次绑定时展示，10 分钟内有效。请勿通过聊天或邮件转发。</small>
          </div>}
          {(user.mfaEnabled || mfaSetup) && <label className="field"><span>6 位动态验证码</span><input inputMode="numeric" autoComplete="one-time-code" value={mfaOtp} onChange={(event) => setMfaOtp(event.target.value.replace(/\D/gu, "").slice(0, 6))} placeholder="000000" /></label>}
          {mfaSetup && <button type="button" className="button primary" disabled={mfaOtp.length !== 6} onClick={confirmMfa}>验证并启用 MFA</button>}
          {user.mfaEnabled && <button type="button" className="button ghost danger" disabled={mfaOtp.length !== 6} onClick={disableMfa}>校验后停用 MFA</button>}
        </section>}
        <div className="form-actions full"><button type="button" className="button ghost" onClick={close}>取消</button><button className="button primary" disabled={submitting}>{submitting ? "正在保存…" : administratorReset ? "确认重置密码" : "确认修改密码"}</button></div>
      </form>
    </Modal>
  );
}

function PageHeader({ eyebrow, title, description, children }: { eyebrow: string; title: string; description: string; children?: ReactNode }) {
  return <header className="page-header"><div><p className="eyebrow">{eyebrow}</p><h1>{title}</h1><p>{description}</p></div>{children && <div className="page-actions">{children}</div>}</header>;
}

function PanelHead({ eyebrow, title, action }: { eyebrow: string; title: string; action?: () => void }) {
  return <header className="panel-head"><div><p className="eyebrow">{eyebrow}</p><h2>{title}</h2></div>{action && <button className="text-button" onClick={action}>查看全部<NavArrowRight {...iconProps} /></button>}</header>;
}

function Status({ value }: { value: string }) {
  const tone = ["已逾期", "推进受阻", "受阻", "已驳回", "渠道已驳回", "停用"].includes(value) ? "danger" : ["今日到期", "三日内到期", "等待反馈", "渠道退回补充", "总管理员退回补充", "待渠道审核", "待总管理员审批", "暂无计划", "未读"].includes(value) ? "warning" : ["已完成", "已通过", "部分通过", "正常推进", "启用", "已读"].includes(value) ? "success" : "neutral";
  return <span className={`status ${tone}`}><i />{value}</span>;
}

function RouteTag({ type }: { type: "技术口" | "采购口" }) {
  return <span className={`route-tag ${type === "技术口" ? "technical" : "purchase"}`}>{type}</span>;
}

function ApplicationRow({ application }: { application: Application }) {
  const detail = application.kind === "客户资料变更"
    ? `客户资料变更 · ${(application.customerChange?.changedFields.length ?? 0)} 项`
    : `${application.routeTypes.join("＋")}${application.ownerName ? ` · 负责 ${application.ownerName}` : ""}`;
  return <article className="application-row"><span className={`application-icon ${application.routeTypes.includes("采购口") ? "purchase" : ""}`}><JournalPage {...iconProps} /></span><div className="application-row-copy"><strong title={application.customer?.name}>{application.customer?.name}</strong><small>{application.channel?.name} · {detail}</small></div><Status value={application.status} /><NavArrowRight {...iconProps} /></article>;
}

function PlanRow({ route, compact = false }: { route: RouteItem; compact?: boolean }) {
  return <article className={`plan-row ${compact ? "compact" : ""}`}><span className={`route-line ${route.type === "技术口" ? "technical" : "purchase"}`} /><div><strong>{route.customer?.name}</strong><small>{route.plan?.nextAction ?? "待补充计划"}</small></div><span><Status value={route.plan?.status ?? "暂无计划"} /><small>{formatDate(route.plan?.followUpDate)}</small></span></article>;
}

function EmptyState({ title, text, compact = false }: { title: string; text: string; compact?: boolean }) {
  return <div className={`empty-state ${compact ? "compact" : ""}`}><span className="empty-drawing"><i /><i /><FileNotFound {...iconProps} /></span><div><strong>{title}</strong><p>{text}</p></div></div>;
}

function Modal({ title, description, close, children, bottomSheet = false }: { title: string; description: string; close: () => void; children: ReactNode; bottomSheet?: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  const closeRef = useRef(close);
  useEffect(() => {
    closeRef.current = close;
  }, [close]);
  useEffect(() => {
    const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    ref.current?.focus();
    const listener = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeRef.current();
        return;
      }
      if (event.key !== "Tab" || !ref.current) return;
      const focusable = [...ref.current.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')]
        .filter((element) => !element.hidden && element.getAttribute("aria-hidden") !== "true");
      if (!focusable.length) {
        event.preventDefault();
        ref.current.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (document.activeElement === ref.current || !ref.current.contains(document.activeElement)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", listener);
    return () => {
      window.removeEventListener("keydown", listener);
      document.body.style.overflow = previousOverflow;
      previouslyFocused?.focus();
    };
  }, []);
  return <div className="modal-layer" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && close()}><div className={`modal ${bottomSheet ? "bottom-sheet" : ""}`} role="dialog" aria-modal="true" aria-labelledby="modal-title" aria-describedby="modal-description" tabIndex={-1} ref={ref}><header><div><h2 id="modal-title">{title}</h2><p id="modal-description">{description}</p></div><button type="button" className="icon-button" aria-label="关闭" onClick={close}><Xmark {...iconProps} /></button></header>{children}</div></div>;
}

function Field(props: { label: string; name: string; required?: boolean; type?: string; placeholder?: string; defaultValue?: string }) {
  return <label className="field"><span>{props.label}{props.required ? " *" : ""}</span><input name={props.name} type={props.type ?? "text"} required={props.required} placeholder={props.placeholder} defaultValue={props.defaultValue} /></label>;
}

function pageLabel(page: PageKey, user: User) {
  return navFor(user).find((item) => item.key === page)?.label ?? "工作台";
}

function pageGroupLabel(page: PageKey, user: User) {
  return navGroupsFor(user).find((group) => group.items.some((item) => item.key === page))?.label ?? "工作区";
}

function importLabel(key: string) {
  return ({
    sourceRows: "读取原行",
    importableRows: "可入库行",
    blockedRows: "阻断行",
    pendingCompletion: "待补全",
    newCustomers: "新增客户",
    matchedExistingCustomers: "匹配现有客户",
    batchDuplicates: "批内重复",
    customerConflicts: "客户冲突",
    channelReferences: "渠道引用",
    ruleMatches: "规则匹配",
    aiAutoMatches: "AI自动匹配",
    manualReview: "待人工研判",
    unmatched: "未匹配",
    rows: "读取行数",
    validRows: "有效行",
    duplicateCustomers: "重复客户",
    duplicateRelations: "重复关系",
    splitRelations: "拆分关系",
    errors: "错误行",
    possibleAliases: "疑似别名"
  } as Record<string, string>)[key] ?? key;
}

function customerDispositionLabel(value: string) {
  return ({ new: "新增客户", existing: "匹配平台已有客户", "batch-duplicate": "批内重复，关联同一主档", conflict: "客户主档冲突", blocked: "阻断行" } as Record<string, string>)[value] ?? value;
}

function channelMatchLabel(value: string) {
  return ({ matched: "已确认", manual: "待人工", unmatched: "未匹配", deferred: "暂缓" } as Record<string, string>)[value] ?? value;
}

function batchStatusLabel(value: string) {
  return ({
    uploading: "分块上传中",
    uploaded: "待处理",
    ready: "待确认导入",
    committed: "已导入",
    rolled_back: "已回滚",
    rollback_conflict: "回滚存在冲突"
  } as Record<string, string>)[value] ?? value;
}
